import * as Phaser from 'phaser';
import { rgb24 } from '../../sim/color.ts';
import {
  BOAT_LENGTH_M,
  BOW_OFFSET_PX,
  fromDisplayKmh,
  CRASH_HARSHNESS,
  CRASH_HARSHNESS_MAX,
  CRASH_HARSHNESS_MIN,
  TURN_TAX,
  TURN_TAX_MAX,
  TURN_TAX_MIN,
  MOVEMENT_RADIUS,
  MOVEMENT_RADIUS_MAX,
  MOVEMENT_RADIUS_MIN,
  MOVEMENT_RADIUS_STEP,
  PIXELS_PER_METER,
  STICK_OVERFLOW_PX,
  STICK_OVERFLOW_PX_MIN,
  STICK_OVERFLOW_PX_STEP,
  TRAIL_LENGTH_DEFAULT_M,
  TRAIL_LENGTH_MAX_M,
  TRAIL_LENGTH_MIN_M,
  TRAIL_LENGTH_STEP_M,
  TRAIL_LIMIT,
  TRAIL_SPEED_GAIN_DEFAULT,
  TRAIL_SPEED_GAIN_MAX,
  TRAIL_SPEED_GAIN_MIN,
  TRAIL_SPEED_GAIN_STEP,
  metersToPixels,
  pixelsToMeters,
  trailKeepLengthM,
  toCompassBearing,
  toDisplayKmh,
} from '../../sim/constants.ts';
import { gradeFor } from '../../sim/environment/grade.ts';
import { SunLighting } from '../lighting/sunLighting.ts';
import { rollEnvironment, type RolledEnvironment } from '../../sim/environment/roll.ts';
import {
  clampWindFeel,
  cloneWindFeel,
  DEFAULT_WIND_FEEL,
  windOffsetPx,
  type WindFeel,
} from '../../sim/environment/wind.ts';
import { polygonBounds } from '../../sim/geometry.ts';
import { formatScaleLabel, gridCellMeters } from '../../sim/grid.ts';
import {
  appendContactLog,
  appendPlayLogLines,
  type PlayLogLine,
} from '../../sim/damage.ts';
import { driftProgress } from '../../sim/drift.ts';
import { contactShape, DEFAULT_HULL, type HullDimensions } from '../../sim/hullSize.ts';
import { placementClear, type PlacedHull } from '../../sim/placement.ts';
import {
  boatCollisionsActive,
  formatBoatHit,
  setBoatCollisionsEnabled,
  type BoatHit,
} from '../../sim/modules/host.ts';
import type { FleetContext } from '../../sim/turn.ts';
import { pointUnderColumnCap } from '../../sim/collision.ts';
import {
  add,
  angleDelta,
  clamp,
  fromAngle,
  lengthSq,
  sub,
  toAngle,
} from '../../sim/math.ts';
import {
  createDefaultEnvironment,
  createEmptyMap,
  cloneAimChrome,
  cloneLook,
  cloneMapDocument,
  DEFAULT_AIM_CHROME,
  GATE_DASH_PX,
  GATE_GAP_PX,
  dashedPolyline,
  isLegalStartPlacement,
  isLegalTestPlacement,
  mapLapCount,
  poleWorldScale,
  startAreaCentroid,
  tessellateRing,
  updateEnvironment,
  updateLook,
  type AimChrome,
  type MapDocument,
  type MapLook,
  type SunSettings,
  type ViewRect,
  findStartAreaAt,
} from '../../sim/map/index.ts';
import {
  bowPosition,
  createBoatState,
  projectMovementArea,
  resolveMove,
  turnAngleDeg,
} from '../../sim/movement.ts';
import type { CurveKind, MovePath } from '../../sim/path.ts';
import {
  DEFAULT_CURVE_KIND,
  arcLengthTable,
  clipMovePathAheadOfBow,
  headingAtProgress,
  pointAtProgress,
  projectMovePath,
  pathFromPolyline,
  pushTrail,
  sampleMovePath,
  splitPolylineByLand,
} from '../../sim/path.ts';
import {
  highScoreBoatFromDocument,
  type HighScore,
  type HighScoreBoat,
  type RaceReplay,
} from '../../sim/highScores/index.ts';
import {
  advanceRace,
  boatClockFromCursor,
  calculatedTimeMs,
  createRaceState,
  gateHudLabel,
  nearMissCheckpoints,
  nearMissWorld,
  NEAR_MISS_PX,
  NEAR_MISS_PX_MAX,
  NEAR_MISS_PX_MIN,
  NEAR_MISS_PX_STEP,
  previewedCheckpointIds,
  racePrefixStates,
  sameRaceProgress,
  type RaceState,
} from '../../sim/race.ts';
import {
  averageSpeedKmh,
  maxSpeedKmh,
  tightestTurnDeg,
} from '../../sim/raceStats.ts';
import {
  DEFAULT_REPLAY_PLAYBACK_MODE,
  DEFAULT_REPLAY_PLAYBACK_RATE,
  previousReplayLandingSpeed,
  previousRoundLandingSpeed,
  nextRingMode,
  reconstructMatchReplay,
  reconstructReplay,
  replayDisplaySpeed,
  replayIncomingWorldSpeed,
  replaySpeed,
  travelJoinWorldSpeed,
  travelProgressFromJoin,
  travelProgressFromSpeeds,
  type ReplayPlaybackMode,
  type ReplayPlaybackRate,
  type RingMode,
} from '../../sim/replay.ts';
import { completeResolve, type CommitResult } from '../../sim/turn.ts';
import {
  planWithFeel,
  paceFeel,
  ringProjection,
  seatCommitArgs,
  type TurnFeel,
} from '../../sim/turnFeel.ts';
import type { BoatState, TurnPhase, Vec2 } from '../../sim/types.ts';
import { buildWake, collisionSplashRadius, type WakeQuad } from '../../sim/wake.ts';
import { pointerScreenMoved, stickToMovementRim } from '../../sim/aimStick.ts';
import {
  cloneBoatDocument,
  createSkiffBoat,
  planningFeelFromBoat,
  SKIFF_BOAT_ID,
  stickToReach,
  headreachLevel,
  type BoatDocument,
  type ReachAxes,
} from '../../sim/boat/index.ts';
import { SKIFF_PLANING_KMH } from '../../sim/boat/limits.ts';
import { loadBoatSvgSource, loadPaintedBoatTexture } from '../boats/liveryTexture.ts';
import {
  testLeaveLabel,
  testPlayReturn,
  type TestOrigin,
} from '../testPlay.ts';
import {
  closeStartingSpeedDialog,
  openStartingSpeedDialog,
} from '../../ui/play/startingSpeed.ts';
import {
  activeCompetitor,
  beginRound,
  clampCompetitorCount,
  commitActiveWithFeel,
  createMatch,
  finishActiveTurn,
  groupReplayStepsByRound,
  latestSharedSplit,
  lockedIn,
  matchComplete,
  matchReplayOrder,
  officialTimeMs,
  passLockIn,
  rankMatch,
  rankStandings,
  resolveRound,
  roundReady,
  seatIsParking,
  type CompetitorPlacement,
  type MatchMovement,
  type MatchReplayStep,
  type MatchState,
  type RoundResolution,
  type ShovedSeat,
} from '../../sim/match/index.ts';
import { decideParkDestination } from '../../sim/park.ts';
import {
  fallbackDestination,
  resolveSeatControllers,
  type RivalSeat,
  seatBotKnobs,
  seatBotLevel,
  seatIsBot,
  type SeatController,
} from '../../sim/bot/index.ts';
import { createBotClient, type BotClient } from '../bot/botClient.ts';
import { competitorLabel, competitorTint, competitorTintCss } from '../competitorTint.ts';
import {
  replaySeatCursorAt,
  replaySeatSnapshot,
  replaySharedSplit,
  type ReplaySeatSnapshot,
} from '../../sim/replayStandings.ts';
import {
  hydratePlayDebugDefaultsFromRepo,
  loadPlayDebugDefaults,
} from '../debugDefaults.ts';
import { CameraGrading } from '../environment/grading.ts';
import {
  CheckpointGeometryCache,
  CheckpointPolePool,
  drawCheckpoints,
  syncCheckpointBadges,
  type CheckpointLayer,
} from '../checkpointRender.ts';
import { ElevatedBodyGraphicsPool, type TerrainPaintMode } from '../elevatedLayers.ts';
import { IslandOverlayPool } from '../islandOverlays.ts';
import { CameraControl } from '../cameraControl.ts';
import { CURSOR_ARROW, setPlayCursor } from '../cursors.ts';
import { applyMapBackdrop, backdropSignature } from '../mapBackdrop.ts';
import { applyColumnVisibility } from '../columnVisibility.ts';
import {
  drawStartArea,
  drawTerrain,
  paintWaterlineHints,
  type TerrainGraphics,
  type TerrainLayer,
} from '../mapRender.ts';
import {
  createFpsClock,
  mountFps,
  pushFps,
  type FpsClock,
  type FpsMeter,
} from '../../ui/fps.tsx';
import { boatGuideHeadingAfterWheel } from '../editor/boatGuide.ts';
import { isFloatingPanelOpen } from '../../ui/floatingPanel.tsx';
import { isTypingInField } from '../../ui/overlayTyping.ts';
import { clearEscapeOwner, setEscapeOwner } from '../../ui/pause/escape.ts';
import { mountPauseMenu, type PauseMenu } from '../../ui/pause/menu.tsx';
import { clearSceneSearch } from '../mode.ts';
import { GatePaintClock } from '../gatePaintClock.ts';
import { ScenePerf } from '../perf/scenePerf.ts';
import { RENDER_FLAGS } from '../renderFlags.ts';
import { StaticMesh } from '../terrain/staticMesh.ts';
import { EMPTY_PERF_READOUT, type PerfReadout } from '../../ui/play/perfReadout.ts';
import { confirmDialog } from '../../ui/editor/dialog.tsx';
import { mountOverlay, showOverlay } from '../../ui/overlay.tsx';
import type { DebugLines, PlayDebug } from '../../ui/play/debugPanel.tsx';
import { mountPlayDebug } from '../../ui/play/debugPanel.tsx';
import {
  describeSaveLookTarget,
  saveLookSummary,
  saveLookToMap,
} from '../../ui/play/saveLookToMap.ts';
import type { DebugDefaultsDocument } from '../../sim/debugDefaults/index.ts';
import type { FinishPopup } from '../../ui/play/finishPopup.tsx';
import { mountFinishPopup } from '../../ui/play/finishPopup.tsx';
import { loadHighScores, recordHighScores } from '../../ui/play/highScoresStore.ts';
import { dealCompetitorNames, resolveCompetitorNames } from '../../ui/play/competitorNames.ts';
import type { RaceHud, RaceHudSeat } from '../../ui/play/raceHud.tsx';
import { mountRaceHud } from '../../ui/play/raceHud.tsx';
import type { TelemetryStrip } from '../../ui/play/telemetryStrip.tsx';
import { mountTelemetryStrip } from '../../ui/play/telemetryStrip.tsx';
import type { SceneScale } from '../../ui/sceneScale.tsx';
import { mountSceneScale } from '../../ui/sceneScale.tsx';
import type { ReplayControls } from '../../ui/play/replayControls.tsx';
import { mountReplayControls } from '../../ui/play/replayControls.tsx';
import {
  DEFAULT_PLAY_CAMERA,
  appliedZoom,
  computePlayCamera,
  emptyCameraAim,
  fitPointsFrame,
  lookAheadPoint,
  nextUserZoomFactor,
  patchPlayCamera,
  smoothToward,
  stepCameraAim,
  stepLookAheadGate,
  userFactorForZoom,
  zoomSmoothness,
  type CameraAimState,
  type PlayCameraLimit,
  type PlayCameraSettings,
  type PlayCameraSliderKey,
} from '../playCamera.ts';
import {
  AREA_STROKE_ALPHA,
  PREDICTION_STROKE_ALPHA,
  paintHoverMarker,
  paintPlanningCircle,
  paintPlanningCircleOverland,
  paintReachAft,
  paintReachArea,
  paintReachGhost,
  paintReachOverland,
  paintRestAftHalf,
  paintWindGhostCircle,
} from '../planningCircle.ts';
import { hullDisplaySize, metricHullDisplaySize } from '../hullDisplay.ts';
import {
  GRID_SCREEN_PX,
  MAX_ZOOM,
  PLAY_MIN_ZOOM,
  VOID_COLOR,
  cameraWorldRect,
  screenToWorld,
  worldToViewUv,
} from '../view.ts';
import { noteZoom } from '../zoomChrome.ts';

const MOVE_DURATION_MS = 700;
/** A shove keeps sliding at least this long, so a late hit does not pop into place. */
const SHOVE_TWEEN_MS = 460;
const RECENTER_DURATION_MS = 400;
/** Soft zoom pull while the camera eases to the next hot-seat hull. */
const HANDOFF_DURATION_MS = 900;
const FINISH_CEREMONY_MS = 750;
/** Header hull / gate-miss chips hide after this, or sooner on the owner's next turn. */
const HEADER_CUE_MS = 5000;
/** Camera wash for a damaging contact or a gate miss (`Camera.flash`). */
const IMPACT_FLASH_MS = 220;
const DAMAGE_FLASH_RGB = [255, 36, 36] as const;
const MISS_FLASH_RGB = [255, 214, 32] as const;
const FINISH_ZOOM = 0.38;
const INACTIVE_HULL_ALPHA = 0.55;
const FINISHED_HULL_ALPHA = 0.38;
const FX_TEXT_DEPTH = 12;
const BRAND_FONT = '"Bebas Neue", "Arial Narrow", Impact, sans-serif';
const GO_BANNER_SIZE_PX = 80;
const PAN_DRAG_THRESHOLD_PX = 8;
const AREA_CENTER_DOT_PX = 4;
const AREA_CENTER_STROKE_PX = 1;
const NEUTRAL_COLOR = 0xf4f7fb;
const ROUTE_COLOR = 0xf0c14a;
const AIM_LINE_M = 12;
/** Screen-fixed arrowhead at the aim-line tip (world size is `px / zoom`). */
const AIM_ARROW_LENGTH_PX = 10;
const AIM_ARROW_HALF_PX = 5;
const PLACEMENT_ILLEGAL_ALPHA = 0.4;
const WAKE_WASH_COLOR = 0xb8e4ee;
const WAKE_FOAM_COLOR = 0xf4fbff;
const WAKE_ARM_COLOR = 0xd2eef6;

const GRID_DEPTH = 0;
const WATER_SHEET_DEPTH = 1.25;
/** Shallow beds sit under the water sheet so they read as thinner water, not surface stickers. */
const WATER_DEPTH = 1.1;
const CHECKPOINT_SUBMERGED_DEPTH = 1;
/** Gate waterline sits on the water so the hull sails over it. */
const CHECKPOINT_WATERLINE_DEPTH = 1.35;
const TRAIL_DEPTH = 5;
const ROUTE_DEPTH = 5.5;
const AREA_DEPTH = 6;
const PREDICTION_DEPTH = 7;
const MOVE_LINE_DEPTH = 8;
/** Hover contact chrome (ghost hulls and the contact dot) sits just over the move line. */
const CONTACT_CHROME_DEPTH = MOVE_LINE_DEPTH + 0.05;
/** Contact dot colours: a free nudge, a medium hit, a hard hit or a tap from behind. */
const CONTACT_TIER_COLORS = { nudge: 0x7fe7a4, medium: 0xffc247, hard: 0xff4f4f } as const;
/** Dashed under-cap movement / prediction rims, just under the overland move line. */
const AREA_OVERLAND_DEPTH = 10.38;
/** Dashed under-cap continuation (legal water) sits with dest, above the columns. */
const MOVE_LINE_OVERLAND_DEPTH = 10.4;
/**
 * 2.5-D columns sit above the wake (Phaser depth is per object, so the trail
 * cannot be sorted point-by-point). The hull sits below land and rock so a boat
 * behind a cliff is occluded. Planning disk fills stay under the columns — they
 * do not punch a window. Under-cap water rims restroke dashed above the grass.
 * The gate waterline sits on the water under the hull.
 * Shafts, badges, and the start-area fill stay above the hull so a tall island
 * does not bury placement UI or a pole. Props and shafts share a Y-sort band
 * at CHECKPOINT_DEPTH so a south plant covers a north pole.
 */
const SKIRT_DEPTH = 1.85;
const LAND_DEPTH = 9.5;
const ROCK_DEPTH = 9.7;
const HULL_DEPTH = 9.0;
/** Above land / rocks / gates, just under the dashed under-cap move line (10.4) and dest (10.5). */
const HULL_GHOST_DEPTH = 10.35;
/** Above columns and props so a cliff cannot hide the north-shore dash. */
const WATERLINE_HINT_DEPTH = 10.45;
const HULL_GHOST_ALPHA = 0.4;
const START_AREA_DEPTH = 10.1;
const CHECKPOINT_DEPTH = 10.2;
/**
 * Props and pole shafts share this band above columns (9.5–9.9) and under the
 * hull ghost (10.35). South / larger world y is in front.
 */
const PROP_POLE_Y_SORT_SPAN = 0.12;
const HOVER_MARKER_DEPTH = 10.5;

/** Shortest a bot seat takes to plant, so a human sees where it went. */
const BOT_PLACE_BEAT_MS = 500;
/** Shortest "thinking" beat before a bot shows its aim, even when the worker answers at once. */
const BOT_THINK_BEAT_MS = 650;
/** How long a bot's aim (hover dot and prediction) shows before it commits. */
const BOT_AIM_BEAT_MS = 450;
/** Poll while a seat-handoff glide is still running. */
const BOT_HANDOFF_POLL_MS = 120;

/** The game-flow stage, distinct from the sim's planning/resolving `TurnPhase`. */
type Stage = 'placing' | 'aiming' | 'racing' | 'finished' | 'replay';

/**
 * `testPlay` marks a race launched from the editor. Placement is the same two clicks as a
 * catalog race (plant, then heading), except open water anywhere is legal — not only the
 * start area. Leaving carries the document back so the editor restores the map the author
 * was working on rather than reopening the autosave.
 */
export type GameSceneData = {
  map?: MapDocument;
  mapId?: string;
  testPlay?: boolean;
  /** Bound `maps/<file>.json` when test-playing from the editor. */
  repoFile?: string;
  /** Catalog replay launched from the start-screen toplist. */
  replayScore?: HighScore;
  /** Hot-seat seats (1–4). Omitted / invalid → 1. Test-play and replay force 1. */
  competitorCount?: number;
  /** Catalog hulls, one per seat, in seat order. Test play uses `boat`. Replay uses `replayScore.boat`. */
  boats?: readonly { id: string; boat: BoatDocument }[];
  /** Dealt or edited racer names, one per seat. Omitted for test play and catalog replay. */
  competitorNames?: readonly string[];
  /** Hot-seat turn style. Omitted, solo, test play, and replay are all `sequential`. */
  movement?: MatchMovement;
  /**
   * Boat-to-boat hits. Omitted, solo, test play, and replay are on.
   * `false` is a Hot Seat choice at 2+ boats; terrain contacts stay.
   */
  boatCollisions?: boolean;
  /** Who clicks for each seat. Omitted or unknown seats are human; test play and replay ignore it. */
  seatControllers?: readonly SeatController[];
  /** Hull raced by a test launch. Catalog races pass `boats` instead. */
  boat?: BoatDocument;
  testOrigin?: TestOrigin;
  boatFile?: string;
  boatBaseline?: string;
};

type SeatHull = {
  id: string;
  boat: BoatDocument;
  reach: ReachAxes | null;
  /** Live disk radius. An oval leaves this as the max axis and does not follow the debug stepper. */
  movementRadius: number;
  bowOffsetPx: number;
  lengthM: number;
  beamM: number;
  textureKey: string | null;
};

type ReplayHullLook = {
  hullKey: string;
  ghostKey: string;
  hullW: number;
  hullH: number;
  ghostW: number;
  ghostH: number;
};

type ShoveAnim = {
  view: CompetitorView;
  /** Arc-length fraction of the mover's tween where this hull is struck. */
  along: number;
  shove: ShovedSeat;
  /** Set once the slide from the live pose to the landing has begun. */
  tween: Phaser.Tweens.Tween | null;
  /** Stopped because a later picture owns the hull. `onComplete` must not rewind it. */
  abandoned: boolean;
};

type CompetitorView = {
  id: number;
  hull: Phaser.GameObjects.Image;
  hullGhost: Phaser.GameObjects.Image;
  route: MovePath[];
  trail: MovePath[];
  /**
   * In-flight fraction of this seat's last wake leg. Live / turn-by-turn replay still
   * clip via `this.trailProgress` on the active seat; simultaneous replay writes a
   * per-seat value so every racing hull grows its wake together.
   */
  trailProgress: number;
  turnAnglesDeg: number[];
  replayStart: { position: Vec2; heading: number } | null;
  replayMoves: { destination: Vec2; radius: number }[];
};

export class GameScene extends Phaser.Scene {
  private map: MapDocument = createEmptyMap();
  private boat: BoatState = createBoatState();
  private phase: TurnPhase = 'planning';
  private stage: Stage = 'placing';
  private race: RaceState = createRaceState();
  /**
   * The committed scoring for the move in flight. Gates stay pending on `race` until the hull
   * actually reaches them; `committed` is applied when the tween finishes.
   */
  private activeTravel: {
    fromRace: RaceState;
    visualPath: MovePath;
    committed: RaceState;
  } | null = null;
  /**
   * Simultaneous movement: the moves locked in this round, hidden until every seat has one.
   * `revealRound` plays them together; the lowest seat id is the representative that drives
   * the single set of poles, the telemetry plate, and `activeTravel`.
   */
  /** The fleet resolution for the round currently on screen. Applied when the tween lands. */
  private stagedRound: RoundResolution | null = null;
  private roundTravel: {
    seatId: number;
    path: MovePath | null;
    fromRace: RaceState;
    committed: RaceState;
    fromSpeed: number;
    damaged: boolean;
  }[] = [];
  /** A simultaneous round is playing: every racing hull moves, the camera frames the pack. */
  private revealing = false;
  private moveSpeeds: number[] = [];
  private pendingFinish = false;
  /** True until the boat is placed; the start area is drawn brighter while it is a target. */
  private startAreaHot = false;
  private testPlay = false;
  private testOrigin: TestOrigin | null = null;
  private testBoat: BoatDocument | null = null;
  private boatFile: string | null = null;
  private boatBaseline: string | null = null;
  /** One hull per seat. Planning fields below are the live copy of the active seat. */
  private seatHulls: SeatHull[] = [];
  /** Unequal reach of the active seat. Null keeps the planning disk. */
  private reach: ReachAxes | null = null;
  /** Playtest ring. Off keeps the authored coast and reaches. Not saved. */
  private ringMode: RingMode = 'off';
  private planingSpeedKmh = SKIFF_PLANING_KMH;
  private bowOffsetPx = BOW_OFFSET_PX;
  private hullLengthM = BOAT_LENGTH_M;
  private hullBeamM = BOAT_LENGTH_M / 2;
  /** Pixels per turn applied when the test boat is planted. */
  private startingSpeedPx = 0;
  private liveryPaintToken = 0;
  private replayPaintToken = 0;
  /** Sprite look to put back when a one-row replay returns to the finish plate. */
  private replayHullLook: ReplayHullLook | null = null;
  /** Hold H while placing or aiming a test drive. Same idea as the editor's Space hand. */
  private handDown = false;
  /** Hold B while placing or aiming a test drive. The wheel turns the hull and does not zoom. */
  private boatHeld = false;
  /** Wheel set the facing, and the pointer has not taken over the aim since. */
  private headingFromWheel = false;
  private wheelAimScreen: { x: number; y: number } | null = null;
  /** Native right-drag or H-drag owns this pointer so Phaser does not plant or aim. */
  private panPointerId: number | null = null;
  private mapId: string | null = null;
  private repoFile: string | null = null;
  private competitorCount = 1;
  /** Dealt or edited first names. Test play and catalog replay stay `Player 1`. */
  private competitorNames: string[] = [competitorLabel(0)];
  /** Hot-seat turn style. One seat, test play, and replay are always `sequential`. */
  private movement: MatchMovement = 'sequential';
  /**
   * Boat-to-boat hits. One seat, test play, and replay are always on.
   * Off lets hulls pass through each other; terrain contacts stay.
   */
  private boatCollisions = true;
  private placements: CompetitorPlacement[] = [];
  /** Who clicks for each seat (`docs/BOTS.md`). Test play and catalog replay are all human. */
  private seatControllers: SeatController[] = ['human'];
  /** Present only when a seat is a bot. */
  private botClient: BotClient | null = null;
  /** Per-race seed for bot aim noise and mistakes; on `data-bot-seed` so a strange race can be replayed. */
  private botSeed = 0;
  /** Bumped whenever a pending bot answer must be dropped: a commit, a stage change, or shutdown. */
  private botToken = 0;
  private match: MatchState | null = null;
  private views: CompetitorView[] = [];
  /** Seats currently riding a shove, so their wake grows with the hull instead of appearing whole. */
  private driftingSeats = new Set<number>();
  /** Shoves waiting on the mover's picture. They stay put until that tween reaches the impact. */
  private shoveAnims: ShoveAnim[] = [];
  /** Where on the mover's picture the hit is, so its own heading can ease onto the landing. */
  private impactAlong: number | null = null;
  /** Ripples queued for a hit that is not on screen yet, plus the rings already drawing. */
  private splashTimers: Phaser.Time.TimerEvent[] = [];
  private splashRings: Phaser.GameObjects.Graphics[] = [];
  private raceStartedAt: number | null = null;
  private raceFinishedAt: number | null = null;
  /** Soft seat-handoff: fixed-duration focus tween; skips per-frame followOffset catch-up. */
  private handoffActive = false;
  private handoffTween: Phaser.Tweens.Tween | null = null;
  /**
   * Screen pointer at handoff start. Camera aim stays empty until the pointer moves so
   * the previous seat's click does not lean the view back the moment planning resumes.
   */
  private handoffAimGate: { x: number; y: number } | null = null;
  /** Target zoom while the finish ceremony pulls the camera back. */
  private finishCeremonyZoom: number | null = null;
  private finishBanner: Phaser.GameObjects.Text | null = null;
  private goBanner: Phaser.GameObjects.Text | null = null;
  /** Last boat-clock ms pushed to the race HUD; used to skip identical Preact updates. */
  private lastHudElapsedBucket = -1;
  private fpsHud: FpsMeter | null = null;
  private fpsClock: FpsClock = createFpsClock();
  /** Render probe behind Debug → Perf (docs/PERFORMANCE.md). */
  private scenePerf: ScenePerf | null = null;
  private perfReadout: PerfReadout = EMPTY_PERF_READOUT;
  /** Uncapped visual path of the whole race; the wake trail is length-capped separately. */
  private route: MovePath[] = [];
  private showRoute = false;
  private replayStart: { position: Vec2; heading: number } | null = null;
  private replayMoves: { destination: Vec2; radius: number }[] = [];
  private turnAnglesDeg: number[] = [];
  private lastFinishProgress = 1;
  private sessionScore: HighScore | null = null;
  /** Per-seat times from the race just finished, with hull tints for the finish list. */
  private sessionScoreRows: { score: HighScore; color: string; seatId: number }[] = [];
  /** Session-only hot-seat match replay data (per seat), built when a hot-seat match finishes. */
  private matchReplayData: RaceReplay[] | null = null;
  /** Finished-race gate set, restored when Close replay returns to the Finished! pane. */
  private finishedRace: RaceState | null = null;
  /** Finished-race wake per seat, restored alongside `finishedRace` (replay re-slices it). */
  private finishedViewTrails: ReadonlyMap<number, MovePath[]> | null = null;
  private replay: {
    /** Per-seat reconstruction; solo replay has exactly one entry (seatId 0). */
    seats: ReadonlyArray<{
      seatId: number;
      boats: BoatState[];
      paths: MovePath[];
      /** Gate state entering each of this seat's turns, so a frame scores one path. */
      racesBefore: RaceState[];
      /** Raw recorded clicks (destination + movement radius), for camera framing. */
      moves: { destination: Vec2; radius: number }[];
    }>;
    /** Flat original turn order across every seat (solo: seatId 0 repeated once per move). */
    steps: readonly MatchReplayStep[];
    stepIndex: number;
    moveProgress: number;
    playing: boolean;
    valid: boolean;
    returnToFinished: boolean;
    /** Solo only, for the Finished! pane numbers on Close replay; null for a hot-seat match. */
    score: HighScore | null;
  } | null = null;
  private replayCurves: (Phaser.Curves.Path | null)[] = [];
  /** Per-seat curves for `simultaneous` mode, keyed by seatId, indexed by that seat's own
   * localIndex — `replayCurves` is flat over the alternating order and cannot be indexed by a
   * round number shared across seats. */
  private replaySeatCurves = new Map<number, (Phaser.Curves.Path | null)[]>();
  /** `steps` grouped into rounds (every seat's Nth move is round N) — see
   * `groupReplayStepsByRound`. Built once per `beginReplayPlayback`. */
  private replayRounds: MatchReplayStep[][] = [];
  /** Per-seat world speed along the current all-at-once path (px / turn-time). */
  private readonly replayWorldSpeeds = new Map<number, number>();
  /** Zoom used the last time poles were painted — not the live camera if a snap is pending. */
  private gatesPaintZoom = 1;
  private readonly gatePaintClock = new GatePaintClock();
  /** Last `data-*` probe values written to the container, so a still frame writes nothing. */
  private readonly probeAttrs = new Map<string, string>();
  /** Camera signature the grid / sheet were last drawn for. */
  private gridDrawnFor = '';
  /** Screen anchor the placement hint was last synced at. */
  private placementHintAnchorKey = '';
  /** Zoom the map layers were last stroked for. A drift inside 8% does not restroke. */
  private mapLayersZoom = 1;
  private moveTween: Phaser.Tweens.Tween | null = null;
  /** Stops Escape and the HUD both queueing a second scene start mid-teardown. */
  private leaving = false;
  private pauseMenu: PauseMenu | null = null;

  private hull!: Phaser.GameObjects.Image;
  private hullGhost!: Phaser.GameObjects.Image;
  private gridGraphics!: Phaser.GameObjects.Graphics;
  private waterSheetGraphics!: Phaser.GameObjects.Graphics;
  private terrainGraphics!: TerrainGraphics;
  private elevatedLayers: ElevatedBodyGraphicsPool | null = null;
  private islandOverlays: IslandOverlayPool | null = null;
  private columnVisibleKey = '';
  private columnVisibleIds: ReadonlySet<string> | null = null;
  /** Padded world view the last cull ran for; a barely-moved camera skips the recompute. */
  private columnVisibleView: ViewRect | null = null;
  private checkpointPoles: CheckpointPolePool | null = null;
  private startAreaGraphics!: Phaser.GameObjects.Graphics;
  private checkpointSubmergedGraphics!: CheckpointLayer;
  private checkpointWaterlineGraphics!: CheckpointLayer;
  private checkpointGraphics!: CheckpointLayer;
  private checkpointLabels = new Map<string, Phaser.GameObjects.Text>();
  private readonly checkpointGeometry = new CheckpointGeometryCache();
  private trailGraphics!: Phaser.GameObjects.Graphics;
  private routeGraphics!: Phaser.GameObjects.Graphics;
  private areaGraphics!: Phaser.GameObjects.Graphics;
  private areaOverlandGraphics!: Phaser.GameObjects.Graphics;
  private predictionGraphics!: Phaser.GameObjects.Graphics;
  private predictionOverlandGraphics!: Phaser.GameObjects.Graphics;
  private moveLineGraphics!: Phaser.GameObjects.Graphics;
  private moveLineOverlandGraphics!: Phaser.GameObjects.Graphics;
  /** Where the aimed move would meet another hull: both contact capsules and the contact point. */
  private contactGraphics?: Phaser.GameObjects.Graphics;
  private hoverMarker!: Phaser.GameObjects.Graphics;

  private debugPanel: PlayDebug | null = null;
  private telemetry: TelemetryStrip | null = null;
  private sceneScale: SceneScale | null = null;
  private raceHud: RaceHud | null = null;
  private finishPopup: FinishPopup | null = null;
  private replayControls: ReplayControls | null = null;
  private replayPlaybackRate: ReplayPlaybackRate = DEFAULT_REPLAY_PLAYBACK_RATE;
  private replayPlaybackMode: ReplayPlaybackMode = DEFAULT_REPLAY_PLAYBACK_MODE;
  /** Header hull chip: owner + expiry; row icon uses per-seat HullState instead. */
  private hullFlash: { owner: number; untilMs: number } | null = null;
  /** Header gate-miss chip: owner + expiry; row icon uses missedGateIds. */
  private missFlash: { owner: number; untilMs: number } | null = null;
  private hullFlashTimer: Phaser.Time.TimerEvent | null = null;
  private missFlashTimer: Phaser.Time.TimerEvent | null = null;
  /** Per-seat missed checkpoint ids; dropped once that seat passes the gate. */
  private missedGateIds = new Map<number, string[]>();
  private grading: CameraGrading | null = null;
  private lighting: SunLighting | null = null;
  private rolled: RolledEnvironment = rollEnvironment(
    createDefaultEnvironment(),
    0,
  );

  private movementRadius = MOVEMENT_RADIUS;
  private crashHarshness = CRASH_HARSHNESS;
  private turnTax = TURN_TAX;
  private windFeel: WindFeel = cloneWindFeel(DEFAULT_WIND_FEEL);
  /** Pre-crash commit so the harshness slider can re-run the bounce chain. */
  private lastCrashPlan: { boat: BoatState; dest: Vec2; radius: number } | null = null;
  private curveKind: CurveKind = DEFAULT_CURVE_KIND;
  private lines: DebugLines = {
    trail: true,
    moveLine: true,
    prediction: true,
    windGhost: false,
  };
  private aimChrome: AimChrome = cloneAimChrome(DEFAULT_AIM_CHROME);
  private previewGates = true;
  private stickyEdges = true;
  private stickyOverflowPx = STICK_OVERFLOW_PX;
  private nearMissPx = NEAR_MISS_PX;
  private sunBakeFrame: number | null = null;
  private trail: MovePath[] = [];
  /** 0–1 along the newest committed path while the hull is still travelling. */
  private trailProgress = 1;
  private trailLengthM = TRAIL_LENGTH_DEFAULT_M;
  private trailSpeedGain = TRAIL_SPEED_GAIN_DEFAULT;
  private hoverPoint: Vec2 | null = null;
  /** Last pointer screen position dest was derived from. Camera-only motion must not update dest. */
  private hoverPointerScreen: Vec2 | null = null;
  /**
   * Stage at the start of the current left-press. A heading-lock down starts the
   * race; the matching up must not commit seat 0's first turn.
   */
  private pointerDownStage: Stage | null = null;
  private hoverPlan: CommitResult | null = null;
  private logLines: PlayLogLine[] = [];
  private tracking = false;
  private playCamera: PlayCameraSettings = { ...DEFAULT_PLAY_CAMERA };
  private cameraAim: CameraAimState = emptyCameraAim();
  /**
   * 0–1 on speed look-ahead toward the planning circle. Stays at 1 during race /
   * replay so the Look-ahead slider always applies; 0 only during a hot-seat
   * handoff glide.
   */
  private lookAheadGate = 0;
  private userZoomFactor = 1;
  private autoZoom = 1;
  private fitZoom = 1;
  private speedZoomValue = 1;
  private cameraLimit: PlayCameraLimit = 'Speed';
  /** Speed at the start of the in-flight commit; camera lerps from this while resolving. */
  private moveFromSpeed: number | null = null;
  private lastHudCameraKey = '';
  private lastHudCameraAt = 0;
  private readonly cameraControl = new CameraControl({
    minZoom: PLAY_MIN_ZOOM,
    maxZoom: MAX_ZOOM,
    constrainFit: 'center',
    usePhaserBoundsWhenMapFillsView: true,
  });
  private readonly curvePoint = new Phaser.Math.Vector2();
  private readonly curveTangent = new Phaser.Math.Vector2();

  constructor() {
    super('GameScene');
  }

  create(data: GameSceneData): void {
    setPlayCursor(true);
    this.input.setDefaultCursor(CURSOR_ARROW);
    this.map = data.map ?? createEmptyMap();
    this.columnVisibleKey = '';
    this.columnVisibleIds = null;
    this.testPlay = data.testPlay === true;
    this.testOrigin = this.testPlay ? (data.testOrigin ?? 'editor') : null;
    this.testBoat = data.boat ? cloneBoatDocument(data.boat) : null;
    this.boatFile = data.boatFile ?? null;
    this.boatBaseline = data.boatBaseline ?? null;
    this.reach = null;
    this.bowOffsetPx = BOW_OFFSET_PX;
    this.hullLengthM = BOAT_LENGTH_M;
    this.hullBeamM = BOAT_LENGTH_M / 2;
    this.startingSpeedPx = 0;
    this.liveryPaintToken += 1;
    this.replayPaintToken += 1;
    this.replayHullLook = null;
    this.handDown = false;
    this.boatHeld = false;
    this.headingFromWheel = false;
    this.wheelAimScreen = null;
    this.panPointerId = null;
    this.mapId = data.mapId ?? null;
    this.repoFile = data.repoFile ?? null;
    this.competitorCount =
      this.testPlay || data.replayScore
        ? 1
        : clampCompetitorCount(data.competitorCount);
    this.competitorNames =
      resolveCompetitorNames(data.competitorNames, this.competitorCount) ??
      (this.competitorCount > 1
        ? dealCompetitorNames(this.competitorCount)
        : [competitorLabel(0)]);
    // One boat has nobody to lock in against, and neither test play nor a replay has seats.
    this.movement =
      this.competitorCount > 1 && data.movement === 'simultaneous'
        ? 'simultaneous'
        : 'sequential';
    // One boat has nobody to hit. Omitted stays on, which is every race before this choice.
    this.boatCollisions =
      this.competitorCount > 1 && data.boatCollisions === false ? false : true;
    setBoatCollisionsEnabled(this.boatCollisions);
    this.seatControllers = resolveSeatControllers(
      this.testPlay || data.replayScore ? undefined : data.seatControllers,
      this.competitorCount,
    );
    this.botToken += 1;
    this.botClient?.destroy();
    this.botClient = this.seatControllers.some((controller) => seatIsBot(controller))
      ? createBotClient(cloneMapDocument(this.map), this.mapId ?? this.map.name, this.boatCollisions)
      : null;
    this.botSeed = Math.floor(Math.random() * 0x100000000);
    this.seatHulls = this.buildSeatHulls(data);
    this.placements = [];
    this.match = null;
    this.clearCompetitorViews();
    this.checkpointLabels.clear();
    this.islandOverlays?.destroy();
    this.islandOverlays = null;
    this.checkpointPoles?.destroy();
    this.checkpointPoles = null;
    this.elevatedLayers?.destroy();
    this.elevatedLayers = null;
    this.boat = createBoatState();
    this.ringMode = 'off';
    this.phase = 'planning';
    this.stage = 'placing';
    this.race = createRaceState();
    this.activeTravel = null;
    this.roundTravel = [];
    this.stagedRound = null;
    this.revealing = false;
    this.moveSpeeds = [];
    this.pendingFinish = false;
    this.startAreaHot = false;
    this.tracking = false;
    this.userZoomFactor = 1;
    this.autoZoom = 1;
    this.fitZoom = 1;
    this.speedZoomValue = 1;
    this.cameraLimit = 'Speed';
    this.moveFromSpeed = null;
    this.lastHudCameraKey = '';
    this.lastHudCameraAt = 0;
    this.cameraControl.reset();
    this.cameraControl.setWorldBounds(this.map.bounds);
    this.lastCrashPlan = null;
    this.applyPlayDebugDefaults(loadPlayDebugDefaults());
    this.installLiveSeat();
    this.hydrateLookFromMap();
    this.trail = [];
    this.trailProgress = 1;
    this.hoverPoint = null;
    this.hoverPointerScreen = null;
    this.pointerDownStage = null;
    this.cameraAim = emptyCameraAim();
    this.lookAheadGate = 0;
    this.hoverPlan = null;
    this.clearHeaderCues();
    this.clearImpactFlash();
    this.missedGateIds.clear();
    this.logLines = [];
    this.route = [];
    this.showRoute = false;
    this.replayStart = null;
    this.replayMoves = [];
    this.turnAnglesDeg = [];
    this.lastFinishProgress = 1;
    this.sessionScore = null;
    this.sessionScoreRows = [];
    this.matchReplayData = null;
    this.finishedRace = null;
    this.finishedViewTrails = null;
    this.replay = null;
    this.mapLayersZoom = 1;
    this.replayPlaybackRate = DEFAULT_REPLAY_PLAYBACK_RATE;
    this.replayPlaybackMode = DEFAULT_REPLAY_PLAYBACK_MODE;
    this.replayRounds = [];
    this.replayWorldSpeeds.clear();
    this.gatesPaintZoom = 1;
    this.gatePaintClock.reset(1);
    this.clearReplayCurves();
    this.moveTween = null;
    this.leaving = false;
    this.pauseMenu = null;
    this.raceStartedAt = null;
    this.raceFinishedAt = null;
    this.handoffActive = false;
    this.handoffTween?.stop();
    this.handoffTween = null;
    this.handoffAimGate = null;
    this.finishCeremonyZoom = null;
    this.finishBanner?.destroy();
    this.finishBanner = null;
    this.goBanner?.destroy();
    this.goBanner = null;
    this.lastHudElapsedBucket = -1;
    this.fpsClock = createFpsClock();

    showOverlay(mountOverlay());
    this.fpsHud = mountFps();
    pushFps(this.fpsHud, this.game.loop, this.fpsClock);
    this.debugPanel = mountPlayDebug(
      {
        setCurve: (kind) => {
          this.setCurve(kind);
        },
        setLine: (line, next) => {
          this.setLine(line, next);
        },
        setCapFlockEnabled: (next) => {
          this.setCapFlockEnabled(next);
        },
        setCapFlockAmount: (next) => {
          this.setCapFlockAmount(next);
        },
        onSunLive: (sun) => {
          this.applySunLive(sun);
        },
        onSunCommit: () => {
          this.commitSunEdit();
        },
        onLookLive: (look) => {
          this.applyLookLive(look);
        },
        onLookCommit: () => {
          this.applySheetLook();
          this.syncHud();
        },
        setShowRoute: (next) => {
          this.setShowRoute(next);
        },
        setPreviewGates: (next) => {
          this.setPreviewGates(next);
        },
        setStickyEdges: (next) => {
          this.setStickyEdges(next);
        },
        stepStickyOverflow: (direction) => {
          this.stepStickyOverflow(direction);
        },
        setStickyOverflow: (next) => {
          this.setStickyOverflow(next);
        },
        stepNearMiss: (direction) => {
          this.stepNearMiss(direction);
        },
        setNearMiss: (next) => {
          this.setNearMiss(next);
        },
        stepRadius: (direction) => {
          this.stepRadius(direction);
        },
        setCrashHarshness: (next) => {
          this.setCrashHarshness(next);
        },
        setTurnTax: (next) => {
          this.setTurnTax(next);
        },
        setRingMode: (next) => {
          this.setRingMode(next);
        },
        setWindFeel: (patch) => {
          this.setWindFeel(patch);
        },
        stepTrailLength: (direction) => {
          this.stepTrailLength(direction);
        },
        stepTrailSpeedGain: (direction) => {
          this.stepTrailSpeedGain(direction);
        },
        setCameraEnabled: (next) => {
          this.setCameraEnabled(next);
        },
        setCameraSlider: (key, next) => {
          this.setCameraSlider(key, next);
        },
        onAimChromeLive: (patch) => {
          this.applyAimChromeLive(patch);
        },
        setWaterlineHintAlpha: (next) => {
          this.setWaterlineHintAlpha(next);
        },
        setUnderCapRimAlpha: (next) => {
          this.setUnderCapRimAlpha(next);
        },
        saveToMap: () => this.saveLookToMap(),
      },
      { perfOpen: RENDER_FLAGS.perf },
    );
    this.scenePerf?.destroy();
    this.scenePerf = new ScenePerf(this.game);
    this.perfReadout = EMPTY_PERF_READOUT;
    this.raceHud = mountRaceHud({
      actions: {
        onExitToEditor: this.testPlay ? () => this.exitToEditor() : null,
        exitLabel: this.testPlay
          ? testLeaveLabel(this.testOrigin ?? 'editor')
          : 'Back to editor',
      },
    });
    this.telemetry = mountTelemetryStrip();
    this.sceneScale = mountSceneScale('hud');

    this.gridGraphics = this.add.graphics().setDepth(GRID_DEPTH);
    // Retained terrain geometry (docs/PERFORMANCE.md). `?staticterrain=0` keeps the
    // per-frame Graphics painter for A/B runs; Canvas always uses it.
    const staticTerrain = RENDER_FLAGS.staticTerrain && StaticMesh.isSupported(this);
    const terrainLayer = (depth: number): TerrainLayer =>
      (staticTerrain
        ? this.add.existing(new StaticMesh(this))
        : this.add.graphics()
      ).setDepth(depth);
    this.terrainGraphics = {
      water: terrainLayer(WATER_DEPTH),
      // Rims and reef soft edges sit just above the bed fills, still under the sheet.
      waterChrome: terrainLayer(WATER_DEPTH + 0.01),
      skirt: terrainLayer(SKIRT_DEPTH),
      land: this.add.graphics().setDepth(LAND_DEPTH),
      rock: this.add.graphics().setDepth(ROCK_DEPTH),
      waterlineHint: terrainLayer(WATERLINE_HINT_DEPTH),
    };
    this.elevatedLayers = new ElevatedBodyGraphicsPool(this, LAND_DEPTH, {
      staticGeometry: staticTerrain,
    });
    this.islandOverlays = new IslandOverlayPool(this, {
      base: CHECKPOINT_DEPTH,
      span: PROP_POLE_Y_SORT_SPAN,
    });
    this.checkpointPoles = new CheckpointPolePool(
      this,
      {
        base: CHECKPOINT_DEPTH,
        span: PROP_POLE_Y_SORT_SPAN,
      },
      { staticGeometry: staticTerrain },
    );
    this.waterSheetGraphics = this.add.graphics().setDepth(WATER_SHEET_DEPTH);
    this.startAreaGraphics = this.add.graphics().setDepth(START_AREA_DEPTH);
    // Gate layers repaint on the gate clock only, so they are retained meshes too.
    this.checkpointSubmergedGraphics = terrainLayer(CHECKPOINT_SUBMERGED_DEPTH);
    this.checkpointWaterlineGraphics = terrainLayer(CHECKPOINT_WATERLINE_DEPTH);
    this.checkpointGraphics = terrainLayer(CHECKPOINT_DEPTH);
    this.trailGraphics = this.add.graphics().setDepth(TRAIL_DEPTH);
    this.routeGraphics = this.add.graphics().setDepth(ROUTE_DEPTH);
    this.areaGraphics = this.add.graphics().setDepth(AREA_DEPTH);
    this.areaOverlandGraphics = this.add.graphics().setDepth(AREA_OVERLAND_DEPTH);
    this.predictionGraphics = this.add.graphics().setDepth(PREDICTION_DEPTH);
    this.predictionOverlandGraphics = this.add
      .graphics()
      .setDepth(AREA_OVERLAND_DEPTH);
    this.moveLineGraphics = this.add.graphics().setDepth(MOVE_LINE_DEPTH);
    this.moveLineOverlandGraphics = this.add.graphics().setDepth(MOVE_LINE_OVERLAND_DEPTH);
    this.contactGraphics = this.add.graphics().setDepth(CONTACT_CHROME_DEPTH);
    const starterSize = this.metricHull();
    const starter = this.spawnHullPair(
      this.boat.position,
      this.boat.heading,
      0,
      starterSize.displayW,
      starterSize.displayH,
    );
    this.hull = starter.hull.setVisible(false);
    this.hullGhost = starter.hullGhost.setVisible(false);
    this.hoverMarker = this.add
      .graphics()
      .setDepth(HOVER_MARKER_DEPTH)
      .setVisible(false);

    this.drawMapLayers();
    this.lighting = new SunLighting(this, 'play');
    this.lighting.setEnabled(true, this.map);
    this.lighting.setSheetAppearance(rgb24(this.map.look.waterColor), this.map.look.waterColor.a);

    this.grading = new CameraGrading(this.cameras.main);
    this.applyEnvironment();

    this.setMovementAreaVisible(false);

    const cam = this.cameras.main;
    cam.setBackgroundColor(VOID_COLOR);
    this.cameraControl.setWorldBounds(this.map.bounds);
    this.constrainCamera();
    this.syncMatchDataAttrs();

    const centroid = startAreaCentroid(this.map);

    // Catalog and test drive both use the hull as a cursor: a click plants, a second click
    // sets heading. Test drive may plant on any open water. A catalog map with no start
    // area still drops the boat, because there is no box to click inside.
    if (data.replayScore) {
      cam.centerOn(centroid.x, centroid.y);
      this.startReplay(data.replayScore);
    } else if (!this.testPlay && !this.map.startArea) {
      this.autoPlaceAll(centroid);
    } else {
      cam.centerOn(centroid.x, centroid.y);
      this.beginPlacementCursor(centroid);
      if (this.testPlay) {
        this.openStartingSpeed();
      }
      this.placeBotSeat();
    }

    void this.paintSeatLiveries();

    this.redrawGrid();
    this.syncHud();
    this.syncRaceHud();

    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('pointerupoutside', this.onPointerUp, this);
    this.input.on('wheel', this.onWheel, this);
    this.input.on('gameout', this.onGameOut, this);
    this.input.on('gameover', this.onGameOver, this);

    const escapeToken = setEscapeOwner({
      onEscape: () => this.handleEscape(),
    });

    window.addEventListener('keydown', this.onPlaningKeyDown);

    if (this.testPlay) {
      this.input.mouse?.disableContextMenu();
      window.addEventListener('keydown', this.onTestPanKeyDown, { capture: true });
      window.addEventListener('keyup', this.onTestPanKeyUp, { capture: true });
      window.addEventListener('blur', this.onTestPanBlur);
      window.addEventListener('pointerdown', this.onTestPanPointerDown, { capture: true });
      window.addEventListener('pointermove', this.onTestPanPointerMove, { capture: true });
      window.addEventListener('pointerup', this.onTestPanPointerUp, { capture: true });
      window.addEventListener('pointercancel', this.onTestPanPointerUp, { capture: true });
    }

    this.scale.on(Phaser.Scale.Events.RESIZE, this.onGameResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.botToken += 1;
      this.botClient?.destroy();
      this.botClient = null;
      this.setBotThinking(null);
      closeStartingSpeedDialog();
      this.liveryPaintToken += 1;
      this.replayPaintToken += 1;
      this.removeSeatTextures();
      clearEscapeOwner(escapeToken);
      this.destroyPause();
      this.unbindTestPan();
      window.removeEventListener('keydown', this.onPlaningKeyDown);
      this.cancelSunBake();
      this.setPlacementCursorHidden(false);
      this.scale.off(Phaser.Scale.Events.RESIZE, this.onGameResize, this);
      this.fpsHud?.destroy();
      this.fpsHud = null;
      this.scenePerf?.destroy();
      this.scenePerf = null;
      this.debugPanel?.destroy();
      this.debugPanel = null;
      this.telemetry?.destroy();
      this.telemetry = null;
      this.sceneScale?.destroy();
      this.sceneScale = null;
      this.raceHud?.destroy();
      this.raceHud = null;
      this.finishPopup?.destroy();
      this.finishPopup = null;
      this.replayControls?.destroy();
      this.replayControls = null;
      this.handoffTween?.stop();
      this.handoffTween = null;
      this.finishBanner?.destroy();
      this.finishBanner = null;
      this.goBanner?.destroy();
      this.goBanner = null;
      this.clearReplayCurves();
      this.teardownPlayVisuals();
      this.checkpointLabels.clear();
      this.checkpointGeometry.clear();
      this.islandOverlays?.destroy();
      this.islandOverlays = null;
      this.checkpointPoles?.destroy();
      this.checkpointPoles = null;
      this.elevatedLayers?.destroy();
      this.elevatedLayers = null;
    });

    this.snapAutoZoom();
    this.mapLayersZoom = this.cameras.main.zoom;
    this.drawMapLayers('chrome');
    this.game.canvas.parentElement?.setAttribute('data-ready', 'true');
    void this.hydratePlayDebugDefaultsFromRepo();
  }

  private async hydratePlayDebugDefaultsFromRepo(): Promise<void> {
    const document = await hydratePlayDebugDefaultsFromRepo();

    if (!document || !this.sys.isActive()) {
      return;
    }

    this.applyPlayDebugDefaults(document);
    this.installLiveSeat();
    this.lighting?.setEnabled(true, this.map, this.columnVisibleIds);
    this.drawMapLayers();

    if (this.stage === 'placing' || this.stage === 'aiming') {
      this.snapAutoZoom();
    }

    this.refreshZoomScaledGraphics();
    this.syncHud();
  }

  update(_time: number, delta: number): void {
    if (!this.sys.isActive() || !this.hull?.scene) {
      return;
    }

    this.updatePlayCamera();
    // Camera follow and pan tweens move the scroll behind our back, so the constraint is
    // re-applied every frame rather than only at the input handlers.
    this.constrainCamera();
    this.writeHandoffProbe();
    this.cullOffscreenColumns();
    this.refreshHoverFromPointer();
    this.stepPlayCameraAim(delta);
    this.stepLookAheadGateState(delta);
    this.refreshPlacementFromPointer();
    this.refreshPlacementHintAnchor();
    this.redrawGrid(false);
    pushFps(this.fpsHud, this.game.loop, this.fpsClock);
    this.pushPerf();

    if (
      this.stage === 'racing' ||
      this.stage === 'replay' ||
      this.stage === 'finished'
    ) {
      const elapsed = this.boatTimeMs();
      const bucket = Math.floor(elapsed / 32);

      if (bucket !== this.lastHudElapsedBucket) {
        this.lastHudElapsedBucket = bucket;
        this.syncRaceHud();
      }
    }
  }

  /**
   * Refresh the Debug → Perf readout four times a second. The probe samples every frame;
   * the overlay only sees a summary so Preact never re-renders per frame.
   */
  private pushPerf(): void {
    const next = this.scenePerf?.poll();

    if (!next) {
      return;
    }

    this.perfReadout = next;
    this.syncHud();
  }

  /** Squared wind drift on the movement-disk centre; zero on a calm or wind-off map. */
  private windOffset(boat: BoatState = this.boat): Vec2 {
    return windOffsetPx(this.map.environment, boat.speed, this.windFeel);
  }

  private elapsedMs(): number {
    if (this.raceStartedAt === null) {
      return 0;
    }

    const end = this.raceFinishedAt ?? performance.now();
    return Math.max(0, end - this.raceStartedAt);
  }

  /**
   * Boat-clock cursor: 0 at rest, one unit per committed turn, plus the in-flight
   * path fraction so the HUD can count `TURN_DURATION_S` seconds over the tween.
   */
  private boatClockCursor(): number {
    if (this.stage === 'replay' && this.replay) {
      return this.replayCursor();
    }

    const pathCount = this.moveSpeeds.length;

    if (pathCount <= 0) {
      return 0;
    }

    const raw = this.activeTravel
      ? pathCount - 1 + this.trailProgress
      : pathCount;
    const finishCursor = pathCount - 1 + this.lastFinishProgress;

    if (this.pendingFinish || this.stage === 'finished') {
      return Math.min(raw, finishCursor);
    }

    return raw;
  }

  private boatTimeMs(): number {
    const pathCount =
      this.stage === 'replay' && this.replay
        ? this.replayDomainLength()
        : this.moveSpeeds.length;
    const clock = boatClockFromCursor(
      this.boatClockCursor(),
      pathCount,
      this.lastFinishProgress,
    );

    // Solo replay carries a HighScore to cap rounding against; a hot-seat match replay has no
    // single official time to cap against (each seat has its own), so the raw clock stands.
    if (this.stage === 'replay' && this.replay?.score) {
      return Math.min(clock, this.replay.score.calculatedTimeMs);
    }

    return clock;
  }

  /**
   * The reach a move is planned and committed against. Wind offsets the disk centre rather than
   * its radius, so this is simply the authored circle size.
   */
  private effectiveMovementRadius(): number {
    return this.movementRadius;
  }

  private metricHull() {
    return metricHullDisplaySize(this.hullLengthM, this.hullBeamM);
  }

  private boatIdFromFile(file: string | null): string {
    if (!file) {
      return SKIFF_BOAT_ID;
    }

    const stem = file.replace(/\.json$/i, '').trim();
    return stem || SKIFF_BOAT_ID;
  }

  private seatHullFromDocument(id: string, boat: BoatDocument): SeatHull {
    const feel = planningFeelFromBoat(boat);

    return {
      id,
      boat,
      reach: feel.reach,
      movementRadius: feel.movementRadius,
      bowOffsetPx: feel.bowOffsetPx,
      lengthM: feel.lengthM,
      beamM: feel.beamM,
      textureKey: null,
    };
  }

  private seatHullFromScore(boat: HighScoreBoat): SeatHull {
    const document = createSkiffBoat();
    document.name = boat.name;
    document.lengthM = boat.lengthM;
    document.beamM = boat.beamM;
    document.turnM = boat.turnM;
    document.accelM = boat.accelM;
    document.brakeM = boat.brakeM;
    document.planingSpeedKmh = boat.planingSpeedKmh;
    document.hull = { ...boat.hull };
    document.deck = { ...boat.deck };
    document.accent = { ...boat.accent };
    return this.seatHullFromDocument(boat.id, document);
  }

  private buildSeatHulls(data: GameSceneData): SeatHull[] {
    if (data.replayScore) {
      return [this.seatHullFromScore(data.replayScore.boat)];
    }

    if (this.testPlay) {
      const boat = data.boat ? cloneBoatDocument(data.boat) : createSkiffBoat();
      return [
        this.seatHullFromDocument(
          this.boatIdFromFile(data.boatFile ?? null),
          boat,
        ),
      ];
    }

    const seats: SeatHull[] = [];

    for (let index = 0; index < this.competitorCount; index += 1) {
      const entry = data.boats?.[index];

      if (entry) {
        seats.push(this.seatHullFromDocument(entry.id, cloneBoatDocument(entry.boat)));
      } else {
        seats.push(this.seatHullFromDocument(SKIFF_BOAT_ID, createSkiffBoat()));
      }
    }

    return seats;
  }

  private liveSeatId(): number {
    if (this.stage === 'replay' && this.replay) {
      return this.replay.steps[this.replay.stepIndex]?.seatId ?? 0;
    }

    if (this.match && (this.stage === 'racing' || this.stage === 'finished')) {
      return this.match.activeId;
    }

    return Math.min(this.placements.length, Math.max(0, this.seatHulls.length - 1));
  }

  /** Copies the seat's live reach onto the planning fields. Does not rebuild them from the document. */
  private installSeat(id: number): void {
    const seat = this.seatHulls[id];

    if (!seat) {
      return;
    }

    this.reach = seat.reach;
    this.movementRadius = seat.movementRadius;
    this.bowOffsetPx = seat.bowOffsetPx;
    this.hullLengthM = seat.lengthM;
    this.hullBeamM = seat.beamM;
    this.planingSpeedKmh = seat.boat.planingSpeedKmh;
  }

  private installLiveSeat(): void {
    this.installSeat(this.liveSeatId());
  }

  private hullTexture(id: number): string {
    const key = this.seatHulls[id]?.textureKey;

    if (key && this.textures.exists(key)) {
      return key;
    }

    return 'boat';
  }

  private applySeatTexture(id: number): void {
    const key = this.seatHulls[id]?.textureKey;

    if (!key || !this.textures.exists(key)) {
      return;
    }

    const view = this.views.find((entry) => entry.id === id);

    if (view?.hull.scene) {
      view.hull.setTexture(key);
      view.hullGhost.setTexture(key);
      this.applySeatTint(view.hull, view.hullGhost, id);
    }

    const cursor =
      !this.match &&
      this.placements.length === id &&
      !this.views.some((entry) => entry.hull === this.hull);
    const bareHull = this.views.length === 0 && id === 0;

    if ((cursor || bareHull) && this.hull?.scene) {
      this.hull.setTexture(key);
      this.hullGhost.setTexture(key);
      this.applySeatTint(this.hull, this.hullGhost, id);
    }
  }

  /** Same multiply tint as the standings dot. White (seat 0) leaves the factory paint. */
  private applySeatTint(
    hull: Phaser.GameObjects.Image,
    ghost: Phaser.GameObjects.Image,
    id: number,
  ): void {
    if (!hull.scene || !ghost.scene) {
      return;
    }

    const tint = competitorTint(id);
    hull.setTint(tint).setTintMode(Phaser.TintModes.MULTIPLY);
    ghost.setTint(tint).setTintMode(Phaser.TintModes.MULTIPLY);
  }

  private hullSeatId(hull: Phaser.GameObjects.Image): number {
    return this.views.find((view) => view.hull === hull)?.id ?? 0;
  }

  private removeSeatTextures(): void {
    const keys = new Set<string>(['boat-replay-row']);

    for (const seat of this.seatHulls) {
      if (seat.textureKey) {
        keys.add(seat.textureKey);
      }
    }

    for (const key of keys) {
      if (this.textures.exists(key)) {
        this.textures.remove(key);
      }
    }
  }

  private async paintSeatLiveries(): Promise<void> {
    const token = ++this.liveryPaintToken;
    const source = await loadBoatSvgSource();

    if (!source || !this.sys.isActive() || token !== this.liveryPaintToken) {
      return;
    }

    for (let index = 0; index < this.seatHulls.length; index += 1) {
      const seat = this.seatHulls[index];

      if (!seat) {
        continue;
      }

      const key = await loadPaintedBoatTexture(this, source, seat.boat, `boat-livery-${index}`);

      if (!key || !this.sys.isActive() || token !== this.liveryPaintToken) {
        return;
      }

      seat.textureKey = key;
      this.applySeatTexture(index);
    }
  }

  private scoreBoat(seatId: number): HighScoreBoat {
    const seat = this.seatHulls[seatId] ?? this.seatHulls[0];

    if (!seat) {
      return highScoreBoatFromDocument(SKIFF_BOAT_ID, createSkiffBoat());
    }

    return highScoreBoatFromDocument(seat.id, seat.boat);
  }

  private openStartingSpeed(): void {
    this.startingSpeedPx = 0;
    openStartingSpeedDialog({
      onAccept: (kmh) => {
        this.startingSpeedPx = fromDisplayKmh(kmh);
      },
      onCancel: () => {
        this.startingSpeedPx = 0;
      },
    });
  }

  private planPoint(boat: BoatState, point: Vec2, radius = this.effectiveMovementRadius()) {
    return planWithFeel(boat, point, this.map, this.turnFeel(radius), this.fleetContext());
  }

  /** Hull sizes per seat, so a shove uses the boat each player actually picked. */
  private seatHullSizes(): Map<number, HullDimensions> {
    const sizes = new Map<number, HullDimensions>();

    this.seatHulls.forEach((seat, id) => {
      sizes.set(id, { lengthM: seat.lengthM, beamM: seat.beamM });
    });

    return sizes;
  }

  /**
   * The other boats, as the hulls they are sitting on right now. Hover cannot
   * know where anyone else is about to click, so this is the honest picture.
   */
  private fleetContext(): FleetContext | undefined {
    const match = this.match;

    if (
      !match ||
      match.competitors.length < 2 ||
      this.stage !== 'racing' ||
      !boatCollisionsActive()
    ) {
      return undefined;
    }

    const sizes = this.seatHullSizes();
    const activeId = match.activeId;

    return {
      id: activeId,
      hull: sizes.get(activeId) ?? DEFAULT_HULL,
      others: match.competitors
        .filter((seat) => seat.id !== activeId)
        .map((seat) => ({
          id: seat.id,
          boat: seat.boat,
          dimensions: sizes.get(seat.id) ?? DEFAULT_HULL,
        })),
    };
  }

  /** The active seat's session feel, shared with replay reconstruction and bots. */
  private turnFeel(radius = this.effectiveMovementRadius()): TurnFeel {
    return {
      radius,
      reach: this.reach,
      ringMode: this.ringMode,
      planingSpeedKmh: this.planingSpeedKmh,
      bowOffsetPx: this.bowOffsetPx,
      crashHarshness: this.crashHarshness,
      turnTax: this.turnTax,
      windFeel: this.windFeel,
    };
  }

  /** Another seat's feel: its own hull's reach and bow, this session's knobs. */
  private seatTurnFeel(seatId: number): TurnFeel {
    const seat = this.seatHulls[seatId];

    if (!seat) {
      return this.turnFeel();
    }

    return {
      ...this.turnFeel(seat.movementRadius),
      reach: seat.reach,
      planingSpeedKmh: seat.boat.planingSpeedKmh,
      bowOffsetPx: seat.bowOffsetPx,
    };
  }

  /**
   * Everyone else's boat, race, and feel, so a bot can predict their lines and judge a block.
   * Sequential only: in a simultaneous round the other clicks are hidden. With collisions off the
   * hulls pass through each other, so there is nothing to read.
   */
  private botRivals(): RivalSeat[] | undefined {
    const match = this.match;

    if (
      !match ||
      match.movement === 'simultaneous' ||
      match.competitors.length < 2 ||
      !boatCollisionsActive()
    ) {
      return undefined;
    }

    const sizes = this.seatHullSizes();

    return match.competitors
      .filter((seat) => seat.id !== match.activeId)
      .map((seat) => ({
        id: seat.id,
        boat: seat.boat,
        race: seat.race,
        feel: this.seatTurnFeel(seat.id),
        dimensions: sizes.get(seat.id) ?? DEFAULT_HULL,
        finished: seat.race.complete,
      }));
  }

  private replayFeelFor(seatId: number) {
    const seat = this.seatHulls[seatId];

    return {
      reach: seat?.reach ?? null,
      bowOffsetPx: seat?.bowOffsetPx ?? BOW_OFFSET_PX,
      startSpeed: this.startingSpeedPx,
      planingSpeedKmh: seat?.boat.planingSpeedKmh ?? this.planingSpeedKmh,
      ringMode: this.ringMode,
    };
  }

  private relaunchData(): GameSceneData {
    return {
      map: cloneMapDocument(this.map),
      testPlay: this.testPlay,
      testOrigin: this.testOrigin ?? undefined,
      boat: this.testBoat ? cloneBoatDocument(this.testBoat) : undefined,
      boats: this.testPlay
        ? undefined
        : this.seatHulls.map((seat) => ({
            id: seat.id,
            boat: cloneBoatDocument(seat.boat),
          })),
      boatFile: this.boatFile ?? undefined,
      boatBaseline: this.boatBaseline ?? undefined,
      mapId: this.mapId ?? undefined,
      repoFile: this.repoFile ?? undefined,
      competitorCount: this.competitorCount,
      competitorNames: this.competitorNames,
      movement: this.movement,
      boatCollisions: this.boatCollisions,
      seatControllers: this.testPlay ? undefined : this.seatControllers,
    };
  }

  /**
   * Conditions are re-rolled once per turn, not once per frame: a boat committed to a move should
   * not be sailing through a wind that changes underneath it. Wind is constant today, so this
   * only re-grades the sky for the current time of day.
   */
  private applyEnvironment(): void {
    const turn = this.match?.cycle ?? this.moveSpeeds.length;
    this.rolled = rollEnvironment(this.map.environment, turn);
    this.grading?.apply(gradeFor(this.rolled));
  }

  private onGameResize(gameSize: Phaser.Structs.Size): void {
    this.cameras.resize(gameSize.width, gameSize.height);
    this.constrainCamera();
    this.refreshZoomScaledGraphics();
  }

  private worldPoint(pointer: Phaser.Input.Pointer): Vec2 {
    return { x: pointer.worldX, y: pointer.worldY };
  }

  private planingView(speedPx: number, radius = this.movementRadius) {
    return ringProjection(speedPx, this.turnFeel(radius));
  }

  /** Hover landing while aiming; otherwise the boat's current speed. */
  private ringMeter(): { ringMode: RingMode; meterLevel: number } {
    if (this.ringMode === 'off') {
      return { ringMode: 'off', meterLevel: 0 };
    }

    const speed =
      this.stage === 'racing' && this.phase === 'planning' && this.hoverPlan
        ? this.hoverPlan.boat.speed
        : this.boat.speed;

    if (this.ringMode === 'headreach') {
      return { ringMode: 'headreach', meterLevel: headreachLevel(speed) };
    }

    return { ringMode: 'planing', meterLevel: this.planingView(speed).projected.level };
  }

  private placedArea(boat: BoatState, radius = this.effectiveMovementRadius()) {
    const feel = paceFeel(boat, this.turnFeel(radius));
    const view = ringProjection(boat.speed, feel);
    const offset = this.windOffset(boat);

    return {
      view,
      offset,
      area: projectMovementArea(boat, view.diskRadius, offset, view.projected.coastPx),
      calm: projectMovementArea(
        boat,
        view.diskRadius,
        { x: 0, y: 0 },
        view.projected.coastPx,
      ),
    };
  }

  /**
   * Applies the sticky-edge remap to a raw world point. Inside the circle the point is returned
   * unchanged. In the outer halo with a slow-moving pointer the point is projected onto the rim.
   * Returns `null` when the point is outside the catchable zone (far out, or fast pointer).
   */
  private rimStick(point: Vec2): Vec2 | null {
    const zoom = this.cameras.main.zoom;
    const opts = {
      enabled: this.stickyEdges,
      overflowWorld: this.stickyOverflowPx / zoom,
    };

    const placed = this.placedArea(this.boat);

    if (placed.view.reach) {
      return stickToReach(
        point,
        placed.area.center,
        this.boat.heading,
        placed.view.reach,
        opts,
      );
    }

    return stickToMovementRim(point, placed.area, opts);
  }

  private beginPan(): void {
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.resetFX();
    this.tracking = false;
    this.cameraControl.panning = true;
    this.clearHover();

    if (this.stage === 'aiming') {
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
      this.clearMoveLineChrome();
    }
  }

  private applyPan(pointer: Phaser.Input.Pointer): void {
    this.cameraControl.applyPan(this.cameras.main, pointer.x, pointer.y);
  }

  private lockFollow(lerp: number): void {
    const cam = this.cameras.main;
    const scrollX = cam.scrollX;
    const scrollY = cam.scrollY;
    // startFollow defaults offset to 0 — that snaps look-ahead to the hull every call.
    const ox = cam.followOffset.x;
    const oy = cam.followOffset.y;

    this.tracking = true;
    cam.startFollow(this.hull, true, lerp, lerp, ox, oy);
    cam.setScroll(scrollX, scrollY);
  }

  /**
   * Ease to the next hot-seat hull with a fixed-duration focus + zoom tween.
   * Seeds followOffset so the frame does not jump, then drives the centre from the current
   * view to the new boat's look-ahead. Skips the normal followOffset/zoom catch-up while
   * active so a large gap or speed change cannot chop in one frame.
   *
   * Clears camera aim: planning "hold" would otherwise re-pin the previous seat's dest and
   * yank the view back the frame handoff ends. Outlines are not rebuilt during the glide.
   * When it finishes they rebuild only if the zoom has left the 8% band.
   */
  private beginSeatHandoffCamera(): void {
    const cam = this.cameras.main;
    const fromX = cam.midPoint.x;
    const fromY = cam.midPoint.y;
    const zoomFrom = cam.zoom;

    this.handoffTween?.stop();
    this.cameraAim = emptyCameraAim();
    this.lookAheadGate = 0;

    // New seat framing with aim cleared. Pan to the hull itself — look-ahead eases in
    // after the tween so we do not overshoot then settle "backward" toward the prior seat.
    const framing = this.playCameraTarget();
    const toX = this.hull.x;
    const toY = this.hull.y;
    const zoomTo = this.shouldAutoCamera() ? framing.appliedZoom : zoomFrom;

    this.handoffActive = true;
    this.tracking = true;
    const pointer = this.input.activePointer;
    this.handoffAimGate = { x: pointer.x, y: pointer.y };
    // startFollow resets offset to 0 unless passed here — that snapped to the new hull
    // and made the focus tween a no-op (flash, then "already there").
    const ox = this.hull.x - fromX;
    const oy = this.hull.y - fromY;
    const scrollX = cam.scrollX;
    const scrollY = cam.scrollY;
    const zoomKeep = cam.zoom;
    cam.startFollow(this.hull, false, 1, 1, ox, oy);
    // Keep the exact prior frame; startFollow's scroll math can disagree with midPoint
    // for one render when zoom ≠ 1.
    cam.setScroll(scrollX, scrollY);
    cam.setZoom(zoomKeep);
    this.writeHandoffProbe();

    const progress = { t: 0 };
    this.handoffTween = this.tweens.add({
      targets: progress,
      t: 1,
      duration: HANDOFF_DURATION_MS,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        if (!this.tracking || !this.handoffActive) {
          return;
        }

        const t = progress.t;
        const focusX = fromX + (toX - fromX) * t;
        const focusY = fromY + (toY - fromY) * t;
        cam.setFollowOffset(this.hull.x - focusX, this.hull.y - focusY);

        const nextZoom = zoomFrom + (zoomTo - zoomFrom) * t;

        if (Math.abs(cam.zoom - nextZoom) > 1e-4) {
          cam.setZoom(nextZoom);
        }

        this.writeHandoffProbe();
      },
      onComplete: () => {
        this.handoffTween = null;
        this.handoffActive = false;
        // Leave followOffset at the tween end; updatePlayCamera eases from here.
        // Do not snap to a fresh playCameraTarget — that used to yank after the glide.
        if (noteZoom(this.mapLayersZoom, cam.zoom) === 'restroke') {
          this.refreshZoomScaledGraphics();
        }
        this.writeHandoffProbe();
      },
    });
  }

  /**
   * Playwright reads `#game-container[data-passed-gates]` — the ids whose poles
   * are currently painted passed (the active seat, or the replay cursor).
   */
  private writePassedGateProbe(): void {
    const parent = this.game.canvas.parentElement;

    if (!parent) {
      return;
    }

    parent.setAttribute(
      'data-passed-gates',
      [...this.race.passedCheckpointIds].sort().join(','),
    );
    parent.setAttribute('data-gates-zoom', this.gatesPaintZoom.toFixed(4));
    parent.setAttribute(
      'data-pole-scale',
      poleWorldScale(this.gatesPaintZoom).toFixed(4),
    );
  }

  /** Playwright samples hull poses during All-at-once replay to catch a round-boundary hitch. */
  private writeReplayMotionProbe(): void {
    const parent = this.game.canvas.parentElement;

    if (!parent) {
      return;
    }

    if (this.stage !== 'replay' || this.replayPlaybackMode !== 'simultaneous') {
      parent.removeAttribute('data-replay-round');
      parent.removeAttribute('data-replay-motion');
      return;
    }

    parent.setAttribute('data-replay-round', String(this.replay?.stepIndex ?? 0));
    parent.setAttribute(
      'data-replay-motion',
      this.views
        .map((view) => {
          const speed = this.replayWorldSpeeds.get(view.id) ?? 0;
          return `${view.id}:${view.hull.x.toFixed(1)},${view.hull.y.toFixed(1)},${speed.toFixed(2)}`;
        })
        .join(';'),
    );
  }

  /** Playwright samples `#game-container[data-handoff]` / `data-cam-m*` during seat switches. */
  private writeHandoffProbe(): void {
    const parent = this.game.canvas.parentElement;

    if (!parent) {
      return;
    }

    // DOM attribute writes invalidate style every frame; only touch them on a change.
    const write = (name: string, value: string): void => {
      if (this.probeAttrs.get(name) !== value) {
        this.probeAttrs.set(name, value);
        parent.setAttribute(name, value);
      }
    };

    if (this.testPlay && (this.stage === 'placing' || this.stage === 'aiming')) {
      const cam = this.cameras.main;
      write('data-cam-mx', cam.scrollX.toFixed(1));
      write('data-cam-my', cam.scrollY.toFixed(1));
      write('data-cam-z', cam.zoom.toFixed(4));
      write('data-placement-heading', String(Math.round(this.headingDegrees(this.boat.heading))));
      return;
    }

    if (this.stage !== 'racing' && this.stage !== 'replay' && !this.handoffActive) {
      if (parent.hasAttribute('data-handoff')) {
        parent.removeAttribute('data-handoff');
        parent.removeAttribute('data-cam-mx');
        parent.removeAttribute('data-cam-my');
        this.probeAttrs.delete('data-handoff');
        this.probeAttrs.delete('data-cam-mx');
        this.probeAttrs.delete('data-cam-my');
      }
      return;
    }

    const cam = this.cameras.main;
    // midPoint lags until preRender; publish the follow focus the next frame will show.
    const focusX = this.tracking
      ? this.hull.x - cam.followOffset.x
      : cam.midPoint.x;
    const focusY = this.tracking
      ? this.hull.y - cam.followOffset.y
      : cam.midPoint.y;
    write('data-handoff', this.handoffActive ? '1' : '0');
    write('data-cam-mx', (Math.round(focusX * 10) / 10).toFixed(1));
    write('data-cam-my', (Math.round(focusY * 10) / 10).toFixed(1));
    write('data-cam-z', cam.zoom.toFixed(4));
  }

  private beginFinishCeremony(): void {
    this.stage = 'finished';
    this.raceFinishedAt = performance.now();
    this.setMovementAreaVisible(false);
    this.handoffTween?.stop();
    this.handoffTween = null;
    this.handoffActive = false;
    this.handoffAimGate = null;
    this.cameraAim = emptyCameraAim();
    this.lookAheadGate = 0;
    this.cameras.main.stopFollow();
    this.cameras.main.resetFX();
    this.tracking = false;
    this.finishCeremonyZoom = Math.min(FINISH_ZOOM, this.cameras.main.zoom * 0.7);
    this.syncCompetitorAlphas();
    this.syncHud();
    this.syncRaceHud();
    this.showRaceFinishedBanner();

    this.time.delayedCall(FINISH_CEREMONY_MS, () => {
      if (this.stage !== 'finished' || this.leaving) {
        return;
      }

      this.clearRaceFinishedBanner();
      this.finishCeremonyZoom = null;
      this.openFinishPopup();
    });
  }

  private showRaceFinishedBanner(): void {
    this.clearRaceFinishedBanner();
    this.finishBanner = this.spawnScreenBanner('Race finished', '#e8f4ff', 64);
    this.tweens.add({
      targets: this.finishBanner,
      alpha: 1,
      scale: { from: 0.92, to: 1 },
      duration: 420,
      ease: 'Sine.easeOut',
    });
  }

  private clearRaceFinishedBanner(): void {
    if (!this.finishBanner) {
      return;
    }

    this.finishBanner.destroy();
    this.finishBanner = null;
  }

  private showGoBanner(): void {
    this.goBanner?.destroy();
    const label = this.spawnScreenBanner('Go!', '#e8f4ff', GO_BANNER_SIZE_PX);
    label.setScale(1.12);
    this.goBanner = label;
    this.tweens.add({
      targets: label,
      alpha: 1,
      scale: 1,
      duration: 280,
      ease: 'Sine.easeOut',
      onComplete: () => {
        this.time.delayedCall(320, () => {
          this.tweens.add({
            targets: label,
            alpha: 0,
            scale: 0.96,
            duration: 280,
            ease: 'Sine.easeIn',
            onComplete: () => {
              label.destroy();

              if (this.goBanner === label) {
                this.goBanner = null;
              }
            },
          });
        });
      },
    });
  }

  private showGateMissedBanner(misses: readonly { id: string }[]): void {
    const owner = this.match?.activeId ?? 0;
    this.recordMissedGates(
      owner,
      misses.map((miss) => miss.id),
    );
    this.armHeaderCue('miss', owner);
    this.flashImpact('miss');
    this.syncRaceHud();
  }

  /** Full-viewport camera wash — red on hull damage, yellow on a gate miss. */
  private flashImpact(kind: 'damage' | 'miss'): void {
    const rgb = kind === 'damage' ? DAMAGE_FLASH_RGB : MISS_FLASH_RGB;
    this.cameras.main.flash(IMPACT_FLASH_MS, rgb[0], rgb[1], rgb[2], true);
    this.game.canvas.parentElement?.setAttribute('data-impact-flash', kind);
  }

  private clearImpactFlash(): void {
    this.game.canvas.parentElement?.removeAttribute('data-impact-flash');
  }

  private recordMissedGates(seatId: number, ids: readonly string[]): void {
    const existing = this.missedGateIds.get(seatId) ?? [];
    const next = [...existing];

    for (const id of ids) {
      if (!next.includes(id)) {
        next.push(id);
      }
    }

    this.missedGateIds.set(seatId, next);
  }

  private liveMissedGates(seatId: number, passed: ReadonlySet<string>): readonly string[] {
    const ids = this.missedGateIds.get(seatId) ?? [];
    const remaining = ids.filter((id) => !passed.has(id));

    if (remaining.length !== ids.length) {
      if (remaining.length === 0) {
        this.missedGateIds.delete(seatId);
      } else {
        this.missedGateIds.set(seatId, remaining);
      }
    }

    return remaining;
  }

  private headerCueLive(
    flash: { owner: number; untilMs: number } | null,
  ): boolean {
    return flash !== null && this.time.now < flash.untilMs;
  }

  private clearHeaderCues(): void {
    this.hullFlash = null;
    this.missFlash = null;
    this.hullFlashTimer?.remove(false);
    this.missFlashTimer?.remove(false);
    this.hullFlashTimer = null;
    this.missFlashTimer = null;
  }

  private armHeaderCue(kind: 'hull' | 'miss', owner: number): void {
    const untilMs = this.time.now + HEADER_CUE_MS;
    const timer = this.time.delayedCall(HEADER_CUE_MS, () => {
      const flash = kind === 'hull' ? this.hullFlash : this.missFlash;

      if (flash?.untilMs === untilMs) {
        if (kind === 'hull') {
          this.hullFlash = null;
          this.hullFlashTimer = null;
        } else {
          this.missFlash = null;
          this.missFlashTimer = null;
        }

        this.syncRaceHud();
      }
    });

    if (kind === 'hull') {
      this.hullFlash = { owner, untilMs };
      this.hullFlashTimer?.remove(false);
      this.hullFlashTimer = timer;
    } else {
      this.missFlash = { owner, untilMs };
      this.missFlashTimer?.remove(false);
      this.missFlashTimer = timer;
    }
  }

  private expireHeaderCuesOnHandoff(previousActiveId: number, nextActiveId: number): void {
    if (
      this.hullFlash &&
      previousActiveId !== this.hullFlash.owner &&
      nextActiveId === this.hullFlash.owner
    ) {
      this.hullFlash = null;
      this.hullFlashTimer?.remove(false);
      this.hullFlashTimer = null;
    }

    if (
      this.missFlash &&
      previousActiveId !== this.missFlash.owner &&
      nextActiveId === this.missFlash.owner
    ) {
      this.missFlash = null;
      this.missFlashTimer?.remove(false);
      this.missFlashTimer = null;
    }
  }

  /** Screen-fixed brand flash centred in the viewport (above HUD chrome). */
  private spawnScreenBanner(
    text: string,
    color: string,
    fontSizePx: number,
  ): Phaser.GameObjects.Text {
    const cam = this.cameras.main;
    return this.add
      .text(cam.width / 2, cam.height / 2, text, {
        fontFamily: BRAND_FONT,
        fontSize: `${fontSizePx}px`,
        color,
        stroke: '#0b1d2a',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(FX_TEXT_DEPTH)
      .setAlpha(0);
  }

  /**
   * A ring of disturbed water at the contact. It waits until the mover's eased
   * picture reaches the impact, so the ripple and the bounce show up together.
   */
  private queueCollisionSplashes(
    contacts: readonly BoatHit[],
    shoves: readonly ShovedSeat[],
  ): void {
    for (const contact of contacts) {
      const along =
        shoves.find((shove) => shove.id === contact.fromId || shove.id === contact.intoId)
          ?.alongMover ?? contact.at;
      const point = { x: contact.point.x, y: contact.point.y };
      const radius = collisionSplashRadius(contact.closingSpeed);
      const timer = this.time.delayedCall(inverseSineInOut(along) * MOVE_DURATION_MS, () => {
        this.playCollisionSplash(point, radius);
      });

      this.splashTimers.push(timer);
    }
  }

  private playCollisionSplash(at: Vec2, radius: number): void {
    const gfx = this.add.graphics().setDepth(TRAIL_DEPTH);
    this.splashRings.push(gfx);
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 720,
      ease: 'Sine.easeOut',
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 1;
        const zoom = this.cameras.main.zoom;
        gfx.clear();
        gfx.lineStyle(2.5 / zoom, WAKE_FOAM_COLOR, 0.75 * (1 - t));
        gfx.strokeCircle(at.x, at.y, radius * (0.3 + 0.7 * t));
        gfx.lineStyle(2 / zoom, WAKE_WASH_COLOR, 0.5 * (1 - t));
        gfx.strokeCircle(at.x, at.y, radius * (0.55 + 0.85 * t));
      },
      onComplete: () => {
        gfx.destroy();
        this.splashRings = this.splashRings.filter((ring) => ring !== gfx);
      },
    });
  }

  private clearSplashes(): void {
    for (const timer of this.splashTimers) {
      timer.remove(false);
    }

    this.splashTimers = [];

    for (const ring of this.splashRings) {
      ring.destroy();
    }

    this.splashRings = [];
  }

  private playCommitRipple(at: Vec2): void {
    const gfx = this.add.graphics().setDepth(AREA_DEPTH + 0.1);
    this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 420,
      ease: 'Sine.easeOut',
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 1;
        const zoom = this.cameras.main.zoom;
        gfx.clear();
        gfx.lineStyle(2 / zoom, 0xffffff, 0.65 * (1 - t));
        gfx.strokeCircle(at.x, at.y, 6 + t * 36);
      },
      onComplete: () => gfx.destroy(),
    });
  }

  /**
   * Whose turn is being shown right now: the live active seat, or during a hot-seat match
   * replay, whichever seat's move the flat replay cursor is currently on.
   */
  private currentSeatId(): number | null {
    if (this.stage === 'replay' && this.replay) {
      if (this.replayPlaybackMode === 'simultaneous') {
        const rounds = this.replayRounds;

        if (rounds.length === 0) {
          return null;
        }

        const index = Math.min(this.replay.stepIndex, rounds.length - 1);
        // The lowest seat id racing this round — the one shared set of checkpoint poles and
        // the telemetry HUD have to pick one seat even though several are animating at once.
        return rounds[index]?.[0]?.seatId ?? null;
      }

      const steps = this.replay.steps;

      if (steps.length === 0) {
        return null;
      }

      const index = Math.min(this.replay.stepIndex, steps.length - 1);
      return steps[index]?.seatId ?? null;
    }

    return this.match?.activeId ?? null;
  }

  private syncCompetitorAlphas(): void {
    if (!this.match || this.views.length <= 1) {
      return;
    }

    // While a round plays — live or in `All at once` replay — every seat racing it is
    // on-screen and moving, so none of them is dimmed. Only the single-instance visuals
    // (poles, HUD) still have to pick one representative seat.
    const replayRound =
      this.stage === 'replay' &&
      this.replayPlaybackMode === 'simultaneous' &&
      this.replay
        ? this.replayRounds[Math.min(this.replay.stepIndex, this.replayRounds.length - 1)]
        : null;
    const activeIds = this.revealing
      ? new Set(this.roundTravel.map((entry) => entry.seatId))
      : replayRound
        ? new Set(replayRound.map((step) => step.seatId))
        : null;
    const activeId = activeIds ? null : this.currentSeatId();

    for (const view of this.views) {
      const seat = this.match.competitors.find((entry) => entry.id === view.id);
      const finished = seat?.race.complete === true;
      const active =
        (activeIds ? activeIds.has(view.id) : view.id === activeId) && this.stage !== 'finished';
      const alpha =
        this.stage === 'finished' ? 1 : active ? 1 : finished ? FINISHED_HULL_ALPHA : INACTIVE_HULL_ALPHA;
      view.hull.setAlpha(alpha);
    }
  }

  private syncPassedGateBadges(): void {
    syncCheckpointBadges(
      this,
      this.checkpointLabels,
      this.map,
      screenToWorld(1, this.cameras.main.zoom),
      { base: CHECKPOINT_DEPTH, span: PROP_POLE_Y_SORT_SPAN },
      { hideIds: this.race.passedCheckpointIds },
    );
    this.writePassedGateProbe();
  }

  private recenterOnBoat(): void {
    const cam = this.cameras.main;
    const focus = this.shouldAutoCamera()
      ? this.playCameraTarget().lookAhead
      : { x: this.hull.x, y: this.hull.y };

    cam.stopFollow();
    cam.resetFX();
    this.tracking = true;
    cam.pan(
      focus.x,
      focus.y,
      RECENTER_DURATION_MS,
      'Sine.easeInOut',
      true,
      (_camera, progress) => {
        if (progress === 1 && this.tracking) {
          this.lockFollow(1);
        }
      },
    );
  }

  private recenterOnMovementArea(): void {
    const area = this.placedArea(this.boat).area;
    const cam = this.cameras.main;

    cam.stopFollow();
    cam.resetFX();
    cam.pan(
      area.center.x,
      area.center.y,
      RECENTER_DURATION_MS,
      'Sine.easeInOut',
    );
  }

  private pinchPointers(): [Phaser.Input.Pointer, Phaser.Input.Pointer] | null {
    return this.cameraControl.pinchPointers(this.input.manager.pointers);
  }

  private beginPinch(): boolean {
    if (
      !this.cameraControl.beginPinch(
        this.cameras.main,
        this.input.manager.pointers,
        () => {
          if (this.stage === 'aiming') {
            this.cancelPlant();
          }
        },
      )
    ) {
      return false;
    }

    this.clearHover();

    return true;
  }

  private applyPinch(): void {
    const pinch = this.cameraControl.applyPinch(this.input.manager.pointers);

    if (!pinch) {
      return;
    }

    if (this.fleetFraming()) {
      const auto = this.fleetCameraTarget()?.autoZoom ?? pinch.zoom;
      this.userZoomFactor = userFactorForZoom(auto, pinch.zoom);
      this.autoZoom = auto;
    } else if (this.shouldAutoCamera()) {
      const auto = this.playCameraTarget().autoZoom;
      this.userZoomFactor = userFactorForZoom(auto, pinch.zoom);
      this.autoZoom = auto;
    }

    this.applyUserZoom(pinch.zoom, pinch.center.x, pinch.center.y);
  }

  private releasePinchPointers(): void {
    this.cameraControl.releasePinch(this.input.manager.pointers);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.cameraControl.pinching || this.beginPinch()) {
      this.applyPinch();
      return;
    }

    if (this.cameraControl.pinchConsumed || this.pinchPointers()) {
      return;
    }

    if (this.panPointerId !== null) {
      return;
    }

    if (this.stage === 'aiming') {
      if (this.placingSeatIsBot()) {
        return;
      }

      if (this.boatHeld) {
        this.hull.setRotation(this.boat.heading);
        this.syncHullGhost();
        this.drawAimLine(this.boat.heading);
        return;
      }

      if (this.headingFromWheel && this.wheelAimScreen) {
        const dx = pointer.x - this.wheelAimScreen.x;
        const dy = pointer.y - this.wheelAimScreen.y;

        if (dx * dx + dy * dy < PAN_DRAG_THRESHOLD_PX * PAN_DRAG_THRESHOLD_PX) {
          this.hull.setRotation(this.boat.heading);
          this.syncHullGhost();
          this.drawAimLine(this.boat.heading);
          return;
        }

        this.headingFromWheel = false;
      }

      this.previewAim(this.worldPoint(pointer));
      return;
    }

    if (pointer.leftButtonDown()) {
      if (!this.cameraControl.panning && pointer.getDistance() > PAN_DRAG_THRESHOLD_PX) {
        this.beginPan();
      }

      if (this.cameraControl.panning) {
        this.applyPan(pointer);
        return;
      }
    }

    if (this.stage === 'placing') {
      this.refreshPlacementFromPointer();
      return;
    }

    // Racing dest is applied in refreshHoverFromPointer after the camera is final.
    // This event's worldX is pre-look-ahead; using it at the rim clears hover
    // and flickers the prediction.
  }

  /**
   * Dest is a world-space aim, applied only here after the camera is final. A parked
   * pointer must not be re-projected. Past the catch zone (disk, plus sticky halo
   * when that toggle is on) dest clears.
   */
  private refreshHoverFromPointer(): void {
    if (
      this.cameraControl.panning ||
      this.stage !== 'racing' ||
      this.phase !== 'planning' ||
      this.activeSeatIsAutopilot()
    ) {
      return;
    }

    const pointer = this.input.activePointer;
    const screen = { x: pointer.x, y: pointer.y };

    if (!pointerScreenMoved(this.hoverPointerScreen, screen)) {
      return;
    }

    this.hoverPointerScreen = screen;

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    const dest = this.rimStick({ x: world.x, y: world.y });

    if (dest === null) {
      if (this.hoverPoint && this.pointerOverChrome(pointer)) {
        return;
      }

      if (this.hoverPoint) {
        this.clearHover();
      }

      return;
    }

    if (
      this.hoverPoint &&
      this.hoverPoint.x === dest.x &&
      this.hoverPoint.y === dest.y
    ) {
      return;
    }

    this.setHover(dest);
  }

  private pointerOverChrome(pointer: Phaser.Input.Pointer): boolean {
    const rect = this.game.canvas.getBoundingClientRect();
    const clientX = rect.left + pointer.x;
    const clientY = rect.top + pointer.y;

    return (
      document.elementFromPoint(clientX, clientY)?.closest('.ui-chrome') != null
    );
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.pinchPointers()) {
      if (this.beginPinch()) {
        this.applyPinch();
      }

      return;
    }

    if (this.cameraControl.pinching || this.cameraControl.pinchConsumed) {
      return;
    }

    if (this.panPointerId !== null) {
      return;
    }

    if (!pointer.leftButtonDown()) {
      return;
    }

    this.pointerDownStage = this.stage;

    if (this.stage === 'aiming') {
      if (this.placingSeatIsBot()) {
        return;
      }

      const point = this.worldPoint(pointer);

      if (this.headingToward(point) !== null) {
        this.commitPlant(point);
      }

      return;
    }

    this.cameraControl.armPan(this.cameras.main, pointer.x, pointer.y);

    // Test drive plants on pointer-up so a drag can pan to another sector first.
    // A catalog start still plants on the press.
    if (this.stage === 'placing' && !this.testPlay && !this.placingSeatIsBot()) {
      const point = this.worldPoint(pointer);

      if (this.canPlant(point)) {
        this.plantBoat(point);
      }
    }
  }

  private onPointerUp(
    pointer: Phaser.Input.Pointer,
    currentlyOver: Phaser.GameObjects.GameObject[] = [],
  ): void {
    const wasPinching = this.cameraControl.pinching || this.cameraControl.pinchConsumed;
    this.releasePinchPointers();

    if (wasPinching) {
      this.pointerDownStage = null;
      return;
    }

    const wasPanning = this.cameraControl.panning;
    this.cameraControl.endPan();
    const downStage = this.pointerDownStage;
    this.pointerDownStage = null;

    if (wasPanning || pointer.getDistance() > PAN_DRAG_THRESHOLD_PX) {
      return;
    }

    const point = this.worldPoint(pointer);

    if (this.testPlay && downStage === 'placing' && this.stage === 'placing') {
      if (!this.pointerOverChrome(pointer) && this.canPlant(point)) {
        this.plantBoat(point);
      }

      return;
    }

    if (this.stage === 'placing' || this.stage === 'aiming') {
      return;
    }

    // Last heading lock is pointerdown → beginRace; that same up is not a plan.
    if (this.stage !== 'racing' || downStage !== 'racing') {
      return;
    }

    if (currentlyOver.includes(this.hull)) {
      this.recenterOnBoat();
      return;
    }

    if (this.activeSeatIsAutopilot()) {
      return;
    }

    this.commitDestination(this.hoverPoint ?? this.rimStick(point) ?? point);
  }

  /**
   * Commits the active seat's click, from the pointer or a bot. Everything after the rules
   * (replay moves, wake, camera, HUD, travel) is the same for both. False when the rules refuse it.
   */
  private commitDestination(destination: Vec2): boolean {
    if (!this.match) {
      return false;
    }

    // The slide still owns this hull. A click now would tween a second path over it, and the
    // slide's landing would then rewind the sprite while the sim kept the click.
    if (this.driftingSeats.has(this.match.activeId)) {
      return false;
    }

    const previousBoat = this.boat;
    const previousRace = this.race;
    const committed = commitActiveWithFeel(
      this.match,
      destination,
      this.map,
      this.turnFeel(),
      this.seatHullSizes(),
    );

    if (!committed) {
      return false;
    }

    this.botToken += 1;
    this.match = committed.match;
    const result = committed.commit;
    const active = activeCompetitor(this.match);
    const tookDamage = result.reports.some((report) => report.damaged);
    // Simultaneous keeps the move secret until the round plays, so the cue waits too.
    const lockingIn = this.match.movement === 'simultaneous';
    // A medium or hard hit this boat caused slows it like a shore crash, so it washes red too.
    const paidForHit =
      !lockingIn &&
      result.boatContacts.some(
        (hit) =>
          hit.severity !== 'nudge' &&
          (hit.faultId === this.match!.activeId || hit.faultId === null),
      );

    if (!lockingIn) {
      if (tookDamage) {
        this.armHeaderCue('hull', this.match.activeId);
      }

      this.logLines = appendContactLog(this.logLines, result.reports, {
        seatId: this.match.activeId,
        label: this.seatLabel(this.match.activeId),
      });
      this.logBoatContacts(result.boatContacts, this.match.activeId);
      this.driftShovedSeats(committed.shoves);
      this.queueCollisionSplashes(result.boatContacts, committed.shoves);
    }

    const path = result.clipped
      ? result.path
      : (projectMovePath(previousBoat, destination, this.curveKind, this.turnTax) ??
        result.path);
    const angleDeg = turnAngleDeg(
      previousBoat,
      result.clipped ? result.boat.position : destination,
    );

    const view = this.views.find((entry) => entry.id === this.match!.activeId);

    // Park moves are live theatre only — official time and Top Lists replay stop at the goal.
    if (!previousRace.complete) {
      this.replayMoves.push({
        destination: { x: destination.x, y: destination.y },
        radius: this.effectiveMovementRadius(),
      });

      if (angleDeg !== null) {
        this.turnAnglesDeg.push(angleDeg);
      }

      if (view) {
        view.replayMoves = this.replayMoves;
        view.turnAnglesDeg = this.turnAnglesDeg;
        view.replayStart = this.replayStart;
      }
    }

    if (lockingIn) {
      // The landed pose stays in `lockIn`. Adopting `result.boat` here would slide the hull
      // the next player is still looking at. `revealRound` is the first thing that moves it.
      this.setMovementAreaVisible(false);
      this.clearHover();
      this.playCommitRipple(destination);
      this.lockInSeatMove({
        seatId: active.id,
        path,
        fromRace: previousRace,
        committed: active.resolvingRace ?? previousRace,
        fromSpeed: previousBoat.speed,
        damaged: tookDamage,
      });
      this.syncHud();
      this.syncRaceHud();
      return true;
    }

    this.boat = result.boat;
    this.phase = result.phase;
    this.moveSpeeds = [...active.moveSpeeds];
    this.moveFromSpeed = previousBoat.speed;
    this.lastCrashPlan = result.clipped
      ? {
          boat: {
            position: { ...previousBoat.position },
            heading: previousBoat.heading,
            speed: previousBoat.speed,
          },
          dest: { x: destination.x, y: destination.y },
          radius: this.effectiveMovementRadius(),
        }
      : null;
    this.pendingFinish =
      previousRace.complete === false && (active.resolvingRace?.complete ?? false);
    this.lastFinishProgress = active.finishProgress ?? this.lastFinishProgress;

    if (path) {
      this.activeTravel = {
        fromRace: previousRace,
        visualPath: path,
        committed: active.resolvingRace ?? previousRace,
      };
      this.trail = pushTrail(this.trail, path, this.trailHistoryLimit());
      this.route = [...this.route, path];

      if (view) {
        view.trail = this.trail;
        view.route = this.route;
      }

      this.trailProgress = 0;
      this.drawTrail();
      this.drawRoute();
    } else {
      this.race = active.resolvingRace ?? previousRace;
      this.drawMapLayers();
    }

    this.syncHud();
    this.syncRaceHud();
    this.setMovementAreaVisible(false);
    this.clearHover();
    this.playCommitRipple(destination);

    if (!this.tracking) {
      this.recenterOnMovementArea();
    }

    // After recenter: that path calls resetFX and would cancel a flash started at commit.
    if (tookDamage || paidForHit) {
      this.flashImpact('damage');
    }

    if (!path) {
      this.releaseShoves();
      this.finishMove();
      return true;
    }

    this.travel(path);
    return true;
  }

  private beginPlacementCursor(centroid: Vec2): void {
    this.hull.setVisible(true);
    this.syncHullDisplaySize();
    const pointer = this.input.activePointer;
    const overCanvas =
      pointer.x >= 0 &&
      pointer.y >= 0 &&
      pointer.x <= this.cameras.main.width &&
      pointer.y <= this.cameras.main.height;

    if (overCanvas) {
      const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      this.previewPlacement({ x: world.x, y: world.y });
      this.syncStartAreaHover({ x: world.x, y: world.y });
    } else {
      this.previewPlacement(centroid);
      this.syncStartAreaHover(centroid);
    }

    this.syncPlacementCursorVisibility();
  }

  private previewPlacement(point: Vec2): void {
    this.hull.setPosition(point.x, point.y);
    this.hull.setRotation(this.boat.heading);
    this.hull.setAlpha(this.canPlant(point) ? 1 : PLACEMENT_ILLEGAL_ALPHA);
    this.syncHullGhost();
  }

  /** Copy hull pose; show only when the real boat is on legal water under a north cap. */
  private syncHullGhost(): void {
    this.hullGhost.setPosition(this.hull.x, this.hull.y);
    this.hullGhost.setRotation(this.hull.rotation);
    this.hullGhost.setVisible(
      this.hull.visible &&
        pointUnderColumnCap(this.map, { x: this.hull.x, y: this.hull.y }),
    );
  }

  /** Same as `syncHullGhost`, for a seat that is not `this.hull` right now — parked replay hulls. */
  private syncViewGhost(view: CompetitorView): void {
    view.hullGhost.setPosition(view.hull.x, view.hull.y);
    view.hullGhost.setRotation(view.hull.rotation);
    view.hullGhost.setVisible(
      view.hull.visible &&
        pointUnderColumnCap(this.map, { x: view.hull.x, y: view.hull.y }),
    );
  }

  /** One play-log line per boat this move touched, written from the mover's side. */
  private logBoatContacts(contacts: readonly BoatHit[], activeId: number): void {
    if (contacts.length === 0) {
      return;
    }

    const label = this.seatLabel(activeId);
    this.logLines = appendPlayLogLines(
      this.logLines,
      contacts.map((contact) => {
        const struck = contact.intoId === activeId;

        return {
          seatId: activeId,
          label,
          text: formatBoatHit(
            contact,
            this.seatLabel(struck ? contact.fromId : contact.intoId),
            struck,
          ),
        };
      }),
    );
  }

  /**
   * Boats this move pushed stay where they are until the mover's picture reaches
   * the impact, then ride their drift on that same tween. The physics was spent
   * at the click; the slide waits until the hit is actually on screen.
   */
  private driftShovedSeats(shoves: readonly ShovedSeat[]): void {
    this.impactAlong =
      shoves.length === 0
        ? null
        : Math.min(...shoves.map((shove) => shove.alongMover));

    for (const shove of shoves) {
      const view = this.views.find((entry) => entry.id === shove.id);

      if (!view) {
        continue;
      }

      const running = this.shoveAnims.find((anim) => anim.view === view);

      if (running) {
        running.abandoned = true;
        const tween = running.tween;
        running.tween = null;
        tween?.stop();
        this.driftingSeats.delete(view.id);
      }

      this.shoveAnims = this.shoveAnims.filter((anim) => anim.view !== view);
      this.shoveAnims.push({
        view,
        along: shove.alongMover,
        shove,
        tween: null,
        abandoned: false,
      });
    }
  }

  /**
   * A shoved hull stays where it is until the impact is on screen, then slides
   * from that live pose to the landing. The slide has its own duration, so a
   * hit at the end of the move still reads as a tween rather than a pop.
   */
  private syncShoves(moverProgress: number): void {
    for (const anim of this.shoveAnims) {
      if (anim.tween || driftProgress(moverProgress, anim.along) <= 0) {
        continue;
      }

      const remaining = (1 - Math.min(1, Math.max(0, moverProgress))) * MOVE_DURATION_MS;
      this.startShoveTween(anim, Math.max(SHOVE_TWEEN_MS, remaining));
    }
  }

  private startShoveTween(anim: ShoveAnim, duration: number): void {
    const { view, shove } = anim;
    const from = { x: view.hull.x, y: view.hull.y };
    const fromHeading = view.hull.rotation;
    const to = shove.boat.position;
    // The drift already went through the map. A straight chord from here to the landing
    // cuts through the rock that bounce steered around, which is the hull jumping land.
    const drift =
      shove.path && shove.path.segments.length > 0 ? shove.path : null;
    const curve = drift ? toCurve(drift) : null;
    const line = drift ?? pathFromPolyline([from, to], 'straight');

    if (line) {
      view.trail = pushTrail(view.trail, line, this.trailHistoryLimit());
    }

    view.trailProgress = 0;
    this.driftingSeats.add(view.id);

    let settled = false;
    const land = (): void => {
      if (settled || anim.abandoned) {
        return;
      }

      settled = true;
      anim.tween = null;
      this.driftingSeats.delete(view.id);
      this.settleShovedSeat(view, shove);
      this.drawTrail();
    };

    anim.tween = this.tweens.addCounter({
      from: 0,
      to: 1,
      duration,
      ease: 'Sine.easeOut',
      onUpdate: (tween) => {
        if (anim.abandoned) {
          return;
        }

        const step = tween.getValue() ?? 1;
        const point = curve?.getPoint(step);

        if (point) {
          view.hull.setPosition(point.x, point.y);
        } else {
          view.hull.setPosition(
            from.x + (to.x - from.x) * step,
            from.y + (to.y - from.y) * step,
          );
        }

        view.hull.setRotation(
          fromHeading + angleDelta(fromHeading, shove.boat.heading) * step,
        );
        view.trailProgress = step;
        this.syncViewGhost(view);
        this.drawTrail();
      },
      onComplete: land,
    });
  }

  /**
   * Drop a slide that must not write this hull again. Used when a newer picture — the
   * boat's own move — takes the sprite. The landing is not applied; that pose is stale.
   */
  private abandonShove(view: CompetitorView): void {
    const anim = this.shoveAnims.find((entry) => entry.view === view);

    if (!anim) {
      return;
    }

    anim.abandoned = true;
    const tween = anim.tween;
    anim.tween = null;
    tween?.stop();
    this.driftingSeats.delete(view.id);
    this.shoveAnims = this.shoveAnims.filter((entry) => entry !== anim);
  }

  /** Start any shove the mover's tween never reached, without cutting a slide short. */
  private releaseShoves(): void {
    for (const anim of this.shoveAnims) {
      if (!anim.tween) {
        this.startShoveTween(anim, SHOVE_TWEEN_MS);
      }
    }

    this.shoveAnims = this.shoveAnims.filter((anim) => anim.tween);
  }

  /** Drop every shove onto its landing. Used when the move is abandoned. */
  private finishShoves(): void {
    const anims = this.shoveAnims;
    this.shoveAnims = [];

    for (const anim of anims) {
      anim.abandoned = true;
      const tween = anim.tween;
      anim.tween = null;
      tween?.stop();
      this.driftingSeats.delete(anim.view.id);
      this.settleShovedSeat(anim.view, anim.shove);
    }
  }

  private settleShovedSeat(view: CompetitorView, shove: ShovedSeat): void {
    const seat = this.match?.competitors.find((entry) => entry.id === view.id);
    const movedOn =
      seat !== undefined &&
      lengthSq(sub(seat.boat.position, shove.boat.position)) > 4;
    const boat = movedOn && seat ? seat.boat : shove.boat;

    view.hull.setPosition(boat.position.x, boat.position.y);
    view.hull.setRotation(boat.heading);
    view.trailProgress = 1;
    this.syncViewGhost(view);

    if (
      this.match?.activeId === view.id &&
      this.stage === 'racing' &&
      this.phase === 'planning' &&
      !this.moveTween
    ) {
      this.drawMovementArea();
      this.setMovementAreaVisible(true);
    }
  }

  private clearCompetitorViews(): void {
    for (const view of this.views) {
      view.hull.destroy();
      view.hullGhost.destroy();
    }

    this.views = [];
  }

  private spawnHullPair(
    position: Vec2,
    heading: number,
    id: number,
    displayW = this.metricHull().displayW,
    displayH = this.metricHull().displayH,
  ): { hull: Phaser.GameObjects.Image; hullGhost: Phaser.GameObjects.Image } {
    const texture = this.hullTexture(id);
    const hull = this.add
      .image(position.x, position.y, texture)
      .setDisplaySize(displayW, displayH)
      .setOrigin(0.5)
      .setRotation(heading)
      .setDepth(HULL_DEPTH);
    const hullGhost = this.add
      .image(position.x, position.y, texture)
      .setDisplaySize(displayW, displayH)
      .setOrigin(0.5)
      .setRotation(heading)
      .setDepth(HULL_GHOST_DEPTH)
      .setAlpha(HULL_GHOST_ALPHA)
      .setVisible(false);
    this.applySeatTint(hull, hullGhost, id);

    return { hull, hullGhost };
  }

  /**
   * Placement cursor floors at `HULL_MIN_SCREEN_PX` so wheel zoom-out stays readable.
   * Racing / replay keep the metric 7 m footprint.
   */
  private syncHullDisplaySize(): void {
    const size =
      this.stage === 'placing' || this.stage === 'aiming'
        ? hullDisplaySize(this.cameras.main.zoom, this.hullLengthM, this.hullBeamM)
        : this.metricHull();
    this.applyHullDisplaySize(this.hull, this.hullGhost, size);
  }

  private applyHullDisplaySize(
    hull: Phaser.GameObjects.Image,
    hullGhost: Phaser.GameObjects.Image,
    size: { displayW: number; displayH: number },
  ): void {
    hull.setDisplaySize(size.displayW, size.displayH);
    hullGhost.setDisplaySize(size.displayW, size.displayH);
  }

  private syncMatchDataAttrs(): void {
    const root = this.game.canvas.parentElement;

    if (!root) {
      return;
    }

    root.setAttribute('data-competitors', String(this.competitorCount));
    root.setAttribute(
      'data-active-id',
      this.match ? String(this.match.activeId) : String(this.placements.length),
    );
    root.setAttribute('data-movement', this.movement);

    if (this.competitorCount > 1) {
      root.setAttribute(
        'data-boat-collisions',
        this.boatCollisions ? 'on' : 'off',
      );
    } else {
      root.removeAttribute('data-boat-collisions');
    }

    if (this.movement === 'simultaneous' && this.match) {
      root.setAttribute('data-round-phase', this.revealing ? 'reveal' : 'lockIn');
      root.setAttribute(
        'data-locked-seats',
        this.match.competitors
          .filter((seat) => lockedIn(seat))
          .map((seat) => seat.id)
          .join(','),
      );
    } else {
      root.removeAttribute('data-round-phase');
      root.removeAttribute('data-locked-seats');
    }
  }

  private syncActiveFromMatch(): void {
    if (!this.match) {
      return;
    }

    const active = activeCompetitor(this.match);
    this.boat = active.boat;
    this.phase = active.phase;
    this.race = active.race;
    this.moveSpeeds = [...active.moveSpeeds];
    this.lastFinishProgress = active.finishProgress ?? 1;
    this.bindActiveView(active.id);
    this.syncMatchDataAttrs();
    // Poles, waterline, and order badges follow the seat whose turn it is — not
    // a shared "anyone has crossed this" set left over from the previous tween.
    this.redrawCheckpoints();
  }

  private bindActiveView(id: number): void {
    this.installSeat(id);
    const view = this.views.find((entry) => entry.id === id);

    if (!view) {
      return;
    }

    this.hull = view.hull;
    this.hullGhost = view.hullGhost;
    this.trail = view.trail;
    this.route = view.route;
    this.turnAnglesDeg = view.turnAnglesDeg;
    this.replayStart = view.replayStart;
    this.replayMoves = view.replayMoves;
    this.hull.setInteractive();
    this.syncHullGhost();
  }

  private parkCurrentPlacement(heading: number): void {
    const id = this.placements.length;
    this.boat = { ...this.boat, heading };
    this.hull.setRotation(heading).setAlpha(1).setVisible(true);
    this.hull.disableInteractive();
    this.applyHullDisplaySize(this.hull, this.hullGhost, this.metricHull());
    this.syncHullGhost();
    this.placements.push({
      position: { x: this.boat.position.x, y: this.boat.position.y },
      heading,
      speed: this.boat.speed,
    });
    this.views.push({
      id,
      hull: this.hull,
      hullGhost: this.hullGhost,
      route: [],
      trail: [],
      trailProgress: 1,
      turnAnglesDeg: [],
      replayStart: {
        position: { x: this.boat.position.x, y: this.boat.position.y },
        heading,
      },
      replayMoves: [],
    });
  }

  private beginNextPlacementCursor(): void {
    const nextId = this.placements.length;
    this.installSeat(nextId);
    const pair = this.spawnHullPair(this.boat.position, this.boat.heading, nextId);
    this.hull = pair.hull.setVisible(true);
    this.hullGhost = pair.hullGhost;
    this.boat = createBoatState();
    this.stage = 'placing';
    this.setPlacementCursorHidden(false);
    this.syncHullDisplaySize();
    this.refreshPlacementFromPointer();
    this.syncMatchDataAttrs();
    this.syncRaceHud();
    this.placeBotSeat();
  }

  private startMatchFromPlacements(): void {
    if (!this.views.some((view) => view.hull === this.hull)) {
      this.hull.destroy();
      this.hullGhost.destroy();
    }

    this.match = createMatch(this.placements, this.movement);

    while (this.views.length < this.match.competitors.length) {
      const seat = this.match.competitors[this.views.length]!;
      this.installSeat(seat.id);
      const pair = this.spawnHullPair(seat.boat.position, seat.boat.heading, seat.id);
      pair.hull.setVisible(true).setInteractive();
      this.views.push({
        id: seat.id,
        hull: pair.hull,
        hullGhost: pair.hullGhost,
        route: [],
        trail: [],
        trailProgress: 1,
        turnAnglesDeg: [],
        replayStart: {
          position: { ...seat.boat.position },
          heading: seat.boat.heading,
        },
        replayMoves: [],
      });
    }

    for (const seat of this.match.competitors) {
      const view = this.views.find((entry) => entry.id === seat.id);

      if (!view) {
        continue;
      }

      view.hull
        .setPosition(seat.boat.position.x, seat.boat.position.y)
        .setRotation(seat.boat.heading)
        .setAlpha(1)
        .setVisible(true);
      view.hullGhost.setPosition(seat.boat.position.x, seat.boat.position.y);
      view.hullGhost.setRotation(seat.boat.heading);
    }

    this.syncActiveFromMatch();
    this.beginRace();
  }

  private autoPlaceAll(centroid: Vec2): void {
    const count = this.competitorCount;
    const gap = metersToPixels(8);
    this.placements = [];

    for (let id = 0; id < count; id += 1) {
      const offset = (id - (count - 1) / 2) * gap;
      const position = { x: centroid.x + offset, y: centroid.y };
      this.placements.push({ position, heading: this.boat.heading });
    }

    this.startMatchFromPlacements();
    const cam = this.cameras.main;
    cam.centerOn(centroid.x, centroid.y);
    this.lockFollow(1);
    this.snapAutoZoom();
  }

  private seatLabel(seatId: number): string {
    return this.competitorNames[seatId] ?? competitorLabel(seatId);
  }

  /**
   * 1-based round this seat is on: next turn, or the in-flight / finished turn.
   * Replay standings use `replaySeatSnapshot` instead of this helper.
   */
  private seatRound(seatId: number): number {
    if (this.replay?.score) {
      const turns = this.replayDomainLength();
      return Math.min(this.replay.stepIndex + 1, Math.max(1, turns));
    }

    const seat = this.match?.competitors.find((entry) => entry.id === seatId);
    const done = seat
      ? seat.id === this.match!.activeId
        ? this.moveSpeeds.length
        : seat.moveSpeeds.length
      : this.moveSpeeds.length;
    const finished =
      this.stage === 'finished' ||
      (seat !== undefined && (seat.race.complete || seat.finishProgress !== null)) ||
      (this.pendingFinish && (!this.match || this.match.activeId === seatId));

    if (finished) {
      return Math.max(1, done);
    }

    const inFlight =
      (seat !== undefined ? seat.phase === 'resolving' : this.phase === 'resolving') ||
      ((!this.match || this.match.activeId === seatId) && this.activeTravel !== null);

    return inFlight ? Math.max(1, done) : done + 1;
  }

  private seatBoatTimeMs(seatId: number): number {
    if (!this.match) {
      return this.boatTimeMs();
    }

    const seat = this.match.competitors.find((entry) => entry.id === seatId);

    if (!seat) {
      return 0;
    }

    const pathCount = seat.moveSpeeds.length;
    const finish = seat.finishProgress ?? 1;
    const isActive =
      seatId === this.match.activeId &&
      this.stage !== 'finished' &&
      this.activeTravel !== null;
    const cursor = isActive
      ? pathCount - 1 + this.trailProgress
      : pathCount;
    const capped =
      seat.finishProgress !== null || (this.pendingFinish && seatId === this.match.activeId)
        ? Math.min(cursor, pathCount - 1 + finish)
        : cursor;

    return boatClockFromCursor(capped, pathCount, finish);
  }

  private refreshPlacementFromPointer(): void {
    if (this.stage !== 'placing' || this.cameraControl.panning || this.placingSeatIsBot()) {
      return;
    }

    const pointer = this.input.activePointer;
    this.syncPlacementCursorVisibility();

    if (this.pointerOverChrome(pointer)) {
      return;
    }

    const world = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
    this.previewPlacement({ x: world.x, y: world.y });
    this.syncStartAreaHover({ x: world.x, y: world.y });
  }

  /** The placement hint follows the start area on screen; sync the HUD only when it moved. */
  private refreshPlacementHintAnchor(): void {
    if (this.stage !== 'placing' && this.stage !== 'aiming') {
      this.placementHintAnchorKey = '';
      return;
    }

    const anchor = this.placementHintAnchor();
    const key = anchor ? `${anchor.x},${anchor.y}` : 'none';

    if (key === this.placementHintAnchorKey) {
      return;
    }

    this.placementHintAnchorKey = key;
    this.syncRaceHud();
  }

  /**
   * Open water for a test drive; the start polygon for a catalog race. Either way the hull keeps
   * `PLACEMENT_GAP_M` of water to every boat already planted (beam only here; the aim checks the
   * whole hull).
   */
  private canPlant(point: Vec2): boolean {
    const legal = this.testPlay
      ? isLegalTestPlacement(this.map, point)
      : isLegalStartPlacement(this.map, point);

    return legal && placementClear(this.placedHulls(), point, null, this.placingHull());
  }

  /** The planted boat may point this way without its hull crowding another planted boat. */
  private canAim(heading: number): boolean {
    return placementClear(this.placedHulls(), this.boat.position, heading, this.placingHull());
  }

  /** Boats already planted this race, as hulls, for the placement gap. */
  private placedHulls(): PlacedHull[] {
    const sizes = this.seatHullSizes();

    return this.placements.map((placement, id) => ({
      position: placement.position,
      heading: placement.heading,
      dimensions: sizes.get(id) ?? DEFAULT_HULL,
    }));
  }

  private placingHull(): HullDimensions {
    return this.seatHullSizes().get(this.placements.length) ?? DEFAULT_HULL;
  }

  private placementHintAnchor(): { x: number; y: number } | null {
    const startArea = this.map.startArea;

    if (
      this.testPlay ||
      !startArea ||
      (this.stage !== 'placing' && this.stage !== 'aiming')
    ) {
      return null;
    }

    const bounds = polygonBounds(tessellateRing(startArea));

    if (!bounds) {
      return null;
    }

    const cam = this.cameras.main;
    const view = cameraWorldRect(
      cam.scrollX,
      cam.scrollY,
      cam.width,
      cam.height,
      cam.zoom,
    );
    const uv = worldToViewUv(
      (bounds.minX + bounds.maxX) / 2,
      bounds.minY,
      view,
    );

    return {
      x: Math.round(clamp(uv.x * cam.width, 96, cam.width - 96)),
      y: Math.round(clamp(uv.y * cam.height, 48, cam.height - 8)),
    };
  }

  /** Right-drag and hold-H pan only while a test drive is still placing or aiming. */
  private testPanActive(): boolean {
    return this.testPlay && (this.stage === 'placing' || this.stage === 'aiming');
  }

  /** Press P to cycle Off, Planing, and Headreach. Ignored while a field is focused. */
  private readonly onPlaningKeyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'KeyP' || event.repeat || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (isTypingInField() || isFloatingPanelOpen()) {
      return;
    }

    event.preventDefault();
    this.setRingMode(nextRingMode(this.ringMode));
  };

  private setRingMode(next: RingMode): void {
    if (next === this.ringMode) {
      return;
    }

    this.ringMode = next;
    this.drawMovementArea();
    this.refreshActiveHover();
    this.syncHud();
  }

  private unbindTestPan(): void {
    window.removeEventListener('keydown', this.onTestPanKeyDown, { capture: true });
    window.removeEventListener('keyup', this.onTestPanKeyUp, { capture: true });
    window.removeEventListener('blur', this.onTestPanBlur);
    window.removeEventListener('pointerdown', this.onTestPanPointerDown, { capture: true });
    window.removeEventListener('pointermove', this.onTestPanPointerMove, { capture: true });
    window.removeEventListener('pointerup', this.onTestPanPointerUp, { capture: true });
    window.removeEventListener('pointercancel', this.onTestPanPointerUp, { capture: true });
    this.handDown = false;
    this.boatHeld = false;
    this.headingFromWheel = false;
    this.wheelAimScreen = null;
    this.panPointerId = null;
    this.cameraControl.endPan();
  }

  private readonly onTestPanBlur = (): void => {
    this.handDown = false;
    this.boatHeld = false;
    this.syncPlacementCursorVisibility();
  };

  private readonly onTestPanKeyDown = (event: KeyboardEvent): void => {
    if (event.code === 'KeyB') {
      this.onTestBoatKeyDown(event);
      return;
    }

    if (event.code !== 'KeyH' || !this.testPanActive()) {
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      isTypingInField() ||
      isFloatingPanelOpen()
    ) {
      return;
    }

    event.preventDefault();

    if (event.repeat) {
      return;
    }

    this.handDown = true;
    this.syncPlacementCursorVisibility();
  };

  private readonly onTestPanKeyUp = (event: KeyboardEvent): void => {
    if (event.code === 'KeyB') {
      this.boatHeld = false;
      return;
    }

    if (event.code !== 'KeyH') {
      return;
    }

    this.handDown = false;
    this.syncPlacementCursorVisibility();
  };

  /** Hold B turns the hull with the wheel. A field, a modifier, or an open float keeps the key. */
  private onTestBoatKeyDown(event: KeyboardEvent): void {
    if (!this.testPanActive()) {
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      isTypingInField() ||
      isFloatingPanelOpen()
    ) {
      return;
    }

    event.preventDefault();

    if (event.repeat || this.boatHeld) {
      return;
    }

    this.boatHeld = true;
  }

  private canvasPointFromClient(clientX: number, clientY: number): { x: number; y: number } {
    const rect = this.game.canvas.getBoundingClientRect();
    const width = rect.width || 1;
    const height = rect.height || 1;

    return {
      x: ((clientX - rect.left) / width) * this.scale.width,
      y: ((clientY - rect.top) / height) * this.scale.height,
    };
  }

  /**
   * Right-button and hold-H drags are native, same as the editor. Phaser still
   * sees the left press and would plant or lock a heading.
   */
  private readonly onTestPanPointerDown = (event: PointerEvent): void => {
    if (!this.sys.isActive() || !this.testPanActive() || this.panPointerId !== null) {
      return;
    }

    const handLeft = event.button === 0 && this.handDown && !isTypingInField();
    const right = event.button === 2;

    if (!handLeft && !right) {
      return;
    }

    if (event.target instanceof Element && event.target.closest('.ui-chrome, [role="dialog"]')) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    const cam = this.cameras.main;
    cam.stopFollow();
    cam.resetFX();
    this.tracking = false;
    this.cameraControl.beginPan(cam, point.x, point.y);
    this.clearHover();

    if (this.stage === 'aiming') {
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
      this.clearMoveLineChrome();
    }

    this.panPointerId = event.pointerId;
    this.syncPlacementCursorVisibility();

    try {
      this.game.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Some synthetic clicks have no capture target; move and up still reach window.
    }
  };

  private readonly onTestPanPointerMove = (event: PointerEvent): void => {
    if (this.panPointerId === null || event.pointerId !== this.panPointerId) {
      return;
    }

    event.preventDefault();
    const point = this.canvasPointFromClient(event.clientX, event.clientY);
    this.cameraControl.applyPan(this.cameras.main, point.x, point.y);
  };

  private readonly onTestPanPointerUp = (event: PointerEvent): void => {
    if (this.panPointerId === null || event.pointerId !== this.panPointerId) {
      return;
    }

    event.preventDefault();

    try {
      this.game.canvas.releasePointerCapture(event.pointerId);
    } catch {
      // Capture may already have been released.
    }

    this.panPointerId = null;
    this.cameraControl.endPan();
    this.syncPlacementCursorVisibility();
  };

  private setPlacementCursorHidden(hidden: boolean): void {
    // Prefer setDefaultCursor so Phaser restore-after-hover does not wipe to OS default.
    this.input.setDefaultCursor(hidden ? 'none' : CURSOR_ARROW);
  }

  private syncPlacementCursorVisibility(): void {
    if (
      this.testPlay &&
      (this.stage === 'placing' || this.stage === 'aiming') &&
      (this.cameraControl.panning || (this.handDown && !isTypingInField()))
    ) {
      this.input.setDefaultCursor(this.cameraControl.panning ? 'grabbing' : 'grab');
      return;
    }

    if (this.stage !== 'placing' || this.placingSeatIsBot()) {
      this.setPlacementCursorHidden(false);
      return;
    }

    const pointer = this.input.activePointer;
    const overChrome = this.pointerOverChrome(pointer);
    this.setPlacementCursorHidden(!overChrome);
  }

  private onGameOut(): void {
    if (this.stage === 'placing') {
      this.setPlacementCursorHidden(false);
    }
  }

  private onGameOver(): void {
    if (this.stage === 'placing') {
      this.syncPlacementCursorVisibility();
    }
  }

  private syncStartAreaHover(point: Vec2): void {
    if (this.stage !== 'placing' && this.stage !== 'aiming') {
      return;
    }

    // Aiming keeps the solid look — the boat is already in the box.
    const hot =
      this.stage === 'aiming' || findStartAreaAt(this.map, point) !== undefined;

    if (hot === this.startAreaHot) {
      return;
    }

    this.startAreaHot = hot;
    this.redrawStartArea();
  }

  private redrawStartArea(): void {
    if (this.stage !== 'placing' && this.stage !== 'aiming') {
      this.startAreaGraphics.clear();
      return;
    }

    drawStartArea(
      this.startAreaGraphics,
      this.map,
      this.startAreaHot,
      1 / this.cameras.main.zoom,
    );
  }

  private plantBoat(position: Vec2): void {
    this.boat = createBoatState({
      position,
      heading: this.boat.heading,
      speed: this.startingSpeedPx,
    });
    const pointer = this.input.activePointer;
    this.wheelAimScreen = { x: pointer.x, y: pointer.y };
    this.stage = 'aiming';
    this.startAreaHot = true;
    this.setPlacementCursorHidden(false);

    this.hull
      .setPosition(position.x, position.y)
      .setRotation(this.boat.heading)
      .setAlpha(1)
      .setVisible(true);
    this.syncHullDisplaySize();
    this.syncHullGhost();
    this.redrawStartArea();
    this.snapAutoZoom();
    this.syncRaceHud();
  }

  private cancelPlant(): void {
    this.stage = 'placing';
    this.clearMoveLineChrome();
    this.syncHullDisplaySize();
    this.refreshPlacementFromPointer();
    this.snapAutoZoom();
    this.syncRaceHud();
  }

  private commitPlant(point: Vec2): void {
    const heading = this.headingFromWheel ? this.boat.heading : this.headingToward(point);

    if (heading === null || !this.canAim(heading)) {
      return;
    }

    this.finishPlacement(heading);
  }

  /** Parks the planted boat at `heading`, then opens the next seat's placement or starts the race. */
  private finishPlacement(heading: number): void {
    this.headingFromWheel = false;

    this.clearMoveLineChrome();
    this.setPlacementCursorHidden(false);
    this.parkCurrentPlacement(heading);

    if (this.placements.length < this.competitorCount) {
      this.beginNextPlacementCursor();
      return;
    }

    this.startMatchFromPlacements();
    const cam = this.cameras.main;
    cam.centerOn(this.boat.position.x, this.boat.position.y);
    this.lockFollow(1);
    this.snapAutoZoom();
  }

  private headingToward(point: Vec2): number | null {
    const delta = sub(point, this.boat.position);

    if (lengthSq(delta) < 1) {
      return null;
    }

    return toAngle(delta);
  }

  private previewAim(point: Vec2): void {
    const heading = this.headingToward(point);

    if (heading === null) {
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
      this.clearMoveLineChrome();
      return;
    }

    // A bow that would crowd a planted boat reads as illegal, as an illegal plant spot does.
    this.hull.setRotation(heading).setAlpha(this.canAim(heading) ? 1 : PLACEMENT_ILLEGAL_ALPHA);
    this.syncHullGhost();
    this.drawAimLine(heading);
  }

  private beginRace(): void {
    this.stage = 'racing';
    this.startAreaHot = false;
    const pointer = this.input.activePointer;
    this.hoverPoint = null;
    this.hoverPointerScreen = { x: pointer.x, y: pointer.y };
    this.handoffAimGate = { x: pointer.x, y: pointer.y };
    this.clearHeaderCues();
    this.clearImpactFlash();
    this.missedGateIds.clear();
    this.logLines = [];
    this.lastCrashPlan = null;
    this.moveFromSpeed = null;
    this.sessionScore = null;
    this.sessionScoreRows = [];
    this.raceStartedAt = performance.now();
    this.raceFinishedAt = null;
    this.lastHudElapsedBucket = 0;
    this.syncHullDisplaySize();
    this.drawMapLayers('chrome');
    this.drawMovementArea();
    this.setMovementAreaVisible(true);
    this.applyEnvironment();
    this.syncCompetitorAlphas();
    this.syncHud();
    this.syncRaceHud();
    this.syncMatchDataAttrs();
    this.showGoBanner();
    this.scheduleBotTurn();
  }

  private drawAimLine(heading: number): void {
    const bow = bowPosition({ position: this.boat.position, heading }, this.bowOffsetPx);
    const tip = add(bow, fromAngle(heading, metersToPixels(AIM_LINE_M)));
    const zoom = this.cameras.main.zoom;
    const head = AIM_ARROW_LENGTH_PX / zoom;
    const half = AIM_ARROW_HALF_PX / zoom;
    const base = add(tip, fromAngle(heading, -head));
    const left = add(base, fromAngle(heading + Math.PI / 2, half));
    const right = add(base, fromAngle(heading - Math.PI / 2, half));

    this.clearMoveLineChrome();
    this.moveLineGraphics.lineStyle(2 / zoom, rgb24(this.aimChrome.moveLine), this.aimChrome.moveLine.a);
    this.moveLineGraphics.beginPath();
    this.moveLineGraphics.moveTo(bow.x, bow.y);
    this.moveLineGraphics.lineTo(base.x, base.y);
    this.moveLineGraphics.strokePath();
    this.moveLineGraphics.fillStyle(rgb24(this.aimChrome.moveLine), this.aimChrome.moveLine.a);
    this.moveLineGraphics.fillTriangle(tip.x, tip.y, left.x, left.y, right.x, right.y);
  }

  private stopMoveTween(): void {
    this.finishShoves();
    this.clearSplashes();

    if (this.moveTween) {
      this.moveTween.stop();
      this.moveTween = null;
    }
  }

  private travel(path: MovePath): void {
    const active = this.views.find((view) => view.hull === this.hull);

    if (active) {
      this.abandonShove(active);
    }

    const curve = toCurve(path);

    // Resolving always looks ahead; do not ease up from a planning gate of 0 mid-tween.
    this.lookAheadGate = 1;

    this.moveTween = this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: MOVE_DURATION_MS,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        const progress = tween.getValue() ?? 1;
        const point = curve.getPoint(progress, this.curvePoint);
        const tangent = curve.getTangent(progress, this.curveTangent);

        if (point) {
          this.hull.setPosition(point.x, point.y);
        }

        if (tangent) {
          const tangentHeading = Math.atan2(tangent.y, tangent.x);
          const spin =
            this.impactAlong === null ? 0 : driftProgress(progress, this.impactAlong);

          this.hull.setRotation(
            tangentHeading + angleDelta(tangentHeading, this.boat.heading) * spin,
          );
        }

        this.syncHullGhost();
        this.syncShoves(progress);
        this.trailProgress = progress;
        this.drawTrail();
        this.revealTravelledGates(progress);
      },
      onComplete: () => {
        this.moveTween = null;
        this.trailProgress = 1;
        // Land on the sim pose. A collision yaws the bow past the path tangent, and the
        // last tween step does not always reach progress 1, so the sprite would otherwise
        // keep the tangent and every later click would leave on the wrong heading.
        this.hull.setPosition(this.boat.position.x, this.boat.position.y);
        this.hull.setRotation(this.boat.heading);
        this.syncHullGhost();
        this.releaseShoves();
        this.drawTrail();
        this.settleTravelledGates();
        this.finishMove();
      },
    });
  }

  private headingDegrees(radians: number): number {
    return ((radians * 180) / Math.PI + 360) % 360;
  }

  private onWheel(
    pointer: Phaser.Input.Pointer,
    _currentlyOver: Phaser.GameObjects.GameObject[],
    _deltaX: number,
    deltaY: number,
  ): void {
    if (this.boatHeld && this.testPanActive()) {
      const degrees = boatGuideHeadingAfterWheel(
        Math.round(this.headingDegrees(this.boat.heading)),
        deltaY,
      );
      this.boat.heading = (degrees * Math.PI) / 180;
      this.headingFromWheel = true;
      this.wheelAimScreen = { x: pointer.x, y: pointer.y };
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();

      if (this.stage === 'aiming') {
        this.drawAimLine(this.boat.heading);
      }

      return;
    }

    const cam = this.cameras.main;
    let nextZoom: number;

    if (this.fleetFraming()) {
      const auto = this.fleetCameraTarget()?.autoZoom ?? cam.zoom;
      this.userZoomFactor = nextUserZoomFactor(
        this.userZoomFactor,
        auto,
        deltaY > 0,
      );
      this.autoZoom = auto;
      nextZoom = appliedZoom(auto, this.userZoomFactor);
    } else if (this.shouldAutoCamera()) {
      const auto = this.playCameraTarget().autoZoom;
      this.userZoomFactor = nextUserZoomFactor(
        this.userZoomFactor,
        auto,
        deltaY > 0,
      );
      this.autoZoom = auto;
      nextZoom = clamp(auto * this.userZoomFactor, PLAY_MIN_ZOOM, MAX_ZOOM);
    } else {
      nextZoom = this.cameraControl.wheelZoom(cam.zoom, deltaY > 0);
    }

    this.applyUserZoom(nextZoom, pointer.x, pointer.y);
  }

  private applyUserZoom(nextZoom: number, anchorX: number, anchorY: number): void {
    const cam = this.cameras.main;

    if (this.tracking) {
      if (nextZoom === cam.zoom) {
        return;
      }

      cam.setZoom(nextZoom);
      this.refreshZoomChrome();
      this.syncHud();
      return;
    }

    if (!this.cameraControl.applyZoom(cam, nextZoom, anchorX, anchorY)) {
      return;
    }

    this.refreshZoomChrome();
    this.syncHud();
  }

  private constrainCamera(): void {
    this.cameraControl.setWorldBounds(this.map.bounds);
    this.cameraControl.constrain(this.cameras.main);
  }

  /** Turns a gate green only once the animated hull has crossed it. */
  private revealTravelledGates(progress: number): void {
    const travel = this.activeTravel;

    if (!travel) {
      return;
    }

    const travelled = advanceRace(
      this.map,
      travel.fromRace,
      travel.visualPath,
      progress,
    );
    const next: RaceState = {
      lap: travelled.lap,
      passedCheckpointIds: travelled.passedCheckpointIds,
      complete: false,
    };

    if (sameRaceProgress(this.race, next)) {
      return;
    }

    this.race = next;
    this.repaintGates();
    this.syncPassedGateBadges();
    this.syncRaceHud();
  }

  private settleTravelledGates(): void {
    const travel = this.activeTravel;

    if (!travel) {
      return;
    }

    const misses = nearMissCheckpoints(
      this.map,
      travel.fromRace,
      travel.visualPath,
      nearMissWorld(this.nearMissPx, this.cameras.main.zoom),
    );

    if (misses.length > 0) {
      this.showGateMissedBanner(misses);
    }

    this.race = travel.committed;
    this.activeTravel = null;
    this.repaintGates();
    this.syncPassedGateBadges();
    this.syncRaceHud();
  }

  private finishMove(): void {
    if (!this.match) {
      const idle = completeResolve(this.boat);
      this.boat = idle.boat;
      this.phase = idle.phase;
      this.moveFromSpeed = null;
      this.hull.setPosition(this.boat.position.x, this.boat.position.y);
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
      this.clearHover();
      this.applyEnvironment();
      this.syncRaceHud();
      return;
    }

    const previousActiveId = this.match.activeId;
    this.match = finishActiveTurn(this.match);
    this.expireHeaderCuesOnHandoff(previousActiveId, this.match.activeId);
    this.syncActiveFromMatch();
    this.moveFromSpeed = null;
    // A hull still sliding into its landing must not be pinned there. The pin teleports
    // it to the end, then the slide's next frame yanks it back along the chord, and a
    // click during that fight leaves the sprite on the old pose for the rest of the race.
    const sliding = this.driftingSeats.has(this.match.activeId);

    if (!sliding) {
      this.hull.setPosition(this.boat.position.x, this.boat.position.y);
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
    }

    this.clearHover();
    this.applyEnvironment();
    this.syncCompetitorAlphas();
    this.syncRaceHud();

    if (matchComplete(this.match)) {
      this.pendingFinish = false;
      this.beginFinishCeremony();
      return;
    }

    this.pendingFinish = false;

    const parking = seatIsParking(activeCompetitor(this.match));

    if (sliding || parking) {
      this.setMovementAreaVisible(false);
    } else {
      this.drawMovementArea();
      this.setMovementAreaVisible(true);
    }

    if (this.tracking) {
      if (parking) {
        // Finished boats autopilot off-camera: keep follow on a still-racing hull.
        this.followRacingSeatCamera(previousActiveId);
      } else if (this.match.activeId !== previousActiveId) {
        this.beginSeatHandoffCamera();
      } else {
        // Same seat (solo every turn): already following this hull. Do not restart
        // follow — startFollow would zero look-ahead and flash the framing.
        this.cameras.main.setLerp(1, 1);
      }
    } else if (!parking) {
      this.recenterOnMovementArea();
    }

    this.syncHud();
    this.syncRaceHud();
    this.scheduleBotTurn();
  }

  /**
   * Camera never rides a finished / parking seat. Point follow at a still-racing hull
   * (prefer the seat we just left if it is still racing).
   */
  private followRacingSeatCamera(preferId: number): void {
    if (!this.match || !this.tracking) {
      return;
    }

    const racing =
      this.match.competitors.find(
        (seat) => seat.id === preferId && !seat.race.complete,
      ) ?? this.match.competitors.find((seat) => !seat.race.complete);

    if (!racing) {
      return;
    }

    const view = this.views.find((entry) => entry.id === racing.id);

    if (!view) {
      return;
    }

    const cam = this.cameras.main;
    const ox = cam.followOffset.x;
    const oy = cam.followOffset.y;
    cam.startFollow(view.hull, true, 1, 1, ox, oy);
  }

  /** The boat being planted or aimed belongs to a bot seat. */
  private placingSeatIsBot(): boolean {
    return (
      this.match === null &&
      (this.stage === 'placing' || this.stage === 'aiming') &&
      seatIsBot(this.seatControllers[this.placements.length])
    );
  }

  /** It is a bot seat's turn to click. */
  private activeSeatIsBot(): boolean {
    return (
      this.match !== null &&
      this.stage === 'racing' &&
      seatIsBot(this.seatControllers[this.match.activeId])
    );
  }

  /** Bot race AI, or post-finish parking autopilot (human or bot). */
  private activeSeatIsAutopilot(): boolean {
    if (!this.match || this.stage !== 'racing') {
      return false;
    }

    const seat = activeCompetitor(this.match);
    return seatIsParking(seat) || this.activeSeatIsBot();
  }

  private setBotThinking(seatId: number | null): void {
    const root = this.game.canvas.parentElement;

    if (!root) {
      return;
    }

    root.setAttribute('data-bot-seed', String(this.botSeed));

    if (seatId === null) {
      root.removeAttribute('data-bot-thinking');
    } else {
      root.setAttribute('data-bot-thinking', String(seatId + 1));
    }
  }

  /** A bot seat plants through `chooseStart`, clear of boats already down, then parks bow on. */
  private placeBotSeat(): void {
    if (!this.botClient || this.stage !== 'placing' || !this.placingSeatIsBot()) {
      return;
    }

    const token = ++this.botToken;
    const seatId = this.placements.length;
    const started = performance.now();
    this.hull.setVisible(false);
    this.hullGhost.setVisible(false);
    this.syncPlacementCursorVisibility();
    this.setBotThinking(seatId);

    const place = (start: { position: Vec2; heading: number } | null): void => {
      if (token !== this.botToken || this.stage !== 'placing' || this.placements.length !== seatId) {
        return;
      }

      this.setBotThinking(null);
      const pose = start ?? { position: startAreaCentroid(this.map), heading: this.boat.heading };
      this.plantBoat(pose.position);
      this.finishPlacement(pose.heading);
    };

    this.botClient
      .start({
        placed: this.placedHulls(),
        hull: this.seatHullSizes().get(seatId) ?? DEFAULT_HULL,
        feel: this.seatTurnFeel(seatId),
      })
      .then((start) => {
        if (token !== this.botToken) {
          return;
        }

        const wait = Math.max(0, BOT_PLACE_BEAT_MS - (performance.now() - started));
        this.time.delayedCall(wait, () => place(start));
      })
      .catch((error: unknown) => {
        console.warn('Bot start failed; planting at the start-area centre.', error);

        if (token === this.botToken) {
          place(null);
        }
      });
  }

  /**
   * When the new turn is a bot seat's (or a finished seat still parking), ask for a click with
   * the exact feel the commit will use. The answer shows as the hover aim for a beat, then
   * goes through `commitDestination` like a pointer click.
   */
  private scheduleBotTurn(): void {
    if (!this.match || this.stage !== 'racing' || this.phase !== 'planning') {
      return;
    }

    const seatId = this.match.activeId;
    const seat = activeCompetitor(this.match);

    if (seatIsParking(seat)) {
      const token = ++this.botToken;
      this.clearHover();
      this.setMovementAreaVisible(false);
      this.setBotThinking(seatId);
      const destination = decideParkDestination(seat.boat, {
        map: this.map,
        feel: this.turnFeel(),
        fleet: this.movement === 'simultaneous' ? undefined : this.fleetContext(),
        parkTurns: seat.parkTurns,
        reverseHeading: seat.parkReverseHeading ?? undefined,
      });
      // No aim / prediction chrome — commit quietly after a short beat.
      this.time.delayedCall(BOT_THINK_BEAT_MS, () => {
        if (!this.botTurnStillOpen(token, seatId)) {
          return;
        }

        this.setBotThinking(null);

        if (!this.commitDestination(destination)) {
          this.commitDestination(this.botFallbackDestination(seatId));
        }
      });
      return;
    }

    if (!this.botClient) {
      this.setBotThinking(null);
      return;
    }

    const controller = this.seatControllers[seatId];
    const knobs = seatBotKnobs(controller);

    if (!knobs) {
      this.setBotThinking(null);
      return;
    }

    const token = ++this.botToken;
    const started = performance.now();
    this.clearHover();
    this.setBotThinking(seatId);

    this.botClient
      .move({
        boat: seat.boat,
        race: seat.race,
        feel: this.turnFeel(),
        fleet: this.movement === 'simultaneous' ? undefined : this.fleetContext(),
        rivals: this.botRivals(),
        level: seatBotLevel(controller) ?? 'expert',
        knobs,
        seed: this.botSeed,
        seat: seatId,
        turn: seat.moveSpeeds.length + 1,
      })
      .then((destination) => {
        if (token !== this.botToken) {
          return;
        }

        const wait = Math.max(0, BOT_THINK_BEAT_MS - (performance.now() - started));
        this.time.delayedCall(wait, () => this.showBotAim(token, seatId, destination));
      })
      .catch((error: unknown) => {
        console.warn('Bot move failed; falling back.', error);

        if (token === this.botToken) {
          this.showBotAim(token, seatId, this.botFallbackDestination(seatId));
        }
      });
  }

  private botTurnStillOpen(token: number, seatId: number): boolean {
    return (
      token === this.botToken &&
      this.match?.activeId === seatId &&
      this.stage === 'racing' &&
      this.phase === 'planning'
    );
  }

  /** The least-bad exact click, when the worker could not answer or its click was refused. */
  private botFallbackDestination(seatId: number): Vec2 {
    void seatId;
    const seat = this.match ? activeCompetitor(this.match) : null;
    const fleet =
      this.movement === 'simultaneous' ? undefined : this.fleetContext();

    if (seat && seatIsParking(seat)) {
      return decideParkDestination(seat.boat, {
        map: this.map,
        feel: this.turnFeel(),
        fleet,
        parkTurns: seat.parkTurns,
        reverseHeading: seat.parkReverseHeading ?? undefined,
      });
    }

    return fallbackDestination(this.boat, seat?.race ?? this.race, this.map, this.turnFeel(), fleet);
  }

  private showBotAim(token: number, seatId: number, destination: Vec2): void {
    if (!this.botTurnStillOpen(token, seatId)) {
      return;
    }

    if (this.handoffActive || this.driftingSeats.has(seatId)) {
      this.time.delayedCall(BOT_HANDOFF_POLL_MS, () =>
        this.showBotAim(token, seatId, destination),
      );
      return;
    }

    this.setHover(destination);
    this.time.delayedCall(BOT_AIM_BEAT_MS, () => {
      if (!this.botTurnStillOpen(token, seatId)) {
        return;
      }

      this.setBotThinking(null);

      if (this.commitDestination(destination)) {
        return;
      }

      // Feel changed mid-think: Expert stuck salvage, others coast, rather than stall.
      if (!this.commitDestination(this.botFallbackDestination(seatId))) {
        console.warn(`Bot seat ${seatId + 1} has no legal click this turn.`);
      }
    });
  }

  /**
   * Simultaneous: park a committed move and hand the seat on. Nothing of it is drawn — no
   * wake, no route, no gate paint, no damage cue — because the next player is sitting in the
   * same chair and must plan without seeing it. The round plays once everyone has locked.
   */
  private lockInSeatMove(entry: {
    seatId: number;
    path: MovePath | null;
    fromRace: RaceState;
    committed: RaceState;
    fromSpeed: number;
    damaged: boolean;
  }): void {
    if (!this.match) {
      return;
    }

    this.roundTravel = [...this.roundTravel, entry];
    this.match = passLockIn(this.match);

    if (roundReady(this.match)) {
      this.revealRound();
      return;
    }

    this.syncActiveFromMatch();
    this.moveFromSpeed = null;
    // The crash-harshness slider re-plans `lastCrashPlan` against the *active* seat. Holding
    // a locked seat's plan across the handoff would rewrite the wrong boat.
    this.lastCrashPlan = null;
    this.syncCompetitorAlphas();
    const parking = seatIsParking(activeCompetitor(this.match));

    if (parking) {
      this.setMovementAreaVisible(false);
    } else {
      this.drawMovementArea();
      this.setMovementAreaVisible(true);
    }

    if (this.tracking) {
      if (parking) {
        this.followRacingSeatCamera(entry.seatId);
      } else {
        this.beginSeatHandoffCamera();
      }
    } else if (!parking) {
      this.recenterOnMovementArea();
    }

    this.syncHud();
    this.syncRaceHud();
    this.scheduleBotTurn();
  }

  /**
   * Play every locked move at once. The lowest seat id still racing is the representative:
   * it drives `activeTravel` (so gate reveal, the round counter, and the telemetry plate keep
   * one code path) while each hull walks its own curve. The camera frames the pack instead of
   * following one boat, same fit as `All at once` replay.
   */
  private revealRound(): void {
    if (!this.match || this.roundTravel.length === 0) {
      return;
    }

    this.match = beginRound(this.match);
    this.revealing = true;
    this.lastCrashPlan = null;
    const damagedBefore = new Map(
      this.match.competitors.map((seat) => [seat.id, seat.hull.damaged]),
    );
    // Resolve before any hull moves, so the curves on screen are the paths the fleet
    // sweep produced, not the sea-to-itself previews stored at lock-in.
    const resolved = resolveRound(
      this.match,
      this.map,
      this.crashHarshness,
      this.turnTax,
      this.seatHullSizes(),
    );
    this.stagedRound = resolved;
    const runs = new Map(resolved.runs.map((run) => [run.id, run]));
    this.roundTravel = this.roundTravel.map((entry) => {
      const run = runs.get(entry.seatId);
      const seat = resolved.match.competitors.find((candidate) => candidate.id === entry.seatId);

      return {
        ...entry,
        path: run?.path ?? entry.path,
        committed: seat?.race ?? entry.committed,
        damaged: Boolean(seat?.hull.damaged) && !damagedBefore.get(entry.seatId),
      };
    });
    this.syncActiveFromMatch();

    const representativeId = this.match.activeId;
    const representative =
      this.roundTravel.find((entry) => entry.seatId === representativeId) ?? this.roundTravel[0]!;
    const resolvedRepresentative = resolved.match.competitors.find(
      (seat) => seat.id === representativeId,
    );

    this.moveFromSpeed = representative.fromSpeed;
    this.pendingFinish =
      representative.fromRace.complete === false && (resolvedRepresentative?.race.complete ?? false);
    this.lastFinishProgress =
      resolvedRepresentative?.finishProgress ?? this.lastFinishProgress;

    if (representative.path) {
      this.activeTravel = {
        fromRace: representative.fromRace,
        visualPath: representative.path,
        committed: representative.committed,
      };
    }

    const legs = new Map<number, Phaser.Curves.Path>();

    for (const entry of this.roundTravel) {
      const view = this.views.find((candidate) => candidate.id === entry.seatId);

      if (!view || !entry.path) {
        continue;
      }

      view.trail = pushTrail(view.trail, entry.path, this.trailHistoryLimit());
      view.route = [...view.route, entry.path];
      view.trailProgress = 0;
      legs.set(entry.seatId, toCurve(entry.path));

      if (view.id === representativeId) {
        this.trail = view.trail;
        this.route = view.route;
      }
    }

    const steered = new Set(this.roundTravel.map((entry) => entry.seatId));
    const parked: ShovedSeat[] = [];
    const spin = new Map<number, { heading: number; at: number | null }>();

    for (const run of resolved.runs) {
      const hit = resolved.contacts
        .filter((contact) => contact.fromId === run.id || contact.intoId === run.id)
        .sort((left, right) => left.at - right.at)[0];

      spin.set(run.id, { heading: run.boat.heading, at: hit?.at ?? null });

      // A seat that steered already has the bounce on its own path. Only a hull
      // that did not lock a move — a finished boat still on the course — slides late.
      if (!run.shoved || steered.has(run.id)) {
        continue;
      }

      parked.push({
        id: run.id,
        boat: run.boat,
        path: run.path,
        alongMover: hit?.at ?? 0,
      });
    }

    this.driftShovedSeats(parked);
    this.queueCollisionSplashes(resolved.contacts, parked);
    this.logBoatContacts(resolved.contacts, representativeId);

    this.trailProgress = 0;
    this.applyFleetCamera();
    this.drawTrail();
    this.drawRoute();
    this.syncCompetitorAlphas();
    this.syncMatchDataAttrs();
    this.syncRaceHud();

    if (legs.size === 0) {
      this.settleRound();
      return;
    }

    // Resolving always looks ahead; do not ease up from a planning gate of 0 mid-tween.
    this.lookAheadGate = 1;

    this.moveTween = this.tweens.addCounter({
      from: 0,
      to: 1,
      duration: MOVE_DURATION_MS,
      ease: 'Sine.easeInOut',
      onUpdate: (tween) => {
        const progress = tween.getValue() ?? 1;

        for (const view of this.views) {
          const curve = legs.get(view.id);

          if (!curve) {
            continue;
          }

          const point = curve.getPoint(progress, this.curvePoint);
          const tangent = curve.getTangent(progress, this.curveTangent);

          if (point) {
            view.hull.setPosition(point.x, point.y);
          }

          if (tangent && (tangent.x !== 0 || tangent.y !== 0)) {
            const tangentHeading = Math.atan2(tangent.y, tangent.x);
            const landed = spin.get(view.id);
            const yaw = landed?.at == null ? 0 : driftProgress(progress, landed.at);

            view.hull.setRotation(
              tangentHeading + angleDelta(tangentHeading, landed?.heading ?? tangentHeading) * yaw,
            );
          }

          view.trailProgress = progress;
          this.syncViewGhost(view);
        }

        this.syncShoves(progress);
        this.syncHullGhost();
        this.trailProgress = progress;
        this.drawTrail();
        this.revealTravelledGates(progress);
      },
      onComplete: () => {
        this.moveTween = null;
        this.trailProgress = 1;

        for (const view of this.views) {
          if (legs.has(view.id)) {
            view.trailProgress = 1;
          }
        }

        this.drawTrail();
        this.releaseShoves();
        this.settleTravelledGates();
        this.settleRound();
      },
    });
  }

  /** Publish every locked move, then open the next round's lock-in on the lowest seat. */
  private settleRound(): void {
    if (!this.match) {
      return;
    }

    const played = this.roundTravel;
    const staged = this.stagedRound;
    this.stagedRound = null;
    this.roundTravel = [];
    this.revealing = false;
    this.impactAlong = null;

    if (staged) {
      this.match = staged.match;
    }

    // Damage held back through the lock-in lands now, with the move that caused it. No
    // handoff expiry: the chip belongs to the round that just played, not to a past turn.
    for (const entry of played) {
      if (entry.damaged) {
        this.armHeaderCue('hull', entry.seatId);
      }
    }

    this.syncActiveFromMatch();
    this.moveFromSpeed = null;

    for (const view of this.views) {
      const seat = this.match.competitors.find((entry) => entry.id === view.id);

      if (seat && !this.driftingSeats.has(view.id)) {
        view.hull.setPosition(seat.boat.position.x, seat.boat.position.y);
        view.hull.setRotation(seat.boat.heading);
        this.syncViewGhost(view);
      }
    }

    this.syncHullGhost();
    this.clearHover();
    this.applyEnvironment();
    this.syncCompetitorAlphas();

    if (played.some((entry) => entry.damaged)) {
      this.flashImpact('damage');
    }

    if (matchComplete(this.match)) {
      this.pendingFinish = false;
      this.syncRaceHud();
      this.beginFinishCeremony();
      return;
    }

    this.pendingFinish = false;
    const parking = seatIsParking(activeCompetitor(this.match));

    if (parking) {
      this.setMovementAreaVisible(false);
    } else {
      this.drawMovementArea();
      this.setMovementAreaVisible(true);
    }

    // The fleet frame stopped follow, so the glide back onto a still-racing seat re-pins
    // the camera — never onto a finished boat's parking autopilot.
    this.tracking = true;
    if (parking) {
      this.followRacingSeatCamera(this.match.activeId);
    } else {
      this.beginSeatHandoffCamera();
    }
    this.syncMatchDataAttrs();
    this.syncHud();
    this.syncRaceHud();
    this.scheduleBotTurn();
  }

  private buildSessionScore(): HighScore {
    const ranking = this.match ? rankMatch(this.match) : [];
    const winner = ranking[0];
    const rounds = winner?.rounds ?? this.moveSpeeds.length;
    const finishProgressValue = winner?.finishProgress ?? this.lastFinishProgress;
    const speeds =
      winner && this.match
        ? [
            ...(this.match.competitors.find((seat) => seat.id === winner.id)
              ?.moveSpeeds ?? this.moveSpeeds),
          ]
        : this.moveSpeeds;
    const angles =
      winner && this.views.find((view) => view.id === winner.id)
        ? this.views.find((view) => view.id === winner.id)!.turnAnglesDeg
        : this.turnAnglesDeg;

    return {
      rounds,
      calculatedTimeMs: calculatedTimeMs(rounds, finishProgressValue),
      timeMs: this.elapsedMs(),
      maxSpeedKmh: maxSpeedKmh(speeds),
      averageSpeedKmh: averageSpeedKmh(speeds),
      tightestTurnDeg: tightestTurnDeg(angles),
      recordedAt: Date.now(),
      boat: this.scoreBoat(winner?.id ?? 0),
      replay:
        (this.match?.competitors.length ?? 1) === 1 && this.replayStart
          ? {
              start: {
                position: { ...this.replayStart.position },
                heading: this.replayStart.heading,
              },
              moves: this.replayMoves.map((move) => ({
                destination: { ...move.destination },
                radius: move.radius,
              })),
            }
          : null,
    };
  }

  /** Session-only per-seat replay data for a just-finished hot-seat match (never persisted). */
  private buildMatchReplayData(): RaceReplay[] {
    return this.views.map((view) => ({
      start: {
        position: { ...(view.replayStart?.position ?? { x: 0, y: 0 }) },
        heading: view.replayStart?.heading ?? 0,
      },
      moves: view.replayMoves.map((move) => ({
        destination: { ...move.destination },
        radius: move.radius,
      })),
    }));
  }

  private buildSessionScoreRows(): { score: HighScore; color: string; seatId: number }[] {
    const recordedAt = Date.now();
    const ranking = this.match ? rankMatch(this.match) : [];

    if (ranking.length === 0) {
      return [
        {
          score: this.buildSessionScore(),
          color: competitorTintCss(0),
          seatId: 0,
        },
      ];
    }

    return ranking.map((result, index) => {
      const seat = this.match?.competitors.find((entry) => entry.id === result.id);
      const view = this.views.find((entry) => entry.id === result.id);
      const speeds = seat ? [...seat.moveSpeeds] : this.moveSpeeds;
      const angles = view?.turnAnglesDeg ?? this.turnAnglesDeg;
      const start = view?.replayStart ?? this.replayStart;
      const moves = view?.replayMoves ?? this.replayMoves;

      return {
        score: {
          rounds: result.rounds,
          calculatedTimeMs: result.calculatedTimeMs,
          timeMs: this.elapsedMs(),
          maxSpeedKmh: maxSpeedKmh(speeds),
          averageSpeedKmh: averageSpeedKmh(speeds),
          tightestTurnDeg: tightestTurnDeg(angles),
          recordedAt: recordedAt + index,
          boat: this.scoreBoat(result.id),
          replay: start
            ? {
                start: {
                  position: { ...start.position },
                  heading: start.heading,
                },
                moves: moves.map((move) => ({
                  destination: { ...move.destination },
                  radius: move.radius,
                })),
              }
            : null,
        },
        color: competitorTintCss(result.id),
        seatId: result.id,
      };
    });
  }

  private currentScores(): HighScore[] {
    if (this.mapId && !this.testPlay) {
      return loadHighScores(this.mapId);
    }

    return this.sessionScoreRows.map((row) => row.score);
  }

  private finishScoreRows(): {
    score: HighScore;
    current?: boolean;
    color?: string;
  }[] {
    const currentAt = new Set(this.sessionScoreRows.map((row) => row.score.recordedAt));
    const colorByAt = new Map(
      this.sessionScoreRows.map((row) => [row.score.recordedAt, row.color]),
    );

    return this.currentScores().map((score) => ({
      score,
      current: currentAt.has(score.recordedAt),
      color: colorByAt.get(score.recordedAt),
    }));
  }

  private openFinishPopup(): void {
    const hotSeat = (this.match?.competitors.length ?? 1) > 1;

    if (this.sessionScoreRows.length === 0) {
      this.sessionScoreRows = this.buildSessionScoreRows();
      this.sessionScore = this.sessionScoreRows[0]?.score ?? null;

      // Bot finishes never reach Top Lists or Helmway `race_runs` (docs/RACE-RUNS.md).
      const humanScores = this.sessionScoreRows
        .filter((row) => !seatIsBot(this.seatControllers[row.seatId]))
        .map((row) => row.score);

      if (this.mapId && !this.testPlay && humanScores.length > 0) {
        recordHighScores(this.mapId, humanScores);
      }
    }

    if (hotSeat && !this.matchReplayData) {
      this.matchReplayData = this.buildMatchReplayData();
    }

    const score = this.sessionScore;

    this.finishPopup?.destroy();
    this.finishPopup = mountFinishPopup({
      actions: {
        onRaceAgain: () => {
          this.scene.restart(this.relaunchData());
        },
        onShowReplay: hotSeat
          ? this.matchReplayData
            ? () => this.startMatchReplay(this.matchReplayData!)
            : undefined
          : score?.replay
            ? () => this.startReplay(score)
            : undefined,
        onLeave: () => {
          if (this.testPlay) {
            this.exitToEditor();
            return;
          }

          this.scene.start('StartScene');
        },
        leaveLabel: this.testPlay
          ? testLeaveLabel(this.testOrigin ?? 'editor')
          : 'Choose another map',
      },
      scores: this.finishScoreRows(),
      onSelectScore: (selected) => {
        if (selected.replay) {
          this.startReplay(selected);
        }
      },
    });
    this.syncRaceHud();
  }

  private startReplay(score: HighScore): void {
    this.finishPopup?.destroy();
    this.finishPopup = null;
    this.stopMoveTween();
    this.activeTravel = null;
    this.pendingFinish = false;

    const returnToFinished = this.stage === 'finished';
    this.finishedRace = returnToFinished ? this.race : null;
    const feel = planningFeelFromBoat(score.boat);
    const rebuilt = score.replay
      ? reconstructReplay(
          this.map,
          score.replay,
          this.crashHarshness,
          this.turnTax,
          this.windFeel,
          {
            reach: feel.reach,
            bowOffsetPx: feel.bowOffsetPx,
            startSpeed: this.startingSpeedPx,
            planingSpeedKmh: score.boat.planingSpeedKmh,
            ringMode: this.ringMode,
          },
        )
      : null;
    const paths = rebuilt?.paths ?? [];

    this.stage = 'replay';
    this.replay = {
      seats: [
        {
          seatId: 0,
          boats: rebuilt?.boats ?? [],
          paths,
          racesBefore: racePrefixStates(this.map, paths),
          moves: score.replay?.moves ?? [],
        },
      ],
      steps: paths.map((_, index) => ({ seatId: 0, localIndex: index })),
      stepIndex: 0,
      moveProgress: 0,
      playing: false,
      valid: rebuilt !== null,
      returnToFinished,
      score,
    };

    this.beginReplayPlayback();

    if (returnToFinished) {
      this.presentReplayHull(score.boat);
    }
  }

  /** Sizes and repaints the animating hull for one stored row, and remembers the race sprite. */
  private presentReplayHull(boat: HighScoreBoat): void {
    if (this.hull?.scene && this.hullGhost?.scene && !this.replayHullLook) {
      this.replayHullLook = {
        hullKey: this.hull.texture.key,
        ghostKey: this.hullGhost.texture.key,
        hullW: this.hull.displayWidth,
        hullH: this.hull.displayHeight,
        ghostW: this.hullGhost.displayWidth,
        ghostH: this.hullGhost.displayHeight,
      };
    }

    const feel = planningFeelFromBoat(boat);
    this.applyHullDisplaySize(
      this.hull,
      this.hullGhost,
      metricHullDisplaySize(feel.lengthM, feel.beamM),
    );
    void this.paintReplayHull(boat);
  }

  private async paintReplayHull(boat: HighScoreBoat): Promise<void> {
    const token = ++this.replayPaintToken;
    const source = await loadBoatSvgSource();

    if (!source || !this.sys.isActive() || token !== this.replayPaintToken || this.stage !== 'replay') {
      return;
    }

    const key = await loadPaintedBoatTexture(this, source, boat, 'boat-replay-row');

    if (
      !key ||
      !this.sys.isActive() ||
      token !== this.replayPaintToken ||
      this.stage !== 'replay' ||
      !this.hull?.scene
    ) {
      return;
    }

    this.hull.setTexture(key);
    this.hullGhost.setTexture(key);
    this.applySeatTint(this.hull, this.hullGhost, this.hullSeatId(this.hull));
  }

  private restoreReplayHull(): void {
    const look = this.replayHullLook;
    this.replayHullLook = null;
    this.replayPaintToken += 1;

    if (!look || !this.hull?.scene || !this.hullGhost?.scene) {
      return;
    }

    if (this.textures.exists(look.hullKey)) {
      this.hull.setTexture(look.hullKey);
    }

    if (this.textures.exists(look.ghostKey)) {
      this.hullGhost.setTexture(look.ghostKey);
    }

    this.hull.setDisplaySize(look.hullW, look.hullH);
    this.hullGhost.setDisplaySize(look.ghostW, look.ghostH);
    this.applySeatTint(this.hull, this.hullGhost, this.hullSeatId(this.hull));
  }

  /**
   * Reconstructs a just-finished hot-seat match from each seat's own recorded clicks
   * (session-only — never persisted) and walks the exact alternating turn order
   * `finishActiveTurn` produced live. Default playback is All at once (`simultaneous`);
   * Turn by turn still uses the live seat-handoff camera glide.
   */
  private startMatchReplay(seatReplays: readonly RaceReplay[]): void {
    this.finishPopup?.destroy();
    this.finishPopup = null;
    this.stopMoveTween();
    this.activeTravel = null;
    this.pendingFinish = false;

    const returnToFinished = this.stage === 'finished';
    this.finishedRace = returnToFinished ? this.race : null;

    // One seat at a time no longer reproduces the race: a hull that was shoved
    // starts its next turn somewhere its own clicks never put it. Sequential
    // walks that turn order. Simultaneous re-sweeps each round, because a
    // contact both boats steered into is not a ram into a parked hull.
    const rebuiltSeats = reconstructMatchReplay(
      this.map,
      seatReplays.map((seatReplay, seatId) => ({
        replay: seatReplay,
        feel: this.replayFeelFor(seatId),
        dimensions: this.seatHulls[seatId]
          ? { lengthM: this.seatHulls[seatId]!.lengthM, beamM: this.seatHulls[seatId]!.beamM }
          : DEFAULT_HULL,
      })),
      this.crashHarshness,
      this.turnTax,
      this.windFeel,
      this.movement,
    );
    const seats = rebuiltSeats.map((rebuilt, seatId) => {
      const paths = rebuilt?.paths ?? [];

      return {
        seatId,
        boats: rebuilt?.boats ?? [],
        paths,
        racesBefore: racePrefixStates(this.map, paths),
        moves: seatReplays[seatId]?.moves ?? [],
      };
    });

    this.stage = 'replay';
    this.replay = {
      seats,
      steps: matchReplayOrder(seatReplays.map((seatReplay) => seatReplay.moves.length)),
      stepIndex: 0,
      moveProgress: 0,
      playing: false,
      valid: rebuiltSeats.every((rebuilt) => rebuilt !== null),
      returnToFinished,
      score: null,
    };

    this.beginReplayPlayback();
  }

  /**
   * Shared tail for `startReplay` (solo) and `startMatchReplay` (hot-seat): builds one curve
   * per flat step, resets every seat's hull back to its recorded start pose with an empty
   * trail (route is set once here, full, and never re-sliced while scrubbing), then poses the
   * first step.
   */
  private beginReplayPlayback(): void {
    const replay = this.replay;

    if (!replay) {
      return;
    }

    this.clearReplayCurves();
    this.replayCurves = replay.steps.map((step) => {
      const seat = replay.seats.find((entry) => entry.seatId === step.seatId);
      const path = seat?.paths[step.localIndex];
      return path ? toCurve(path) : null;
    });
    this.replaySeatCurves = new Map(
      replay.seats.map((seat) => [
        seat.seatId,
        seat.paths.map((path) => (path ? toCurve(path) : null)),
      ]),
    );
    this.replayRounds = groupReplayStepsByRound(replay.steps);
    this.replayWorldSpeeds.clear();
    this.finishCeremonyZoom = null;

    // The wake being drawn for the finished race is about to be overwritten by progressive
    // replay slices; snapshot it so Close replay can put it back exactly as it was.
    this.finishedViewTrails = replay.returnToFinished
      ? new Map(this.views.map((view) => [view.id, view.trail]))
      : null;

    for (const view of this.views) {
      const seat = replay.seats.find((entry) => entry.seatId === view.id);
      view.route = seat ? [...seat.paths] : [];
      view.trail = [];
      view.trailProgress = 0;

      const start = seat?.boats[0];

      if (start) {
        view.hull.setPosition(start.position.x, start.position.y);
        view.hull.setRotation(start.heading);
      }

      view.hull.setVisible(true);
      this.syncViewGhost(view);
    }

    const firstSeatId = replay.steps[0]?.seatId;
    const firstView =
      firstSeatId !== undefined ? this.views.find((view) => view.id === firstSeatId) : undefined;

    if (firstView) {
      this.hull = firstView.hull;
      this.hullGhost = firstView.hullGhost;
    }

    this.route = replay.seats[0]?.paths ?? [];
    this.showRoute = false;
    this.lastCrashPlan = null;
    this.hull.setVisible(true);
    this.syncHullGhost();
    this.setMovementAreaVisible(false);
    this.clearHover();

    if (replay.valid) {
      // Snap the replay camera *before* the first gate paint. Painting at the
      // finish-ceremony zoom (0.38) then viewing at Rest left huge shafts until
      // a pole turned green and `repaintGates` ran at the live zoom.
      if (this.replayPlaybackMode === 'simultaneous') {
        this.applyFleetCamera(true);
      } else {
        this.ensureReplayFollow();
      }

      this.applyReplayCursor(0);
      this.refreshZoomScaledGraphics();
      this.writeHandoffProbe();
    } else {
      this.applyReplayRace(replay.steps[0]?.seatId ?? 0, -1, 0);
    }

    this.syncCompetitorAlphas();
    this.drawRoute();
    this.mountReplayBar();
    this.syncHud();
    this.syncRaceHud();
  }

  private mountReplayBar(): void {
    const replay = this.replay;

    if (!replay) {
      return;
    }

    this.replayControls?.destroy();
    this.replayControls = mountReplayControls({
      state: {
        turnIndex: replay.stepIndex,
        turnCount: this.replayDomainLength(),
        moveProgress: replay.moveProgress,
        playing: replay.playing,
        playbackRate: this.replayPlaybackRate,
        seatCount: replay.seats.length,
        playbackMode: this.replayPlaybackMode,
        valid: replay.valid,
      },
      actions: {
        onBack: () => this.replayBack(),
        onForward: () => this.replayForward(),
        onTogglePlay: () => this.replayTogglePlay(),
        onSetPlaybackRate: (rate) => this.setReplayPlaybackRate(rate),
        onSetPlaybackMode: (mode) => this.setReplayPlaybackMode(mode),
        onClose: () => this.exitReplay(),
      },
    });
  }

  private syncReplayBar(): void {
    const replay = this.replay;

    if (!replay || !this.replayControls) {
      return;
    }

    this.replayControls.sync({
      turnIndex: replay.stepIndex,
      turnCount: this.replayDomainLength(),
      moveProgress: replay.moveProgress,
      playing: replay.playing,
      playbackRate: this.replayPlaybackRate,
      seatCount: replay.seats.length,
      playbackMode: this.replayPlaybackMode,
      valid: replay.valid,
    });
  }

  /** Number of steps in whichever domain is active: flat alternating steps for `turnByTurn`,
   * or rounds for `simultaneous` — what the replay bar counts against and what `playReplaySpan`
   * clamps to. */
  private replayDomainLength(): number {
    if (!this.replay) {
      return 0;
    }

    return this.replayPlaybackMode === 'simultaneous'
      ? this.replayRounds.length
      : this.replay.steps.length;
  }

  private clearReplayCurves(): void {
    this.replayCurves = [];
    this.replaySeatCurves.clear();
  }

  private replayCursor(): number {
    const replay = this.replay;

    if (!replay) {
      return 0;
    }

    const domainLength = this.replayDomainLength();

    if (replay.stepIndex >= domainLength) {
      return domainLength;
    }

    return replay.stepIndex + replay.moveProgress;
  }

  /**
   * Landing speed of the step/round before `index` in the active replay domain.
   * HUD and camera lerp from this so a seat change does not snap; hull travel
   * still uses the stepping boat's own from/to.
   */
  private replayPreviousLandingSpeed(index: number): number | undefined {
    const replay = this.replay;

    if (!replay) {
      return undefined;
    }

    return this.replayPlaybackMode === 'simultaneous'
      ? previousRoundLandingSpeed(replay.seats, this.replayRounds, index)
      : previousReplayLandingSpeed(replay.seats, replay.steps, index);
  }

  private ensureReplayFollow(): void {
    if (this.tracking) {
      this.cameras.main.setLerp(1, 1);
      return;
    }

    this.lockFollow(1);
  }

  /** How many of `seatId`'s own moves are done (through the one currently in flight) by
   * `stepIndex` in the flat replay order — a pure function of the cursor. */
  private replaySeatMovesThroughStep(stepIndex: number, seatId: number): number {
    const replay = this.replay;

    if (!replay) {
      return 0;
    }

    const upTo = Math.min(stepIndex, replay.steps.length - 1);
    let count = 0;

    for (let i = 0; i <= upTo; i += 1) {
      if (replay.steps[i]?.seatId === seatId) {
        count += 1;
      }
    }

    return count;
  }

  private applyReplayCursor(cursor: number): void {
    if (this.replayPlaybackMode === 'simultaneous') {
      this.applySimultaneousReplayCursor(cursor);
      return;
    }

    this.applyTurnByTurnReplayCursor(cursor);
  }

  private applyTurnByTurnReplayCursor(cursor: number): void {
    const replay = this.replay;

    if (!replay) {
      return;
    }

    const stepCount = replay.steps.length;

    if (stepCount === 0) {
      replay.stepIndex = 0;
      replay.moveProgress = 0;
      this.trail = [];
      this.drawTrail();
      this.applyReplayRace(0, -1, 0);
      return;
    }

    const clamped = clamp(cursor, 0, stepCount);
    const atEnd = clamped >= stepCount - 1e-9;
    const stepIndex = atEnd ? stepCount - 1 : Math.min(Math.floor(clamped), stepCount - 1);
    const timeT = atEnd ? 1 : clamped - stepIndex;
    const step = replay.steps[stepIndex]!;
    const seatEntry = replay.seats.find((entry) => entry.seatId === step.seatId);
    const fromBoat = seatEntry?.boats[step.localIndex] ?? seatEntry?.boats[0];
    const toBoat = seatEntry?.boats[step.localIndex + 1] ?? fromBoat;
    const fromSpeed = fromBoat?.speed ?? 0;
    const toSpeed = toBoat?.speed ?? fromSpeed;
    const pathT = travelProgressFromSpeeds(fromSpeed, toSpeed, timeT);
    const displaySpeed = replayDisplaySpeed(
      this.replayPreviousLandingSpeed(stepIndex),
      fromSpeed,
      toSpeed,
      timeT,
    );

    const previousStepIndex = Math.min(replay.stepIndex, stepCount - 1);
    const previousSeatId = replay.steps[previousStepIndex]?.seatId;

    replay.stepIndex = atEnd ? stepCount : stepIndex;
    replay.moveProgress = atEnd ? 0 : timeT;

    const activeView = this.views.find((view) => view.id === step.seatId);

    if (activeView) {
      this.hull = activeView.hull;
      this.hullGhost = activeView.hullGhost;
    }

    const curve = this.replayCurves[stepIndex];
    const point = curve?.getPoint(pathT, this.curvePoint);
    const tangent = curve?.getTangent(pathT, this.curveTangent);

    if (point) {
      this.hull.setPosition(point.x, point.y);
    }

    if (tangent && (tangent.x !== 0 || tangent.y !== 0)) {
      this.hull.setRotation(Math.atan2(tangent.y, tangent.x));
    }

    this.syncHullGhost();

    const pose = fromBoat ?? toBoat;

    if (pose) {
      this.boat = {
        ...pose,
        position: { x: this.hull.x, y: this.hull.y },
        heading:
          tangent && (tangent.x !== 0 || tangent.y !== 0)
            ? Math.atan2(tangent.y, tangent.x)
            : pose.heading,
        speed: displaySpeed,
      };
    }

    this.trailProgress = pathT;

    const activeDoneCount = this.replaySeatMovesThroughStep(stepIndex, step.seatId);
    this.trail = seatEntry?.paths.slice(0, activeDoneCount) ?? [];

    // Park every other seat's hull at its most recently completed pose, and reveal each
    // seat's own wake trail up through its moves so far — a pure function of the cursor, so
    // scrubbing backward never leaves a stale trail. Route (the full uncapped path) was set
    // once, in `beginReplayPlayback`, and is never re-sliced here.
    for (const view of this.views) {
      const entry = replay.seats.find((seat) => seat.seatId === view.id);

      if (!entry) {
        continue;
      }

      const doneCount = this.replaySeatMovesThroughStep(stepIndex, view.id);
      view.trail = entry.paths.slice(0, doneCount);

      if (view.id === step.seatId) {
        continue;
      }

      const parkedPose = entry.boats[doneCount] ?? entry.boats[0];

      if (parkedPose) {
        view.hull.setPosition(parkedPose.position.x, parkedPose.position.y);
        view.hull.setRotation(parkedPose.heading);
      }

      this.syncViewGhost(view);
    }

    this.applyReplayRace(step.seatId, step.localIndex, pathT);
    this.syncCompetitorAlphas();
    this.drawTrail();

    if (previousSeatId !== undefined && previousSeatId !== step.seatId && this.tracking) {
      this.beginSeatHandoffCamera();
    }
  }

  /**
   * `simultaneous` mode's cursor: every seat racing round `roundIndex` (see
   * `groupReplayStepsByRound`) animates its own move at once instead of one seat per flat
   * step. The camera frames the fleet (`applyFleetCamera` / per-frame
   * adaptive zoom), so there is no follow target and no seat-handoff glide here. One seat still drives the
   * shared, single-instance visuals — checkpoint pole colours and the telemetry HUD are one
   * set of objects in the world, not one per seat — using the lowest seat id racing this
   * round, the same seat `currentSeatId` reports for this mode.
   */
  private applySimultaneousReplayCursor(cursor: number): void {
    const replay = this.replay;

    if (!replay) {
      return;
    }

    const roundCount = this.replayRounds.length;

    if (roundCount === 0) {
      replay.stepIndex = 0;
      replay.moveProgress = 0;
      this.trail = [];
      this.drawTrail();
      this.applyReplayRace(0, -1, 0);
      return;
    }

    const clamped = clamp(cursor, 0, roundCount);
    const atEnd = clamped >= roundCount - 1e-9;
    const roundIndex = atEnd ? roundCount - 1 : Math.min(Math.floor(clamped), roundCount - 1);
    const timeT = atEnd ? 1 : clamped - roundIndex;
    const round = this.replayRounds[roundIndex] ?? [];
    const racingSeatIds = new Set(round.map((step) => step.seatId));
    const representativeSeatId = round[0]?.seatId ?? 0;

    replay.stepIndex = atEnd ? roundCount : roundIndex;
    replay.moveProgress = atEnd ? 0 : timeT;

    const representativeView = this.views.find((view) => view.id === representativeSeatId);

    if (representativeView) {
      this.hull = representativeView.hull;
      this.hullGhost = representativeView.hullGhost;
    }

    let representativePathT = 0;
    let representativeFromSpeed = 0;
    let representativeToSpeed = 0;

    for (const view of this.views) {
      const entry = replay.seats.find((seat) => seat.seatId === view.id);

      if (!entry) {
        continue;
      }

      if (racingSeatIds.has(view.id)) {
        const fromBoat = entry.boats[roundIndex] ?? entry.boats[0];
        const toBoat = entry.boats[roundIndex + 1] ?? fromBoat;
        const fromSpeed = fromBoat?.speed ?? 0;
        const toSpeed = toBoat?.speed ?? fromSpeed;
        const pathLengths = entry.paths.map((_, index) => {
          const seatCurve = this.replaySeatCurves.get(view.id)?.[index];
          return seatCurve?.getLength() ?? 0;
        });
        const pathLength = pathLengths[roundIndex] ?? 0;
        const incomingWorldSpeed = replayIncomingWorldSpeed(
          entry.boats,
          pathLengths,
          roundIndex,
        );
        const pathT = travelProgressFromJoin(
          fromSpeed,
          toSpeed,
          timeT,
          pathLength,
          incomingWorldSpeed,
        );
        this.replayWorldSpeeds.set(
          view.id,
          travelJoinWorldSpeed(
            fromSpeed,
            toSpeed,
            timeT,
            pathLength,
            incomingWorldSpeed,
          ),
        );
        const curve = this.replaySeatCurves.get(view.id)?.[roundIndex];
        const point = curve?.getPoint(pathT, this.curvePoint);
        const tangent = curve?.getTangent(pathT, this.curveTangent);

        if (point) {
          view.hull.setPosition(point.x, point.y);
        }

        if (tangent && (tangent.x !== 0 || tangent.y !== 0)) {
          view.hull.setRotation(Math.atan2(tangent.y, tangent.x));
        }

        view.trail = entry.paths.slice(0, roundIndex + 1);
        view.trailProgress = pathT;

        if (view.id === representativeSeatId) {
          representativePathT = pathT;
          representativeFromSpeed = fromSpeed;
          representativeToSpeed = toSpeed;
        }
      } else {
        // Finished before this round: parked at the final pose, same as turn-by-turn.
        const doneCount = entry.paths.length;
        const parkedPose = entry.boats[doneCount] ?? entry.boats[0];

        if (parkedPose) {
          view.hull.setPosition(parkedPose.position.x, parkedPose.position.y);
          view.hull.setRotation(parkedPose.heading);
        }

        view.trail = entry.paths.slice(0, doneCount);
        view.trailProgress = 1;
        this.replayWorldSpeeds.set(view.id, 0);
      }

      this.syncViewGhost(view);
    }

    this.syncHullGhost();

    const representativeEntry = replay.seats.find(
      (seat) => seat.seatId === representativeSeatId,
    );
    const representativeFromBoat =
      representativeEntry?.boats[roundIndex] ?? representativeEntry?.boats[0];
    const representativeToBoat =
      representativeEntry?.boats[roundIndex + 1] ?? representativeFromBoat;
    const pose = representativeFromBoat ?? representativeToBoat;

    if (pose && representativeView) {
      this.boat = {
        ...pose,
        position: { x: representativeView.hull.x, y: representativeView.hull.y },
        heading: representativeView.hull.rotation,
        speed: replayDisplaySpeed(
          this.replayPreviousLandingSpeed(roundIndex),
          representativeFromSpeed,
          representativeToSpeed,
          timeT,
        ),
      };
    }

    this.trailProgress = representativePathT;
    this.trail = representativeEntry?.paths.slice(0, roundIndex + 1) ?? [];

    this.applyReplayRace(representativeSeatId, roundIndex, representativePathT);
    this.syncCompetitorAlphas();
    this.drawTrail();
    this.writeReplayMotionProbe();
  }

  /**
   * Fleet camera: stop hull-follow and snap to a tight frame around every moving boat.
   * After that, `updatePlayCamera` eases zoom/centre toward the live pack so the round
   * starts close and opens out as the boats spread. Shared by `All at once` replay and a
   * live simultaneous round; only replay resets the wheel factor, because a live round must
   * not throw away the player's zoom every time the fleet moves.
   */
  private applyFleetCamera(resetUserZoom = false): void {
    const cam = this.cameras.main;
    cam.stopFollow();
    this.tracking = false;

    if (resetUserZoom) {
      this.userZoomFactor = 1;
    }

    const target = this.fleetCameraTarget();

    if (!target) {
      return;
    }

    this.autoZoom = target.autoZoom;
    this.fitZoom = target.autoZoom;
    cam.setZoom(target.appliedZoom);
    cam.centerOn(target.center.x, target.center.y);
  }

  /**
   * Every boat is moving this frame, so the camera fits the pack instead of following one
   * hull: a live simultaneous round, or `All at once` replay.
   */
  private fleetFraming(): boolean {
    return (
      this.revealing ||
      (this.stage === 'replay' && this.replayPlaybackMode === 'simultaneous')
    );
  }

  /** Hulls plus a look-ahead mark along each still-racing seat's current move. */
  private fleetFramePoints(): Vec2[] {
    const points: Vec2[] = this.views.map((view) => ({
      x: view.hull.x,
      y: view.hull.y,
    }));
    const lead = this.playCamera.lookAhead;

    for (const leg of this.fleetRoundDests()) {
      const view = this.views.find((entry) => entry.id === leg.seatId);

      if (!view) {
        continue;
      }

      // Grow look-ahead with this round's travel. A full-lead mark toward the new
      // dest at pathT=0 jumped the fleet frame every round (the previous dest was
      // the hull they just arrived at).
      points.push(
        lookAheadPoint(
          { x: view.hull.x, y: view.hull.y },
          leg.dest,
          lead * (view.trailProgress ?? 0),
        ),
      );
    }

    return points;
  }

  /** Where each boat moving this round is headed — live lock-ins, or the replay cursor's round. */
  private fleetRoundDests(): { seatId: number; dest: Vec2 }[] {
    if (this.revealing) {
      return this.roundTravel.flatMap((entry) => {
        const end = entry.path?.segments.at(-1)?.p3;

        return end ? [{ seatId: entry.seatId, dest: end }] : [];
      });
    }

    const replay = this.replay;

    if (!replay || this.replayRounds.length === 0) {
      return [];
    }

    const roundIndex = Math.min(replay.stepIndex, this.replayRounds.length - 1);

    return (this.replayRounds[roundIndex] ?? []).flatMap((step) => {
      const dest = replay.seats.find((entry) => entry.seatId === step.seatId)?.boats[
        roundIndex + 1
      ];

      return dest ? [{ seatId: step.seatId, dest: dest.position }] : [];
    });
  }

  /**
   * Fit the current fleet. Close end is Rest zoom so a packed start line is not
   * microscopically tight; the floor is `PLAY_MIN_ZOOM` so a spread-out field can
   * open as far as play zoom allows.
   */
  private fleetCameraTarget(): {
    autoZoom: number;
    appliedZoom: number;
    center: Vec2;
  } | null {
    const cam = this.cameras.main;
    const frame = fitPointsFrame(
      this.fleetFramePoints(),
      cam.width,
      cam.height,
      this.playCamera.paddingM + BOAT_LENGTH_M,
    );

    if (!frame) {
      return null;
    }

    const autoZoom = clamp(
      Math.min(frame.zoom, this.playCamera.restZoom),
      PLAY_MIN_ZOOM,
      MAX_ZOOM,
    );

    return {
      autoZoom,
      appliedZoom: appliedZoom(autoZoom, this.userZoomFactor),
      center: frame.center,
    };
  }

  private updateFleetCamera(dt: number): void {
    const target = this.fleetCameraTarget();

    if (!target) {
      return;
    }

    const cam = this.cameras.main;
    this.autoZoom = target.autoZoom;
    this.fitZoom = target.autoZoom;

    if (this.cameraControl.panning) {
      this.syncCameraHud();
      return;
    }

    const nextZoom = smoothToward(
      cam.zoom,
      target.appliedZoom,
      zoomSmoothness(this.playCamera, cam.zoom, target.appliedZoom),
      dt,
    );

    if (Math.abs(nextZoom - cam.zoom) > 1e-4) {
      cam.setZoom(nextZoom);
      this.refreshZoomChrome();
    }

    cam.centerOn(
      smoothToward(cam.midPoint.x, target.center.x, this.playCamera.smoothness, dt),
      smoothToward(cam.midPoint.y, target.center.y, this.playCamera.smoothness, dt),
    );
    this.syncCameraHud();
  }

  /**
   * Scores the turn the hull is on against the state that seat entered that turn with, so
   * poles turn green as they are crossed. `localIndex` of -1 is that seat's start line, before
   * any turn.
   */
  private applyReplayRace(seatId: number, localIndex: number, progress: number): void {
    const replay = this.replay;

    if (!replay) {
      return;
    }

    const seatEntry = replay.seats.find((entry) => entry.seatId === seatId);
    const before = seatEntry?.racesBefore[Math.max(localIndex, 0)] ?? createRaceState();
    const next =
      localIndex < 0
        ? createRaceState()
        : advanceRace(this.map, before, seatEntry?.paths[localIndex] ?? null, progress);

    if (sameRaceProgress(this.race, next)) {
      return;
    }

    this.race = next;
    this.repaintGates();
    this.syncPassedGateBadges();
    this.syncRaceHud();
  }

  private poseReplayTurn(index: number): void {
    this.applyReplayCursor(index);

    if (this.replayPlaybackMode !== 'simultaneous') {
      this.ensureReplayFollow();
    }

    this.syncReplayBar();
    this.syncHud();
    this.syncRaceHud();
  }

  private playReplaySpan(from: number, to: number, playing: boolean): void {
    const replay = this.replay;

    if (!replay?.valid) {
      return;
    }

    const stepCount = this.replayDomainLength();
    const start = clamp(from, 0, stepCount);
    const end = clamp(to, 0, stepCount);

    if (end <= start + 1e-6) {
      this.applyReplayCursor(end);
      replay.playing = false;
      this.syncReplayBar();
      this.syncHud();
      this.syncRaceHud();
      return;
    }

    this.stopMoveTween();
    replay.playing = playing;
    this.syncReplayBar();

    if (this.replayPlaybackMode !== 'simultaneous') {
      this.ensureReplayFollow();
    }

    let lastTurn = replay.stepIndex;
    let lastSpeedBucket = Math.round(this.boat.speed);

    this.moveTween = this.tweens.addCounter({
      from: start,
      to: end,
      duration: (end - start) * MOVE_DURATION_MS,
      ease: 'Linear',
      onUpdate: (tween) => {
        if (!this.replay) {
          return;
        }

        this.applyReplayCursor(tween.getValue() ?? end);

        if (this.replay.stepIndex !== lastTurn) {
          lastTurn = this.replay.stepIndex;
          this.syncReplayBar();
          this.syncRaceHud();
        }

        const speedBucket = Math.round(this.boat.speed);

        if (speedBucket !== lastSpeedBucket) {
          lastSpeedBucket = speedBucket;
          this.syncHud();
        }
      },
      onComplete: () => {
        this.moveTween = null;

        if (!this.replay) {
          return;
        }

        this.applyReplayCursor(end);
        this.replay.playing = false;
        this.syncReplayBar();
        this.syncHud();
        this.syncRaceHud();
      },
    });
    this.moveTween.setTimeScale(this.replayPlaybackRate);
  }

  private setReplayPlaybackRate(rate: ReplayPlaybackRate): void {
    this.replayPlaybackRate = rate;
    this.moveTween?.setTimeScale(rate);
    this.syncReplayBar();
  }

  /**
   * Switching modes mid-scrub always restarts playback at the beginning rather than trying to
   * carry the cursor across two different domains (flat steps vs. rounds) — simplest thing
   * that cannot leave a half-animated hull or a camera stuck mid-handoff.
   */
  private setReplayPlaybackMode(mode: ReplayPlaybackMode): void {
    if (this.replayPlaybackMode === mode) {
      return;
    }

    this.replayPlaybackMode = mode;
    const replay = this.replay;

    if (!replay) {
      return;
    }

    this.stopMoveTween();
    replay.playing = false;
    this.finishCeremonyZoom = null;

    this.applyReplayCursor(0);

    if (mode === 'simultaneous') {
      this.applyFleetCamera(true);
    } else {
      this.tracking = true;
      this.ensureReplayFollow();
    }

    this.refreshZoomScaledGraphics();
    this.writeHandoffProbe();
    this.syncReplayBar();
    this.syncHud();
    this.syncRaceHud();
  }

  private replayBack(): void {
    const replay = this.replay;

    if (!replay?.valid) {
      return;
    }

    this.stopMoveTween();
    replay.playing = false;

    const cursor = this.replayCursor();
    const previous =
      cursor - Math.floor(cursor) > 1e-4
        ? Math.floor(cursor)
        : Math.max(0, Math.floor(cursor) - 1);

    this.poseReplayTurn(previous);
  }

  private replayForward(): void {
    const replay = this.replay;

    if (!replay?.valid) {
      return;
    }

    const cursor = this.replayCursor();
    this.playReplaySpan(cursor, Math.floor(cursor) + 1, false);
  }

  private replayTogglePlay(): void {
    const replay = this.replay;
    const domainLength = this.replayDomainLength();

    if (!replay?.valid || domainLength === 0) {
      return;
    }

    if (replay.playing) {
      this.stopMoveTween();
      replay.playing = false;
      this.syncReplayBar();
      return;
    }

    const cursor = this.replayCursor();
    const from = cursor >= domainLength ? 0 : cursor;

    if (from === 0 && cursor >= domainLength) {
      this.applyReplayCursor(0);
    }

    this.playReplaySpan(from, domainLength, true);
  }

  private exitReplay(): void {
    const returnToFinished = this.replay?.returnToFinished ?? false;
    this.stopMoveTween();
    if (returnToFinished) {
      this.restoreReplayHull();
    }
    this.replayControls?.destroy();
    this.replayControls = null;
    this.replay = null;
    this.clearReplayCurves();

    if (returnToFinished) {
      this.stage = 'finished';
      if (this.finishedRace) {
        this.race = this.finishedRace;
        this.finishedRace = null;
        this.repaintGates();
        this.syncPassedGateBadges();
      }
      if (this.finishedViewTrails) {
        for (const view of this.views) {
          const trail = this.finishedViewTrails.get(view.id);
          if (trail) {
            view.trail = trail;
          }
        }
        this.finishedViewTrails = null;
      }
      this.drawRoute();
      this.drawTrail();
      this.syncCompetitorAlphas();
      this.syncHud();
      this.syncRaceHud();
      this.openFinishPopup();
      return;
    }

    this.scene.restart(this.relaunchData());
  }

  private handleEscape(): boolean {
    if (this.pauseMenu) {
      this.closePause();
      return true;
    }

    if (this.testPlay) {
      this.exitToEditor();
      return true;
    }

    this.openPause();
    return true;
  }

  private openPause(): void {
    if (this.pauseMenu || this.leaving) {
      return;
    }

    this.scene.pause();
    this.pauseMenu = mountPauseMenu({
      scene: 'play',
      racing: this.stage === 'racing',
      onResume: () => this.closePause(),
      onRestart: () => this.restartFromPause(),
      onEditor: () => this.leaveFromPause('editor'),
      onBoats: () => this.leaveFromPause('boats'),
      onMainMenu: () => this.leaveFromPause('start'),
    });
  }

  private restartFromPause(): void {
    if (this.leaving) {
      return;
    }

    this.leaving = true;
    this.destroyPause();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }

    this.scene.restart(this.relaunchData());
  }

  private closePause(): void {
    this.destroyPause();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }
  }

  private destroyPause(): void {
    this.pauseMenu?.destroy();
    this.pauseMenu = null;
  }

  private leaveFromPause(next: 'start' | 'editor' | 'boats'): void {
    if (this.leaving) {
      return;
    }

    this.leaving = true;
    this.destroyPause();
    clearSceneSearch();

    if (this.scene.isPaused()) {
      this.scene.resume();
    }

    try {
      this.teardownPlayVisuals();
    } catch {
      this.grading = null;
      this.lighting = null;
    }

    if (next === 'editor') {
      this.scene.start('EditorScene', {
        map: cloneMapDocument(this.map),
        fileName: this.mapId ? `${this.mapId}.json` : undefined,
      });
      return;
    }

    if (next === 'boats') {
      this.scene.start('BoatScene', {});
      return;
    }

    this.scene.start('StartScene');
  }

  /**
   * Hands the document back rather than letting the editor reload its autosave, so an unsaved
   * edit survives a test-play. A clone, because the scene keeps rendering until the swap lands.
   */
  private exitToEditor(): void {
    if (this.leaving) {
      return;
    }

    this.leaving = true;
    try {
      this.teardownPlayVisuals();
    } catch {
      this.grading = null;
      this.lighting = null;
    }

    const origin = this.testOrigin ?? 'editor';
    const returned = testPlayReturn(
      origin,
      this.map,
      this.testBoat ?? createSkiffBoat(),
      this.boatFile,
      this.boatBaseline,
    );
    this.scene.start(returned.scene, returned.data);
  }

  /** Grade and sun lighting hold WebGL visuals; safe to call twice. */
  private teardownPlayVisuals(): void {
    this.grading?.destroy();
    this.grading = null;
    this.lighting?.destroy();
    this.lighting = null;
  }

  private setCurve(kind: CurveKind): void {
    this.curveKind = kind;
    this.drawHoverAids();
    this.syncHud();
  }

  private setLine(line: keyof DebugLines, next: boolean): void {
    this.lines = { ...this.lines, [line]: next };
    this.drawTrail();
    if (this.stage === 'racing') {
      this.drawMovementArea();
    }
    this.drawHoverAids();
    this.syncHud();
  }

  private setWindFeel(patch: Partial<WindFeel>): void {
    this.windFeel = clampWindFeel({ ...this.windFeel, ...patch });
    if (this.stage === 'racing') {
      this.drawMovementArea();
    }
    this.refreshActiveHover();
    this.syncHud();
  }

  private setPreviewGates(next: boolean): void {
    this.previewGates = next;
    this.repaintGates();
    this.syncHud();
  }

  /** Gate preview: the wall the aim would meet, not the rebound or the over-grass dash. */
  private hoverPreviewPath(): MovePath | null {
    if (this.hoverPlan?.clipped) {
      return this.hoverPlan.impactPath ?? this.hoverPlan.path;
    }

    if (!this.hoverPoint) {
      return this.hoverPlan?.path ?? null;
    }

    return (
      projectMovePath(this.boat, this.hoverPoint, this.curveKind, this.turnTax) ??
      this.hoverPlan?.path ??
      null
    );
  }

  private setStickyEdges(next: boolean): void {
    this.stickyEdges = next;
    this.syncHud();
  }

  private stepStickyOverflow(direction: -1 | 1): void {
    this.setStickyOverflow(
      this.stickyOverflowPx + direction * STICK_OVERFLOW_PX_STEP,
    );
  }

  /** Uncapped on purpose while movement feel is being tuned; only nonsense is rejected. */
  private setStickyOverflow(px: number): void {
    if (!Number.isFinite(px)) {
      return;
    }

    this.stickyOverflowPx = Math.max(STICK_OVERFLOW_PX_MIN, px);
    this.syncHud();
  }

  private stepNearMiss(direction: -1 | 1): void {
    this.setNearMiss(this.nearMissPx + direction * NEAR_MISS_PX_STEP);
  }

  private setNearMiss(px: number): void {
    if (!Number.isFinite(px)) {
      return;
    }

    this.nearMissPx = clamp(px, NEAR_MISS_PX_MIN, NEAR_MISS_PX_MAX);
    this.syncHud();
  }

  private hydrateLookFromMap(): void {
    this.cancelSunBake();
    this.aimChrome = cloneAimChrome(this.map.look.aimChrome);
  }

  private applyPlayDebugDefaults(doc: DebugDefaultsDocument): void {
    this.curveKind = doc.curve;
    this.movementRadius = clamp(
      metersToPixels(doc.radiusMeters),
      MOVEMENT_RADIUS_MIN,
      MOVEMENT_RADIUS_MAX,
    );
    this.crashHarshness = clamp(
      doc.crashHarshness,
      CRASH_HARSHNESS_MIN,
      CRASH_HARSHNESS_MAX,
    );
    this.turnTax = clamp(doc.turnTax, TURN_TAX_MIN, TURN_TAX_MAX);
    this.windFeel = clampWindFeel({
      ...DEFAULT_WIND_FEEL,
      ...doc.wind,
      enabled: true,
    });
    this.lines = { ...doc.lines };
    this.previewGates = doc.previewGates;
    this.stickyEdges = doc.stickyEdges;
    this.stickyOverflowPx = Math.max(STICK_OVERFLOW_PX_MIN, doc.stickyOverflowPx);
    this.nearMissPx = clamp(doc.nearMissPx, NEAR_MISS_PX_MIN, NEAR_MISS_PX_MAX);
    this.trailLengthM = clamp(
      doc.trailLengthMeters,
      TRAIL_LENGTH_MIN_M,
      TRAIL_LENGTH_MAX_M,
    );
    this.trailSpeedGain = clamp(
      doc.trailSpeedGain,
      TRAIL_SPEED_GAIN_MIN,
      TRAIL_SPEED_GAIN_MAX,
    );
    this.playCamera = { ...doc.camera };
    this.refreshAimChromeDraw();
  }

  private applyAimChromeLive(patch: Partial<AimChrome>): void {
    updateLook(this.map.look, {
      aimChrome: cloneAimChrome({
        movement: patch.movement ?? this.map.look.aimChrome.movement,
        prediction: patch.prediction ?? this.map.look.aimChrome.prediction,
        moveLine: patch.moveLine ?? this.map.look.aimChrome.moveLine,
        dest: patch.dest ?? this.map.look.aimChrome.dest,
      }),
    });
    this.aimChrome = cloneAimChrome(this.map.look.aimChrome);
    this.refreshAimChromeDraw();
    this.syncHud();
  }

  private refreshAimChromeDraw(): void {
    if (!this.areaGraphics) {
      return;
    }

    if (this.stage === 'racing') {
      this.drawMovementArea();
      this.drawHoverAids();

      if (this.hoverPoint) {
        this.placeHoverMarker(this.hoverPoint, !this.hoverCosts(this.hoverPlan));
      }
    }

    if (this.stage === 'aiming') {
      this.drawAimLine(this.hull.rotation);
    }
  }

  private async saveLookToMap() {
    const request = {
      liveMap: this.map,
      mapId: this.mapId,
      testPlay: this.testPlay,
      repoFile: this.repoFile,
    };
    const confirmed = await confirmDialog({
      title: 'Save to Map',
      message: `Write look settings to ${describeSaveLookTarget(request)}?`,
      detail: saveLookSummary(),
      confirmLabel: 'Save to Map',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) {
      return 'cancelled';
    }

    return saveLookToMap(request);
  }

  private setWaterlineHintAlpha(next: number): void {
    const alpha = clamp(next, 0, 1);

    if (alpha === this.map.look.waterlineHintAlpha) {
      return;
    }

    updateLook(this.map.look, { waterlineHintAlpha: alpha });
    this.drawMapLayers('chrome');
    this.syncHud();
  }

  private setUnderCapRimAlpha(next: number): void {
    const alpha = clamp(next, 0, 1);

    if (alpha === this.map.look.underCapRimAlpha) {
      return;
    }

    updateLook(this.map.look, { underCapRimAlpha: alpha });
    this.refreshAimChromeDraw();
    this.syncHud();
  }

  private setCapFlockEnabled(next: boolean): void {
    if (next === this.map.look.capFlockEnabled) {
      return;
    }

    updateLook(this.map.look, { capFlockEnabled: next });
    this.drawMapLayers();
    this.syncHud();
  }

  private setCapFlockAmount(next: number): void {
    const amount = clamp(next, 0, 1);

    if (amount === this.map.look.capFlockAmount) {
      return;
    }

    updateLook(this.map.look, { capFlockAmount: amount });
    this.drawMapLayers();
    this.syncHud();
  }

  private applyLookLive(patch: Partial<MapLook>): void {
    updateLook(this.map.look, patch);

    if (patch.aimChrome) {
      this.aimChrome = cloneAimChrome(this.map.look.aimChrome);
      this.refreshAimChromeDraw();
    }

    this.applySheetLook();
    this.syncHud();
  }

  private applySunLive(sun: Partial<SunSettings>): void {
    updateEnvironment(this.map.environment, { sun });

    if (this.sunBakeFrame !== null) {
      return;
    }

    this.sunBakeFrame = requestAnimationFrame(() => {
      this.sunBakeFrame = null;
      this.refreshSunLook();
    });
  }

  private commitSunEdit(): void {
    this.cancelSunBake();
    this.refreshSunLook();
  }

  private cancelSunBake(): void {
    if (this.sunBakeFrame !== null) {
      cancelAnimationFrame(this.sunBakeFrame);
      this.sunBakeFrame = null;
    }
  }

  private refreshSunLook(): void {
    this.lighting?.rebuild(this.map, this.columnVisibleIds);
    this.drawMapLayers();
    this.syncHud();
  }

  private applySheetLook(): void {
    this.redrawGrid();
  }

  private setShowRoute(next: boolean): void {
    this.showRoute = next;
    this.drawRoute();
    this.syncHud();
  }

  private stepRadius(direction: -1 | 1): void {
    if (this.reach) {
      return;
    }

    const next = clamp(
      this.movementRadius + direction * MOVEMENT_RADIUS_STEP,
      MOVEMENT_RADIUS_MIN,
      MOVEMENT_RADIUS_MAX,
    );

    if (next === this.movementRadius) {
      return;
    }

    this.movementRadius = next;
    const seat = this.seatHulls[this.liveSeatId()];

    if (seat && seat.reach === null) {
      seat.movementRadius = next;
    }

    this.drawMovementArea();
    this.refreshActiveHover();
    this.syncHud();
  }

  private setCrashHarshness(next: number): void {
    const harshness = clamp(next, CRASH_HARSHNESS_MIN, CRASH_HARSHNESS_MAX);

    if (harshness === this.crashHarshness) {
      return;
    }

    this.crashHarshness = harshness;
    this.applyLastCrashHarshness();
    this.refreshActiveHover();
    this.syncHud();
  }

  private setTurnTax(next: number): void {
    const tax = clamp(next, TURN_TAX_MIN, TURN_TAX_MAX);

    if (tax === this.turnTax) {
      return;
    }

    this.turnTax = tax;
    this.refreshActiveHover();
    this.syncHud();
  }

  private applyLastCrashHarshness(): void {
    if (!this.lastCrashPlan || this.stage !== 'racing') {
      return;
    }

    const planned = this.planPoint(
      this.lastCrashPlan.boat,
      this.lastCrashPlan.dest,
      this.lastCrashPlan.radius,
    );

    if (!planned?.clipped) {
      return;
    }

    this.boat = planned.boat;

    const last = this.moveSpeeds.length - 1;

    if (last >= 0) {
      this.moveSpeeds[last] = this.boat.speed;
    }

    if (this.match) {
      const active = activeCompetitor(this.match);
      this.match = {
        ...this.match,
        competitors: this.match.competitors.map((seat) =>
          seat.id === active.id
            ? {
                ...seat,
                boat: planned.boat,
                moveSpeeds:
                  last >= 0
                    ? seat.moveSpeeds.map((speed, index) =>
                        index === last ? planned.boat.speed : speed,
                      )
                    : seat.moveSpeeds,
              }
            : seat,
        ),
      };
    }

    if (this.phase === 'planning') {
      this.hull.setPosition(this.boat.position.x, this.boat.position.y);
      this.hull.setRotation(this.boat.heading);
      this.syncHullGhost();
      this.drawMovementArea();
    }
  }

  private refreshActiveHover(): void {
    if (!this.hoverPoint) {
      return;
    }

    const effective = this.rimStick(this.hoverPoint);

    if (effective !== null) {
      this.setHover(effective);
    } else {
      this.clearHover();
    }
  }

  private stepTrailLength(direction: -1 | 1): void {
    const next = clamp(
      this.trailLengthM + direction * TRAIL_LENGTH_STEP_M,
      TRAIL_LENGTH_MIN_M,
      TRAIL_LENGTH_MAX_M,
    );

    if (next === this.trailLengthM) {
      return;
    }

    this.trailLengthM = next;
    this.drawTrail();
    this.syncHud();
  }

  private shouldAutoCamera(): boolean {
    // Fleet framing (`updateFleetCamera`) replaces the single-seat play-camera target while
    // every boat moves at once. Wheel/pinch still write `userZoomFactor` against that fleet
    // fit (see `onWheel` / `applyPinch`).
    if (this.fleetFraming()) {
      return false;
    }

    return (
      this.playCamera.enabled &&
      (this.stage === 'placing' ||
        this.stage === 'aiming' ||
        this.stage === 'racing' ||
        this.stage === 'replay')
    );
  }

  /** Place/aim keep Rest framing on the start view — the sprite is a cursor, not a follow target. */
  private cameraFocusHull(): Vec2 {
    if (this.stage === 'placing') {
      return startAreaCentroid(this.map);
    }

    if (this.stage === 'aiming') {
      return { x: this.boat.position.x, y: this.boat.position.y };
    }

    return { x: this.hull.x, y: this.hull.y };
  }

  private cameraBoat(): BoatState {
    const hull = this.cameraFocusHull();
    const speed =
      this.stage === 'placing' || this.stage === 'aiming'
        ? 0
        : this.phase === 'resolving' && this.moveFromSpeed !== null
          ? replaySpeed(this.moveFromSpeed, this.boat.speed, this.trailProgress)
          : this.boat.speed;

    return {
      position: hull,
      heading:
        this.stage === 'placing' || this.stage === 'aiming'
          ? this.boat.heading
          : this.hull.rotation,
      speed,
    };
  }

  private cameraPlanningRadius(): number {
    const replay = this.replay;

    if (this.stage !== 'replay' || !replay || replay.steps.length === 0) {
      return this.effectiveMovementRadius();
    }

    const stepIndex = Math.min(
      replay.stepIndex >= replay.steps.length ? replay.steps.length - 1 : replay.stepIndex,
      replay.steps.length - 1,
    );
    const step = replay.steps[stepIndex];
    const seatEntry = step ? replay.seats.find((entry) => entry.seatId === step.seatId) : undefined;
    const radius = step ? seatEntry?.moves[step.localIndex]?.radius : undefined;

    return radius ?? this.effectiveMovementRadius();
  }

  private playCameraTarget() {
    const cam = this.cameras.main;
    const boat = this.cameraBoat();

    return computePlayCamera({
      hull: { x: boat.position.x, y: boat.position.y },
      area: this.placedArea(
        boat,
        this.reach ? this.movementRadius : this.cameraPlanningRadius(),
      ).area,
      speed: boat.speed,
      viewportWidth: cam.width,
      viewportHeight: cam.height,
      settings: this.playCamera,
      userZoomFactor: this.userZoomFactor,
      aim: this.cameraAim.point,
      aimWeight: this.cameraAim.weight,
      lookAheadGate: this.lookAheadGate,
      placing: this.stage === 'placing',
    });
  }

  private snapAutoZoom(): void {
    if (!this.shouldAutoCamera() || !this.hull) {
      return;
    }

    const cam = this.cameras.main;
    const target = this.playCameraTarget();
    this.autoZoom = target.autoZoom;
    this.fitZoom = target.fitZoom;
    this.speedZoomValue = target.speedZoom;
    this.cameraLimit = target.limit;
    cam.setZoom(target.appliedZoom);

    if (this.tracking) {
      cam.setFollowOffset(target.followOffset.x, target.followOffset.y);
    }
  }

  private updatePlayCamera(): void {
    const cam = this.cameras.main;
    const dt = this.game.loop.delta / 1000;

    if (this.finishCeremonyZoom !== null && this.stage === 'finished') {
      const nextZoom = smoothToward(cam.zoom, this.finishCeremonyZoom, 3.2, dt);

      if (Math.abs(nextZoom - cam.zoom) > 1e-4) {
        cam.setZoom(nextZoom);
        this.refreshZoomChrome();
      }

      // Ease toward the fleet centroid so the pull-back frames every hull.
      if (this.views.length > 0) {
        let sx = 0;
        let sy = 0;

        for (const view of this.views) {
          sx += view.hull.x;
          sy += view.hull.y;
        }

        const cx = sx / this.views.length;
        const cy = sy / this.views.length;
        cam.centerOn(
          smoothToward(cam.midPoint.x, cx, 3.2, dt),
          smoothToward(cam.midPoint.y, cy, 3.2, dt),
        );
      }

      this.syncCameraHud();
      return;
    }

    if (this.fleetFraming()) {
      this.updateFleetCamera(dt);
      return;
    }

    const target = this.playCameraTarget();
    this.autoZoom = target.autoZoom;
    this.fitZoom = target.fitZoom;
    this.speedZoomValue = target.speedZoom;
    this.cameraLimit = target.limit;
    const offsetX =
      this.shouldAutoCamera() && this.tracking ? target.followOffset.x : 0;
    const offsetY =
      this.shouldAutoCamera() && this.tracking ? target.followOffset.y : 0;

    if (this.tracking && !this.handoffActive) {
      cam.setFollowOffset(
        smoothToward(
          cam.followOffset.x,
          offsetX,
          this.playCamera.smoothness,
          dt,
        ),
        smoothToward(
          cam.followOffset.y,
          offsetY,
          this.playCamera.smoothness,
          dt,
        ),
      );
    }

    if (!this.shouldAutoCamera() || this.handoffActive) {
      return;
    }

    const nextZoom = smoothToward(
      cam.zoom,
      target.appliedZoom,
      zoomSmoothness(this.playCamera, cam.zoom, target.appliedZoom),
      dt,
    );

    if (Math.abs(nextZoom - cam.zoom) > 1e-4) {
      cam.setZoom(nextZoom);
      this.refreshZoomChrome();
    }

    this.syncCameraHud();
  }

  private syncCameraHud(): void {
    const now = this.game.loop.now;
    const key = [
      this.autoZoom.toFixed(2),
      this.fitZoom.toFixed(2),
      this.speedZoomValue.toFixed(2),
      this.cameraLimit,
      this.userZoomFactor.toFixed(2),
      this.cameras.main.zoom.toFixed(2),
    ].join(':');

    if (key === this.lastHudCameraKey) {
      return;
    }

    if (now - this.lastHudCameraAt < 200) {
      return;
    }

    this.lastHudCameraKey = key;
    this.lastHudCameraAt = now;
    this.syncHud();
  }

  private setCameraEnabled(next: boolean): void {
    if (next === this.playCamera.enabled) {
      return;
    }

    if (next) {
      this.userZoomFactor = userFactorForZoom(
        this.playCameraTarget().autoZoom,
        this.cameras.main.zoom,
      );
    }

    this.playCamera = { ...this.playCamera, enabled: next };
    this.syncHud();
  }

  private setCameraSlider(key: PlayCameraSliderKey, next: number): void {
    const patched = patchPlayCamera(this.playCamera, key, next);

    if (patched === this.playCamera) {
      return;
    }

    this.playCamera = patched;
    this.syncHud();
  }

  /**
   * Dest chrome updates first; camera aim follows. Planning holds the last lean
   * after dest clears. Resolve / replay / place fade so hold does not stick
   * through the hull tween.
   */
  private stepPlayCameraAim(delta: number): void {
    if (this.handoffActive) {
      this.cameraAim = emptyCameraAim();
      return;
    }

    if (this.handoffAimGate) {
      const pointer = this.input.activePointer;
      const moved = Math.hypot(
        pointer.x - this.handoffAimGate.x,
        pointer.y - this.handoffAimGate.y,
      );

      if (moved < 8) {
        this.cameraAim = emptyCameraAim();
        return;
      }

      this.handoffAimGate = null;
    }

    const planning = this.stage === 'racing' && this.phase === 'planning';

    this.cameraAim = stepCameraAim(
      this.cameraAim,
      planning ? this.hoverPoint : null,
      planning ? 'hold' : 'fade',
      this.playCamera.smoothness,
      delta / 1000,
    );
  }

  /**
   * Speed look-ahead toward the circle: on for the whole race / replay at the
   * Look-ahead slider value (scaled by speed). Off only during a hot-seat handoff
   * glide so the view does not yank toward the next seat's circle mid-tween.
   */
  private stepLookAheadGateState(delta: number): void {
    if (this.handoffActive) {
      this.lookAheadGate = 0;
      return;
    }

    const want = this.stage === 'replay' || this.stage === 'racing' ? 1 : 0;

    this.lookAheadGate = stepLookAheadGate(
      this.lookAheadGate,
      want,
      this.playCamera.smoothness,
      delta / 1000,
    );
  }

  private stepTrailSpeedGain(direction: -1 | 1): void {
    const next = clamp(
      Math.round(
        (this.trailSpeedGain + direction * TRAIL_SPEED_GAIN_STEP) * 10,
      ) / 10,
      TRAIL_SPEED_GAIN_MIN,
      TRAIL_SPEED_GAIN_MAX,
    );

    if (next === this.trailSpeedGain) {
      return;
    }

    this.trailSpeedGain = next;
    this.drawTrail();
    this.syncHud();
  }

  private trailKeepM(): number {
    return trailKeepLengthM(
      this.trailLengthM,
      this.trailSpeedGain,
      this.boat.speed,
    );
  }

  private trailHistoryLimit(): number {
    return Math.max(
      TRAIL_LIMIT,
      Math.ceil(this.trailKeepM() / pixelsToMeters(MOVEMENT_RADIUS_MIN)) + 1,
    );
  }

  private setHover(point: Vec2): void {
    const planned = this.planPoint(this.boat, point);

    if (!planned) {
      if (this.hoverPoint) {
        return;
      }

      this.clearHover();
      return;
    }

    this.hoverPoint = point;
    this.hoverPlan = planned;
    this.placeHoverMarker(point, !this.hoverCosts(planned));
    this.drawHoverAids();
    this.syncHud();
  }

  /** Screen-fixed dot on the aimed point; X when the move costs this boat (shore or a paid hit). */
  private placeHoverMarker(point: Vec2, legal: boolean): void {
    paintHoverMarker(
      this.hoverMarker,
      legal,
      rgb24(this.aimChrome.dest),
      this.aimChrome.dest.a,
    );
    this.hoverMarker.setPosition(point.x, point.y);
    this.hoverMarker.setScale(1 / this.cameras.main.zoom);
    this.hoverMarker.setVisible(true);
  }

  private clearHover(): void {
    this.hoverPoint = null;
    this.hoverPlan = null;
    this.hoverMarker.setVisible(false);
    this.clearMoveLineChrome();
    this.predictionGraphics.clear();
    this.predictionOverlandGraphics.clear();
    this.repaintGates();
    this.syncHud();
  }

  private clearMoveLineChrome(): void {
    this.moveLineGraphics.clear();
    this.moveLineOverlandGraphics.clear();
    this.contactGraphics?.clear();
  }

  /**
   * The aimed move costs this boat something: it meets the shore, or it causes a boat hit that is
   * not a free nudge. A nudge still bends the line (the shove is real) but keeps the dot.
   */
  private hoverCosts(plan: CommitResult | null): boolean {
    if (!plan) {
      return false;
    }

    const id = this.match?.activeId ?? 0;

    return (
      plan.crash !== null ||
      plan.boatContacts.some(
        (hit) => hit.severity !== 'nudge' && (hit.faultId === id || hit.faultId === null),
      )
    );
  }

  /**
   * At the first hull contact on the aimed move: this boat's contact capsule where it is at that
   * instant, the other boat's capsule, and a dot on the contact point coloured by what the hit
   * would cost. The move line is the hull's centre, so without this the contact reads as far
   * from the other boat when it is really the bow that touches.
   */
  private drawContactChrome(point: Vec2): void {
    const gfx = this.contactGraphics;

    if (!gfx) {
      return;
    }

    gfx.clear();

    const plan = this.hoverPlan;
    const match = this.match;

    if (!plan || !match || plan.boatContacts.length === 0) {
      return;
    }

    const hit = plan.boatContacts.reduce((first, next) => (next.at < first.at ? next : first));
    const aimed = projectMovePath(this.boat, point, 'arc', this.turnTax);

    if (!aimed) {
      return;
    }

    const id = match.activeId;
    const sizes = this.seatHullSizes();
    const zoom = this.cameras.main.zoom;
    const table = arcLengthTable(aimed);
    const center = pointAtProgress(table, hit.at);
    const heading = headingAtProgress(table, hit.at) ?? this.boat.heading;
    const otherId = hit.fromId === id ? hit.intoId : hit.fromId;
    const other = match.competitors.find((seat) => seat.id === otherId)?.boat;
    const paid = hit.severity !== 'nudge' && (hit.faultId === id || hit.faultId === null);
    const color = hit.rearEnd
      ? CONTACT_TIER_COLORS.hard
      : paid
        ? CONTACT_TIER_COLORS[hit.severity]
        : CONTACT_TIER_COLORS.nudge;

    gfx.lineStyle(screenToWorld(1.5, zoom), color, 0.9);
    this.strokeCapsule(gfx, center, heading, sizes.get(id) ?? DEFAULT_HULL);

    if (other) {
      gfx.lineStyle(screenToWorld(1.5, zoom), color, 0.55);
      this.strokeCapsule(gfx, other.position, other.heading, sizes.get(otherId) ?? DEFAULT_HULL);
    }

    gfx.fillStyle(color, 1);
    gfx.fillCircle(hit.point.x, hit.point.y, screenToWorld(5, zoom));
  }

  /** A contact capsule outline: the shape the collision sweep uses, not the drawn hull. */
  private strokeCapsule(
    gfx: Phaser.GameObjects.Graphics,
    center: Vec2,
    heading: number,
    dimensions: HullDimensions,
  ): void {
    const shape = contactShape(dimensions);
    const ux = Math.cos(heading) * shape.halfSpine;
    const uy = Math.sin(heading) * shape.halfSpine;

    gfx.beginPath();
    gfx.arc(center.x + ux, center.y + uy, shape.radius, heading - Math.PI / 2, heading + Math.PI / 2, false);
    gfx.arc(
      center.x - ux,
      center.y - uy,
      shape.radius,
      heading + Math.PI / 2,
      heading + (3 * Math.PI) / 2,
      false,
    );
    gfx.closePath();
    gfx.strokePath();
  }

  private drawHoverMoveLine(point: Vec2): void {
    const zoom = this.cameras.main.zoom;
    const width = screenToWorld(2, zoom);
    const color = rgb24(this.aimChrome.moveLine);
    const alpha = this.aimChrome.moveLine.a;

    if (this.hoverPlan?.clipped) {
      const path = this.hoverPreviewPath();
      const chrome = path ? clipMovePathAheadOfBow(path, this.boat, this.bowOffsetPx) : [];

      if (chrome.length >= 2) {
        this.strokeMoveLineRuns(this.moveLineGraphics, [chrome], width, color, alpha, false);
      }

      return;
    }

    const path = projectMovePath(this.boat, point, this.curveKind, this.turnTax);
    const chrome = path ? clipMovePathAheadOfBow(path, this.boat, this.bowOffsetPx) : [];

    if (chrome.length < 2) {
      return;
    }

    const split = splitPolylineByLand(chrome, (sample) =>
      pointUnderColumnCap(this.map, sample),
    );

    this.strokeMoveLineRuns(this.moveLineGraphics, split.water, width, color, alpha, false);
    this.strokeMoveLineRuns(
      this.moveLineOverlandGraphics,
      split.land,
      width,
      color,
      alpha,
      true,
    );
  }

  private strokeMoveLineRuns(
    gfx: Phaser.GameObjects.Graphics,
    runs: readonly Vec2[][],
    width: number,
    color: number,
    alpha: number,
    dashed: boolean,
  ): void {
    if (runs.length === 0) {
      return;
    }

    gfx.lineStyle(width, color, alpha);

    if (!dashed) {
      for (const run of runs) {
        const first = run[0];

        if (!first || run.length < 2) {
          continue;
        }

        gfx.beginPath();
        gfx.moveTo(first.x, first.y);

        for (let index = 1; index < run.length; index += 1) {
          const next = run[index]!;
          gfx.lineTo(next.x, next.y);
        }

        gfx.strokePath();
      }

      return;
    }

    const zoom = this.cameras.main.zoom;
    const dash = GATE_DASH_PX * screenToWorld(1, zoom);
    const gap = GATE_GAP_PX * screenToWorld(1, zoom);

    for (const run of runs) {
      for (const segment of dashedPolyline(run, false, dash, gap)) {
        gfx.lineBetween(segment.a.x, segment.a.y, segment.b.x, segment.b.y);
      }
    }
  }

  private drawHoverAids(): void {
    this.clearMoveLineChrome();
    this.predictionGraphics.clear();
    this.predictionOverlandGraphics.clear();

    const point = this.hoverPoint;

    if (!point || this.stage !== 'racing' || this.phase !== 'planning') {
      this.repaintGates();
      return;
    }

    if (this.lines.moveLine) {
      this.drawHoverMoveLine(point);
    }

    this.drawContactChrome(point);
    this.repaintGates();

    if (this.lines.prediction && !this.hoverCosts(this.hoverPlan)) {
      const nextBoat =
        this.hoverPlan?.boat ??
        resolveMove(
          this.boat,
          point,
          this.turnTax,
          seatCommitArgs(this.boat, point, this.turnFeel()).keptSpeed,
        );
      const placed = this.placedArea(nextBoat);
      const { area, calm, offset, view } = placed;
      const zoom = this.cameras.main.zoom;
      const color = rgb24(this.aimChrome.prediction);
      const reach = view.reach;

      if (this.lines.windGhost && lengthSq(offset) > 1e-6) {
        if (reach) {
          paintReachGhost(
            this.predictionGraphics,
            calm.center,
            nextBoat.heading,
            reach,
            color,
            zoom,
          );
        } else {
          paintWindGhostCircle(this.predictionGraphics, calm.center, calm.radius, color, zoom);
        }
      }

      if (reach) {
        const runs = paintReachArea(
          this.predictionGraphics,
          this.map,
          area.center,
          nextBoat.heading,
          reach,
          color,
          this.aimChrome.prediction.a,
          PREDICTION_STROKE_ALPHA,
          zoom,
        );
        paintReachOverland(
          this.predictionOverlandGraphics,
          runs.underCap,
          color,
          this.map.look.underCapRimAlpha,
          zoom,
        );
      } else {
        const classes = paintPlanningCircle(
          this.predictionGraphics,
          this.map,
          area.center,
          area.radius,
          color,
          this.aimChrome.prediction.a,
          PREDICTION_STROKE_ALPHA,
          zoom,
        );
        paintPlanningCircleOverland(
          this.predictionOverlandGraphics,
          area.center,
          area.radius,
          classes.underCap,
          color,
          this.map.look.underCapRimAlpha,
          zoom,
        );
      }
    }

  }

  private syncHud(): void {
    const speedKmh = toDisplayKmh(this.boat.speed);
    const radiusM = pixelsToMeters(this.movementRadius);

    this.debugPanel?.sync({
      radiusMeters: radiusM,
      radiusLocked: this.reach !== null,
      crashHarshness: this.crashHarshness,
      turnTax: this.turnTax,
      ringMode: this.ringMode,
      windFeel: cloneWindFeel(this.windFeel),
      trailLengthMeters: this.trailLengthM,
      trailSpeedGain: this.trailSpeedGain,
      trailKeptMeters: this.trailKeepM(),
      curve: this.curveKind,
      lines: this.lines,
      capFlockEnabled: this.map.look.capFlockEnabled,
      capFlockAmount: this.map.look.capFlockAmount,
      waterlineHintAlpha: this.map.look.waterlineHintAlpha,
      underCapRimAlpha: this.map.look.underCapRimAlpha,
      sun: { ...this.map.environment.sun },
      look: cloneLook(this.map.look),
      showRoute: this.showRoute,
      previewGates: this.previewGates,
      logLines: this.logLines,
      stickyEdges: this.stickyEdges,
      stickyOverflowPx: this.stickyOverflowPx,
      nearMissPx: this.nearMissPx,
      camera: this.playCamera,
      fitZoom: this.fitZoom,
      speedZoom: this.speedZoomValue,
      autoZoom: this.autoZoom,
      cameraLimit: this.cameraLimit,
      userZoomFactor: this.userZoomFactor,
      viewZoom: this.cameras.main.zoom,
      aimChrome: cloneAimChrome(this.map.look.aimChrome),
      perf: this.perfReadout,
    });

    this.game.canvas.parentElement?.setAttribute(
      'data-cam-z',
      this.cameras.main.zoom.toFixed(4),
    );

    this.telemetry?.sync({
      speedKmh,
      bearing: toCompassBearing(this.boat.heading),
      windDirectionDeg: this.rolled.wind.directionDeg,
      windSpeed: this.rolled.wind.speed,
      ...this.ringMeter(),
    });
    this.sceneScale?.setLabel(
      formatScaleLabel(
        gridCellMeters(
          this.cameras.main.zoom,
          PIXELS_PER_METER,
          GRID_SCREEN_PX,
        ),
      ),
    );
  }

  /** Live-as-of-cursor standings for every seat in the current replay. */
  private replayHudSnapshots(): ReplaySeatSnapshot[] | null {
    const replay = this.replay;

    if (!replay || this.stage !== 'replay') {
      return null;
    }

    const rounds = this.replayRounds;
    const progressBySeat = new Map<number, number>();

    if (this.replayPlaybackMode === 'turnByTurn') {
      const current =
        replay.stepIndex < replay.steps.length
          ? replay.steps[replay.stepIndex]
          : undefined;

      if (current) {
        progressBySeat.set(current.seatId, this.trailProgress);
      }
    } else {
      const round =
        replay.stepIndex < rounds.length ? (rounds[replay.stepIndex] ?? []) : [];

      for (const step of round) {
        const view = this.views.find((entry) => entry.id === step.seatId);
        progressBySeat.set(step.seatId, view?.trailProgress ?? this.trailProgress);
      }
    }

    return replay.seats.map((entry) => {
      const cursor = replaySeatCursorAt(
        this.replayPlaybackMode,
        replay.steps,
        replay.stepIndex,
        replay.moveProgress,
        entry.seatId,
        entry.paths.length,
        rounds,
      );
      const override = progressBySeat.get(entry.seatId);

      return replaySeatSnapshot(
        this.map,
        entry,
        override !== undefined && cursor.localIndex >= 0
          ? { ...cursor, progress: override }
          : cursor,
      );
    });
  }

  /** Every seat, in seat order, before the match exists. Nobody has a time or a gate. */
  private rosterSeats(activeId: number): RaceHudSeat[] {
    return Array.from({ length: this.competitorCount }, (_, id) => ({
      id,
      label: this.seatLabel(id),
      color: competitorTintCss(id),
      place: id + 1,
      round: 1,
      lap: 1,
      checkpointsDone: 0,
      elapsedMs: 0,
      officialTimeMs: null,
      behindMs: null,
      active: id === activeId,
      leader: false,
      finished: false,
    }));
  }

  private syncRaceHud(): void {
    const replay = this.replay;
    const turnCount = replay ? this.replayDomainLength() : 0;
    const multi = (this.match?.competitors.length ?? this.competitorCount) > 1;
    const shared = this.match ? latestSharedSplit(this.match) : null;
    const snapshots = this.replayHudSnapshots();
    const replayGap = snapshots !== null ? replaySharedSplit(snapshots) : null;
    const currentTurnSeatId = this.currentSeatId();
    const winnerId = this.match ? (rankMatch(this.match)[0]?.id ?? null) : null;
    const winnerLabel = multi && winnerId !== null ? this.seatLabel(winnerId) : null;
    const replayComplete =
      snapshots !== null && snapshots.length > 0 && snapshots.every((seat) => seat.finished);
    const matchFinished = this.stage === 'finished' || replayComplete;
    const roster =
      (this.stage === 'placing' || this.stage === 'aiming') && snapshots === null;
    const placingIndex = this.placements.length + 1;
    const placingId = Math.min(placingIndex, this.competitorCount) - 1;

    const seats: RaceHudSeat[] = roster
      ? this.rosterSeats(placingId)
      : snapshots !== null
        ? rankStandings(
            snapshots.map((snapshot) => {
              const seat = this.match?.competitors.find((entry) => entry.id === snapshot.seatId);

              return {
                id: snapshot.seatId,
                label: this.seatLabel(snapshot.seatId),
                color: competitorTintCss(snapshot.seatId),
                round: snapshot.round,
                lap: snapshot.lap,
                checkpointsDone: snapshot.checkpointsDone,
                elapsedMs: snapshot.elapsedMs,
                officialTimeMs: snapshot.officialTimeMs,
                behindMs: replayGap?.behindMs.get(snapshot.seatId) ?? null,
                active: snapshot.seatId === currentTurnSeatId && !matchFinished,
                finished: snapshot.finished,
                hullDamaged: seat?.hull.damaged ?? false,
                missedGates: this.liveMissedGates(
                  snapshot.seatId,
                  snapshot.race.passedCheckpointIds,
                ).map((id) => gateHudLabel(this.map, id)),
              };
            }),
          )
        : multi && this.match
          ? rankStandings(
              this.match.competitors.map((seat) => {
                const passed =
                  seat.id === currentTurnSeatId
                    ? this.race.passedCheckpointIds
                    : seat.race.passedCheckpointIds;
                const timeMs = officialTimeMs(seat);

                return {
                  id: seat.id,
                  label: this.seatLabel(seat.id),
                  color: competitorTintCss(seat.id),
                  round: this.seatRound(seat.id),
                  lap: seat.id === currentTurnSeatId ? this.race.lap : seat.race.lap,
                  checkpointsDone: passed.size,
                  elapsedMs: this.seatBoatTimeMs(seat.id),
                  officialTimeMs: timeMs,
                  behindMs: shared?.behindMs.get(seat.id) ?? null,
                  active: seat.id === currentTurnSeatId && !matchFinished,
                  finished: timeMs !== null,
                  locked: lockedIn(seat),
                  hullDamaged: seat.hull.damaged,
                  missedGates: this.liveMissedGates(seat.id, passed).map((id) =>
                    gateHudLabel(this.map, id),
                  ),
                };
              }),
            )
          : [
              {
                id: 0,
                label: this.seatLabel(0),
                color: competitorTintCss(0),
                place: 1,
                round: this.seatRound(0),
                lap: this.race.lap,
                checkpointsDone: this.race.passedCheckpointIds.size,
                elapsedMs: this.boatTimeMs(),
                officialTimeMs: this.match?.competitors[0]
                  ? officialTimeMs(this.match.competitors[0])
                  : null,
                behindMs: null,
                active: !matchFinished,
                leader: true,
                finished:
                  matchFinished ||
                  (this.match?.competitors[0] !== undefined &&
                    officialTimeMs(this.match.competitors[0]) !== null),
                hullDamaged: this.match?.competitors[0]?.hull.damaged ?? false,
                missedGates: this.liveMissedGates(0, this.race.passedCheckpointIds).map((id) =>
                  gateHudLabel(this.map, id),
                ),
              },
            ];

    const placementHint =
      this.testPlay && this.stage === 'placing'
        ? 'Place your boat anywhere. Right-drag or hold H to pan. Hold B and scroll to turn.'
        : this.competitorCount > 1 &&
            (this.stage === 'placing' || this.stage === 'aiming')
          ? `Place ${this.seatLabel(placingId)} (${placingIndex} of ${this.competitorCount}).`
          : null;

    this.raceHud?.sync({
      stage: this.stage,
      round: replay
        ? Math.min(replay.stepIndex + 1, Math.max(1, turnCount))
        : this.moveSpeeds.length + 1,
      lap:
        snapshots?.find((seat) => seat.seatId === currentTurnSeatId)?.lap ?? this.race.lap,
      laps: mapLapCount(this.map.laps),
      checkpointsDone: roster
        ? 0
        : (snapshots?.find((seat) => seat.seatId === currentTurnSeatId)?.checkpointsDone ??
          this.race.passedCheckpointIds.size),
      checkpointsTotal: this.map.checkpoints.length,
      hasGoal: this.map.checkpoints.some((checkpoint) => checkpoint.isGoal),
      anchor: this.placementHintAnchor(),
      placementHint,
      seats: this.botClient
        ? seats.map((seat) => ({
            ...seat,
            bot: seatIsBot(this.seatControllers[seat.id]),
          }))
        : seats,
      activePlayer: roster
        ? placingId + 1
        : multi && currentTurnSeatId !== null && !matchFinished
          ? currentTurnSeatId + 1
          : null,
      activePlayerName: roster
        ? this.seatLabel(placingId)
        : multi && currentTurnSeatId !== null && !matchFinished
          ? this.seatLabel(currentTurnSeatId)
          : null,
      // Sequential and replay leave this null and keep the plain turn line.
      roundPhase:
        this.movement === 'simultaneous' && this.stage === 'racing' && !matchFinished
          ? this.revealing
            ? 'reveal'
            : 'lockIn'
          : null,
      matchFinished,
      winnerLabel,
      hullDamaged: this.match ? activeCompetitor(this.match).hull.damaged : false,
      headerHull: this.headerCueLive(this.hullFlash),
      headerMiss: this.headerCueLive(this.missFlash),
    });
  }

  /**
   * Seafloor, grid, and sheet follow the camera. `update()` calls this every frame with
   * `force` off, so a still camera costs one string compare; look edits force it.
   */
  private redrawGrid(force = true): void {
    const cam = this.cameras.main;
    const options = { lighting: this.lighting };
    const signature = backdropSignature(cam, this.map, options);

    if (!force && signature === this.gridDrawnFor) {
      return;
    }

    this.gridDrawnFor = signature;
    applyMapBackdrop(this.gridGraphics, this.waterSheetGraphics, cam, this.map, options);
  }

  private drawTrail(): void {
    this.trailGraphics.clear();

    if (!this.lines.trail) {
      return;
    }

    // One code path for live and replay, solo and hot-seat: `view.trail` (or the scalar
    // `this.trail` fallback with no views) already holds each seat's wake-so-far. A single
    // seat's turn clips only that seat's in-flight last leg; a simultaneous round — live or
    // in replay — clips every seat racing it via that view's own `trailProgress`.
    const simultaneous = this.fleetFraming();
    const activeSeatId = this.currentSeatId();
    const sources =
      this.views.length > 0
        ? this.views.map((view) => ({
            paths: view.trail,
            progress:
              simultaneous || this.driftingSeats.has(view.id)
                ? view.trailProgress
                : view.id === activeSeatId
                  ? this.trailProgress
                  : 1,
          }))
        : [{ paths: this.trail, progress: this.trailProgress }];

    for (const source of sources) {
      const wake = buildWake(
        source.paths,
        source.progress,
        metersToPixels(this.trailKeepM()),
      );
      fillWakeLayer(this.trailGraphics, wake.wash, WAKE_WASH_COLOR);
      fillWakeLayer(this.trailGraphics, wake.arms, WAKE_ARM_COLOR);
      fillWakeLayer(this.trailGraphics, wake.foam, WAKE_FOAM_COLOR);
    }
  }

  /**
   * Each seat's route is its own polyline — never concatenated across seats, or the stroke
   * walks straight from one boat's last point to another boat's first. In hot-seat (more than
   * one view) each route uses that seat's hull tint; solo keeps the plain `ROUTE_COLOR`.
   */
  private drawRoute(): void {
    this.routeGraphics.clear();

    if (!this.showRoute) {
      return;
    }

    const lineWidth = 3 / this.cameras.main.zoom;

    if (this.views.length > 0) {
      const hotSeat = this.views.length > 1;

      for (const view of this.views) {
        this.strokeRoute(view.route, hotSeat ? competitorTint(view.id) : ROUTE_COLOR, lineWidth);
      }

      return;
    }

    this.strokeRoute(this.route, ROUTE_COLOR, lineWidth);
  }

  private strokeRoute(paths: MovePath[], color: number, lineWidth: number): void {
    if (paths.length === 0) {
      return;
    }

    const points = paths.flatMap((path) => sampleMovePath(path));

    if (points.length < 2) {
      return;
    }

    const start = points[0]!;
    this.routeGraphics.lineStyle(lineWidth, color, 0.95);
    this.routeGraphics.beginPath();
    this.routeGraphics.moveTo(start.x, start.y);

    for (let i = 1; i < points.length; i += 1) {
      const point = points[i]!;
      this.routeGraphics.lineTo(point.x, point.y);
    }

    this.routeGraphics.strokePath();
  }

  /**
   * Aiming chrome updates now. Map rims, hints, gates, and the route restroke only
   * once the zoom has left the 8% band. Stopping the camera does not restroke.
   * Column fills are not a zoom cost.
   */
  private refreshZoomChrome(): void {
    const zoom = this.cameras.main.zoom;
    this.hoverMarker.setScale(1 / zoom);
    this.syncHullDisplaySize();

    if (this.areaGraphics.visible) {
      this.drawMovementArea();
    }

    this.drawHoverAids();

    if (this.stage === 'aiming') {
      this.drawAimLine(this.hull.rotation);
    }

    if (noteZoom(this.mapLayersZoom, zoom) === 'restroke') {
      this.refreshZoomScaledGraphics();
    }
  }

  private refreshZoomScaledGraphics(): void {
    this.mapLayersZoom = this.cameras.main.zoom;
    this.drawMapLayers('chrome');
    this.drawRoute();

    if (this.areaGraphics.visible) {
      this.drawMovementArea();
    }

    this.hoverMarker.setScale(1 / this.cameras.main.zoom);
    this.drawHoverAids();

    if (this.stage === 'aiming') {
      this.drawAimLine(this.hull.rotation);
    }
  }

  /** `chrome` restrokes rims, hints, gates, and the start area. Fills stay. */
  private drawMapLayers(paint: TerrainPaintMode = 'all'): void {
    const strokeScale = screenToWorld(1, this.cameras.main.zoom);
    drawTerrain(this.terrainGraphics, this.map, {
      strokeScale,
      waterlineHintAlpha: this.map.look.waterlineHintAlpha,
      capFlock: this.map.look.capFlockEnabled,
      capFlockAmount: this.map.look.capFlockAmount,
      columns: this.elevatedLayers ?? undefined,
      paint,
      bodyVisible: this.columnVisibleIds
        ? (id) => this.columnVisibleIds?.has(id) === true
        : undefined,
    });

    if (paint !== 'chrome') {
      this.islandOverlays?.sync(this.map);
    }

    this.redrawCheckpoints();

    if (this.stage === 'placing' || this.stage === 'aiming') {
      drawStartArea(
        this.startAreaGraphics,
        this.map,
        this.startAreaHot,
        strokeScale,
      );
    } else {
      this.startAreaGraphics.clear();
    }

    // Terrain objects may have been recreated visible; reapply the cull regardless of camera.
    this.cullOffscreenColumns(true);
  }

  private cullOffscreenColumns(force = false): void {
    if (!this.elevatedLayers && !this.islandOverlays) {
      return;
    }

    const next = applyColumnVisibility({
      map: this.map,
      cam: this.cameras.main,
      elevate: true,
      columns: this.elevatedLayers,
      overlays: this.islandOverlays,
      lastKey: this.columnVisibleKey,
      lastIds: this.columnVisibleIds,
      lastView: this.columnVisibleView,
      force,
      restrokeHints: (ids) => {
        paintWaterlineHints(
          this.terrainGraphics.waterlineHint,
          this.map,
          screenToWorld(1, this.cameras.main.zoom),
          this.map.look.waterlineHintAlpha,
          (id) => ids.has(id),
        );
      },
    });

    this.columnVisibleKey = next.key;
    this.columnVisibleIds = next.ids;
    this.columnVisibleView = next.view;
    this.lighting?.syncVisibleShade(this.map, next.ids, next.key);
  }

  private checkpointPreviewIds(): ReadonlySet<string> {
    if (
      !this.previewGates ||
      this.stage !== 'racing' ||
      this.phase !== 'planning' ||
      !this.hoverPoint
    ) {
      return new Set();
    }

    return previewedCheckpointIds(
      this.map,
      this.race,
      this.hoverPreviewPath(),
    );
  }

  /**
   * Colour-only repaint for a gate turning green or yellow. Water-clipped geometry comes from the
   * cache, so this costs no land sampling and can run mid-playback without dropping a frame.
   * Keeps the zoom the gates were last sized at; `GatePaintClock` skips the call outright when
   * neither the preview set nor the race changed (docs/PERFORMANCE.md, root cause 3).
   */
  private repaintGates(options: { zoom?: number; force?: boolean } = {}): void {
    const previewIds = this.checkpointPreviewIds();
    const plan = this.gatePaintClock.plan(previewIds, this.race, options);

    if (!plan.paint) {
      return;
    }

    this.gatesPaintZoom = plan.zoom;
    drawCheckpoints(
      {
        above: this.checkpointGraphics,
        waterline: this.checkpointWaterlineGraphics,
        submerged: this.checkpointSubmergedGraphics,
      },
      this.map,
      this.race,
      screenToWorld(1, plan.zoom),
      {
        previewIds,
        geometry: this.checkpointGeometry,
        shafts: this.checkpointPoles ?? undefined,
      },
    );
    this.writePassedGateProbe();
  }

  /** Chrome restroke: gates follow the 8% zoom band, sized at the live camera zoom. */
  private redrawCheckpoints(): void {
    this.repaintGates({ zoom: this.cameras.main.zoom, force: true });
    this.syncPassedGateBadges();
  }

  private setMovementAreaVisible(on: boolean): void {
    this.areaGraphics.setVisible(on);
    this.areaOverlandGraphics.setVisible(on);
  }

  private drawMovementArea(): void {
    const placed = this.placedArea(this.boat);
    const { area, calm, offset, view } = placed;
    const reach = view.reach;
    const zoom = this.cameras.main.zoom;
    const centerDotRadius = AREA_CENTER_DOT_PX / zoom;
    const color = rgb24(this.aimChrome.movement);

    this.areaGraphics.clear();
    this.areaOverlandGraphics.clear();
    if (this.lines.windGhost && lengthSq(offset) > 1e-6) {
      if (reach) {
        paintReachGhost(this.areaGraphics, calm.center, this.boat.heading, reach, color, zoom);
      } else {
        paintWindGhostCircle(this.areaGraphics, calm.center, calm.radius, color, zoom);
      }
    }
    if (reach) {
      const runs = paintReachArea(
        this.areaGraphics,
        this.map,
        area.center,
        this.boat.heading,
        reach,
        color,
        this.aimChrome.movement.a,
        AREA_STROKE_ALPHA,
        zoom,
      );
      paintReachOverland(
        this.areaOverlandGraphics,
        runs.underCap,
        color,
        this.map.look.underCapRimAlpha,
        zoom,
      );
      if (this.boat.speed === 0) {
        paintReachAft(
          this.areaGraphics,
          area.center,
          this.boat.heading,
          reach,
          this.bowOffsetPx,
          this.aimChrome.movement.a,
        );
      }
    } else {
      const classes = paintPlanningCircle(
        this.areaGraphics,
        this.map,
        area.center,
        area.radius,
        color,
        this.aimChrome.movement.a,
        AREA_STROKE_ALPHA,
        zoom,
      );
      paintPlanningCircleOverland(
        this.areaOverlandGraphics,
        area.center,
        area.radius,
        classes.underCap,
        color,
        this.map.look.underCapRimAlpha,
        zoom,
      );

      if (this.boat.speed === 0) {
        paintRestAftHalf(
          this.areaGraphics,
          area.center,
          area.radius,
          this.boat.heading,
          this.aimChrome.movement.a,
          this.bowOffsetPx,
        );
      }
    }
    this.areaGraphics.fillStyle(NEUTRAL_COLOR, 0.95);
    this.areaGraphics.fillCircle(area.center.x, area.center.y, centerDotRadius);
    this.areaGraphics.lineStyle(AREA_CENTER_STROKE_PX / zoom, color, 1);
    this.areaGraphics.strokeCircle(area.center.x, area.center.y, centerDotRadius);
  }
}

/**
 * Two triangles per quad from plain numbers. `fillPoints` would allocate four Vector2 per
 * quad per tween tick and run Earcut on them at render; a wake is convex quads only.
 */
function fillWakeLayer(
  gfx: Phaser.GameObjects.Graphics,
  quads: readonly WakeQuad[],
  color: number,
): void {
  let lastAlpha = -1;

  for (const quad of quads) {
    if (quad.alpha !== lastAlpha) {
      gfx.fillStyle(color, quad.alpha);
      lastAlpha = quad.alpha;
    }

    gfx.fillTriangle(quad.a.x, quad.a.y, quad.b.x, quad.b.y, quad.c.x, quad.c.y);
    gfx.fillTriangle(quad.a.x, quad.a.y, quad.c.x, quad.c.y, quad.d.x, quad.d.y);
  }
}

/** Wall-clock fraction at which a Sine.easeInOut tween reaches `amount`. */
function inverseSineInOut(amount: number): number {
  const y = Math.min(1, Math.max(0, amount));

  return Math.acos(1 - 2 * y) / Math.PI;
}

function toCurve(move: MovePath): Phaser.Curves.Path {
  const start = move.segments[0]?.p0 ?? { x: 0, y: 0 };
  const path = new Phaser.Curves.Path(start.x, start.y);

  for (const segment of move.segments) {
    path.add(
      new Phaser.Curves.CubicBezier(
        new Phaser.Math.Vector2(segment.p0.x, segment.p0.y),
        new Phaser.Math.Vector2(segment.p1.x, segment.p1.y),
        new Phaser.Math.Vector2(segment.p2.x, segment.p2.y),
        new Phaser.Math.Vector2(segment.p3.x, segment.p3.y),
      ),
    );
  }

  return path;
}
